// ============================================================================
// WORK ITEM DETAILS SIDEBAR
// Two-panel sidebar: Details (status/priority/assignees) + Timing & Context
// Supports multi-user assignment, labels, categories, teams
// Date: November 30, 2025
// Updated: December 9, 2025 - Added Priority selector
// Updated: January 2, 2026 - Added finance document workflow info
// Updated: March 2, 2026 - Two-panel toggle with Timing & Relationship tree
// ============================================================================

'use client';

/**
 * USER DOCS: changes to this component should be accompanied by a
 * corresponding update to the user guide:
 * next_portal/user-docs/content/docs/total-operations/workflows/service-operations/job-details.mdx
 * (published at user-docs.totalaccess.co.za/total-operations/workflows/service-operations/job-details#the-details-sidebar)
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { resolveWorkItemStatus } from '@/lib/work-items/workItemStatus';
import {
  User,
  Users,
  Settings,
  X,
  Plus,
  Search,
  Activity,
  ExternalLink,
  Info,
  FileText,
  Receipt,
  CreditCard,
  Truck,
  Package,
  Lock,
  ChevronRight,
  Timer,
  Network,
  Briefcase,
  DollarSign,
  Loader2,
  ClipboardCheck,
  UserCheck,
  Bot,
} from 'lucide-react';
import type { WorkItem, WorkItemAssignee } from '../UniversalWorkItems';
import { resolveVehicleDriver } from '@/lib/operations/fleetDriver';
import { useTheme } from '@/contexts/ThemeContext';
import { authService } from '@/lib/authService';
import { getApiUrl } from '@/lib/apiConfig';
import { isSilentFetchError } from '@/lib/capacitorFetchPatch';
import { emit } from '@/lib/typedEventBus';
import { confirm, notify } from '@/lib/notifications';
import { useOpenTeamMember } from '@/hooks/useOpenTeamMember';
import { useOperationsAssignmentOptions } from '@/hooks/useOperationsAssignmentOptions';
import StatusTransition, { type StatusTransitionOption } from './StatusTransition';
import FollowUpSection from './FollowUpSection';
import ParentItemSection from './ParentItemSection';
import { useJobConfig, type PriorityOption, type WorkflowStatus } from '@/hooks/useJobConfig';
import { useWorkItemStatus } from '@/hooks/useWorkItemStatus';
import { useWorkItemDetailContext } from '@/contexts/WorkItemDetailContext';
import WorkItemTypeConfigurationModal from '@/app/client-zone/modules/total-operations/components/settings/WorkItemTypeConfigurationModal';
import UserDocsLink from '@/components/shared/UserDocsLink';
import {
  getSuggestedProgressFromStatus,
  isAdminReopenLockedStatus,
  isActivelyWorkedStatus,
  isNegativeOutcomeStatus,
  isOnRouteWorkItemStatus,
  isOnSiteWorkItemStatus,
  isSuccessfulCompletionStatus,
  isProtectedTerminalAppointmentStatus,
  isTerminalWorkItemStatus,
  isTimingOverlayStatus,
  normalizeWorkflowStatusKey,
} from '@/lib/work-items/operationalStatusConfig';
import {
  buildAppointmentRealtimeFanoutChannels,
  formatAppointmentWorkflowStatusLabel,
  normalizeRealtimeUserIds,
} from '@/lib/work-items/appointmentRealtimeUtils';
import {
  getAllowedAppointmentAdminOverrideStatuses,
  type AppointmentAdminOverrideStatus,
} from '@/lib/work-items/appointmentAdminOverrideTransitions';
import {
  formatAdminAppointmentOverrideSuccessMessage,
  prepareAdminAppointmentOverride,
  resolveAppointmentAdminOverrideAudience,
  type PreparedAdminAppointmentOverrideRequest,
} from '@/lib/work-items/adminAppointmentOverrideClient';
import { canManuallyTargetWorkflowStatusKey } from '@/lib/work-items/workflowStatusRules';
import { getAppointmentDisplayStatus, getWorkItemResolutionType } from '@/lib/work-items/appointmentRescheduleState';
import { getAppointmentTimingStatus } from '@/lib/appointmentCardPresentation';
import { WORK_ITEM_LIST_CHANGED_EVENT } from '@/lib/websocket/lifecycle';
import { buildOperationsRealtimeEvent } from '@/lib/websocket/operationsRealtime';
import { useWebSocketChannel } from '@/hooks/useWebSocketChannel';
import { UserIdentityLink, type WorkItemUserIdentityData } from '@/components/work-items/WorkItemUserIdentity';
import { getAppointmentClassification, type AppointmentClassification } from '@/lib/appointmentDisplay';
import { parseStakeholderEntityWorkItemId } from '@/lib/contacts/stakeholderWorkItemIdentity';
import { getStakeholderContactId } from '@/components/contacts/stakeholderWorkItemAdapter';
import { isActiveContactAccountStatus } from '@/lib/contacts/accountStanding';
import FinanceRelationshipEnhancements from './FinanceRelationshipEnhancements';

type SidebarAssignee = WorkItemAssignee & {
  role?: string | null;
};

const DETAILS_SIDEBAR_WEBSOCKET_NOOP = () => { };

// Named assignment roles per workflow context (work_item_assignees.role) —
// distinct from the IAM group tier (admin/supervisor/user). Voting officials
// carry governance roles; extend the map as other workflows define theirs.
const ASSIGNEE_ROLE_OPTIONS_BY_WORKFLOW: Record<string, Array<{ value: string; label: string }>> = {
  'digital-voting': [
    { value: 'chairperson', label: 'Chairperson' },
    { value: 'secretary', label: 'Secretary' },
    { value: 'scrutineer', label: 'Scrutineer' },
    { value: 'member', label: 'Member' },
    { value: 'observer', label: 'Observer' },
  ],
};

function getAuthToken(): string | null {
  return authService.getTokens()?.access_token || null;
}

async function checkUserDriverEligibility(
  userId: number,
  token: string | null,
): Promise<{ hasValidLicense: boolean; isDesignatedDriver: boolean }> {
  const drivingTypes = ['drivers_license', 'heavy_vehicle'];
  try {
    const [licensesRes, driversRes] = await Promise.all([
      fetch(`/api/v1/licenses?user_id=${userId}&include_expired=true`, {
        headers: { Authorization: `Bearer ${token}` },
      }),
      fetch('/api/v1/module-access/users?module_key=total-operations&for_drivers=true', {
        headers: { Authorization: `Bearer ${token}` },
      }),
    ]);
    const [licensesData, driversData] = await Promise.all([
      licensesRes.json() as Promise<{ licenses?: unknown[] }>,
      driversRes.json() as Promise<{ data?: unknown[] }>,
    ]);
    const licenses = Array.isArray(licensesData.licenses) ? licensesData.licenses : [];
    const drivers = Array.isArray(driversData.data) ? driversData.data : [];
    const hasValidLicense = (licenses as Array<{ license_type: string; urgency_level: string }>).some(
      (l) => drivingTypes.includes(l.license_type) && l.urgency_level !== 'expired',
    );
    const isDesignatedDriver = (drivers as Array<{ id: number }>).some((d) => Number(d.id) === userId);
    return { hasValidLicense, isDesignatedDriver };
  } catch {
    // On API error, don't block the action — assume eligible
    return { hasValidLicense: true, isDesignatedDriver: true };
  }
}

async function getResponseErrorMessage(response: Response, fallbackMessage: string): Promise<string> {
  const responseClone = response.clone();
  const errorPayload = await responseClone.json().catch(() => null) as
    | { error?: string; message?: string }
    | null;

  if (typeof errorPayload?.error === 'string' && errorPayload.error.trim()) {
    return errorPayload.error;
  }

  if (typeof errorPayload?.message === 'string' && errorPayload.message.trim()) {
    return errorPayload.message;
  }

  const responseText = await response.text().catch(() => '');
  const trimmedText = responseText.trim();
  if (trimmedText) {
    return trimmedText;
  }

  const statusSuffix = response.status
    ? ` (${response.status}${response.statusText ? ` ${response.statusText}` : ''})`
    : '';

  return `${fallbackMessage}${statusSuffix}`;
}

function getInitialAssigneesFromFields(
  assignees: WorkItem['assignees'],
  assignedTo: WorkItem['assigned_to'],
  allocation?: WorkItem['allocation'],
): SidebarAssignee[] {
  // SSOT: vehicle_driver_allocations takes priority over assigned_to and work_item_assignees.
  // resolveVehicleDriver() implements the full priority chain (allocation → assigned_to →
  // assignees → claim), but here we only need to synthesize an assignee entry when the
  // allocation provides a driver that isn't already in the assignees array.
  if (allocation?.driver_user_id && Number(allocation.driver_user_id) > 0) {
    const allocationDriverId = Number(allocation.driver_user_id);
    const existing = (assignees || []).find((a) => Number(a.user_id) === allocationDriverId);
    if (existing) {
      return assignees || [];
    }
    // Synthesize an assignee from the allocation so the sidebar shows the driver
    const driverName = allocation.driver_name || '';
    const nameParts = driverName.split(' ');
    return [{
      user_id: allocationDriverId,
      first_name: nameParts[0] || '',
      last_name: nameParts.slice(1).join(' ') || '',
      email: '',
    }];
  }

  if (assignees && assignees.length > 0) {
    return assignees;
  }

  if (assignedTo && assignedTo !== 0) {
    return [{
      user_id: typeof assignedTo === 'string' ? parseInt(assignedTo) : assignedTo,
      first_name: '',
      last_name: '',
      email: '',
    }];
  }

  return [];
}

function getAssigneeStateKey(assignees: SidebarAssignee[]): string {
  return JSON.stringify(
    assignees.map((assignee) => [
      assignee.assignee_type || 'user',
      assignee.user_id,
      assignee.contact_id,
      assignee.first_name || '',
      assignee.last_name || '',
      assignee.email || '',
      assignee.company_name || '',
      assignee.contact_person || '',
      assignee.role || '',
    ])
  );
}

function getAssigneeIdentityKey(assignees: SidebarAssignee[]): string {
  return JSON.stringify(
    assignees.map((assignee) => [
      assignee.assignee_type || 'user',
      assignee.assignee_type === 'contractor' ? assignee.contact_id : assignee.user_id,
    ])
  );
}

function toNonEmptyString(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) {
    return value.trim();
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }

  return null;
}

function getWorkItemCustomFields(workItem: WorkItem): Record<string, unknown> {
  if (typeof workItem.custom_fields === 'string') {
    try {
      const parsed = JSON.parse(workItem.custom_fields);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? parsed as Record<string, unknown>
        : {};
    } catch {
      return {};
    }
  }

  if (workItem.custom_fields && typeof workItem.custom_fields === 'object' && !Array.isArray(workItem.custom_fields)) {
    return workItem.custom_fields as Record<string, unknown>;
  }

  return {};
}

function getTeamMemberVehicleLink(workItem: WorkItem): { vehicleId: string | null; vehicleLabel: string | null } {
  const customFields = getWorkItemCustomFields(workItem);
  const currentOperations = customFields.current_operations && typeof customFields.current_operations === 'object' && !Array.isArray(customFields.current_operations)
    ? customFields.current_operations as Record<string, unknown>
    : {};

  const vehicleId = toNonEmptyString(currentOperations.current_vehicle_id)
    || toNonEmptyString(customFields.current_vehicle_id)
    || null;

  const vehicleLabel = toNonEmptyString(currentOperations.current_vehicle_registration)
    || toNonEmptyString(customFields.current_vehicle_registration)
    || toNonEmptyString(customFields.current_vehicle_title)
    || toNonEmptyString(customFields.vehicle_registration)
    || toNonEmptyString(customFields.current_vehicle)
    || null;

  return { vehicleId, vehicleLabel };
}

// ============================================================================
// FINANCE DOCUMENT HELPERS
// ============================================================================

// Check if item type is a financial document
const isFinanceDocument = (itemType: string): boolean => {
  const financeTypes = [
    'invoice', 'quote', 'credit_note', 'debit_note', 'proforma', 'sales_order',
    'expense', 'purchase_order', 'supplier_credit', 'supplier_quote', 'purchase_requisition', 'rfq',
    'delivery_note', 'grn', 'picking_slip', 'packing_slip',
    'fuel', 'vehicle_expense'
  ];
  return financeTypes.includes(itemType);
};

/**
 * Payment-driven AR/AP statuses (paid, partial, overdue, …) are system-managed via documentLifecycle.
 * Hide DetailsSidebar workflow dropdown — use Payment management / journals / lifecycle actions in the finance document UI.
 */
function suppressesFinancePaymentStatusDropdown(itemType: string): boolean {
  return ['invoice', 'expense', 'debit_note', 'credit_note', 'supplier_credit'].includes(itemType);
}

// Get document type display info
const getDocumentTypeInfo = (itemType: string): { label: string; icon: React.ElementType; color: string; workflow: string } => {
  const typeInfo: Record<string, { label: string; icon: React.ElementType; color: string; workflow: string }> = {
    'invoice': { label: 'Invoice', icon: Receipt, color: 'text-green-600', workflow: 'Draft → Approved → Sent → Paid' },
    'quote': { label: 'Quote', icon: FileText, color: 'text-blue-600', workflow: 'Draft → Approved → Sent → Accepted/Declined' },
    'expense': { label: 'Expense', icon: CreditCard, color: 'text-orange-600', workflow: 'Draft → Approved → Paid' },
    'purchase_order': { label: 'Purchase Order', icon: Package, color: 'text-purple-600', workflow: 'Draft → Approved → Sent → Received' },
    'sales_order': { label: 'Sales Order', icon: FileText, color: 'text-teal-600', workflow: 'Draft → Approved → Processing → Shipped' },
    'credit_note': { label: 'Credit Note', icon: Receipt, color: 'text-red-600', workflow: 'Draft → Approved → Applied' },
    'debit_note': { label: 'Debit Note', icon: Receipt, color: 'text-amber-600', workflow: 'Draft → Approved → Applied' },
    'proforma': { label: 'Proforma Invoice', icon: FileText, color: 'text-indigo-600', workflow: 'Draft → Sent → Converted to Invoice' },
    'delivery_note': { label: 'Delivery Note', icon: Truck, color: 'text-cyan-600', workflow: 'Draft → Dispatched → Delivered' },
    'grn': { label: 'Goods Received', icon: Package, color: 'text-emerald-600', workflow: 'Draft → Approved → Matched to PO' }
  };
  return typeInfo[itemType] || { label: itemType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()), icon: FileText, color: 'text-gray-600', workflow: 'Standard workflow' };
};

// ============================================================================
// PRIORITY HELPERS
// ============================================================================

// Map color names to Tailwind classes
const PRIORITY_COLOR_MAP: Record<string, { color: string; dot: string }> = {
  green: { color: 'bg-green-100 text-green-700 border-green-300', dot: 'bg-green-500' },
  yellow: { color: 'bg-yellow-100 text-yellow-700 border-yellow-300', dot: 'bg-yellow-500' },
  orange: { color: 'bg-orange-100 text-orange-700 border-orange-300', dot: 'bg-orange-500' },
  red: { color: 'bg-red-100 text-red-700 border-red-300', dot: 'bg-red-500' },
  blue: { color: 'bg-blue-100 text-blue-700 border-blue-300', dot: 'bg-blue-500' },
  purple: { color: 'bg-purple-100 text-purple-700 border-purple-300', dot: 'bg-purple-500' },
  pink: { color: 'bg-pink-100 text-pink-700 border-pink-300', dot: 'bg-pink-500' },
  gray: { color: 'bg-gray-100 text-gray-700 border-gray-300', dot: 'bg-gray-500' },
  teal: { color: 'bg-teal-100 text-teal-700 border-teal-300', dot: 'bg-teal-500' },
  cyan: { color: 'bg-cyan-100 text-cyan-700 border-cyan-300', dot: 'bg-cyan-500' },
  indigo: { color: 'bg-indigo-100 text-indigo-700 border-indigo-300', dot: 'bg-indigo-500' },
  amber: { color: 'bg-amber-100 text-amber-700 border-amber-300', dot: 'bg-amber-500' },
};

const getPriorityDot = (priorities: PriorityOption[], value: string): string => {
  const p = priorities.find(p => p.key === value);
  if (!p) return 'bg-yellow-500';
  return PRIORITY_COLOR_MAP[p.color]?.dot || 'bg-yellow-500';
};

function formatWorkflowStatusLabel(value: string): string {
  return value.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatItemTypeLabel(value?: string | null): string {
  if (!value) return 'Work Item';

  return value
    .split(/[_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function getStakeholderContext(workItem: WorkItem): {
  customFields: Record<string, unknown>;
  entityKind: string | null;
  contactId: number | null;
  isStakeholderEntity: boolean;
  isStakeholderContact: boolean;
} {
  const stakeholderFields = typeof workItem.custom_fields === 'object' && workItem.custom_fields
    ? workItem.custom_fields as Record<string, unknown>
    : {};
  const parsedStakeholderIdentity = parseStakeholderEntityWorkItemId(String(workItem.id || ''));
  const stakeholderEntityKind = typeof stakeholderFields.stakeholder_entity_kind === 'string'
    ? stakeholderFields.stakeholder_entity_kind
    : typeof stakeholderFields.stakeholder_entity_type === 'string'
      ? stakeholderFields.stakeholder_entity_type
      : parsedStakeholderIdentity?.kind ?? null;
  const stakeholderContactId = getStakeholderContactId(workItem);
  const isStakeholderEntity = Boolean(stakeholderFields.is_virtual_stakeholder)
    || Boolean(stakeholderEntityKind)
    || parsedStakeholderIdentity !== null;
  const isStakeholderContact = stakeholderEntityKind === 'contact' && stakeholderContactId !== null;

  return {
    customFields: stakeholderFields,
    entityKind: stakeholderEntityKind,
    contactId: stakeholderContactId,
    isStakeholderEntity,
    isStakeholderContact,
  };
}

function getWorkflowStatusOption(
  workflowStatuses: WorkflowStatus[],
  targetStatus: 'admin_reopened' | 'closed',
): WorkflowStatus {
  const matchedStatus = workflowStatuses.find((status) => normalizeWorkflowStatusKey(status.key) === targetStatus);

  if (matchedStatus) {
    return matchedStatus;
  }

  if (targetStatus === 'admin_reopened') {
    return {
      key: 'admin_reopened',
      label: 'Admin Re-opened',
      color: 'blue',
      description: 'Unlocked again by an authorised admin after closure.',
      isSystem: true,
    };
  }

  return {
    key: 'closed',
    label: 'Closed',
    color: 'emerald',
    description: 'Formally closed and locked until an admin re-opens it.',
    isEnd: true,
    isSystem: true,
  };
}

function getPrimaryStartWorkflowStatusKey(
  workflowStatuses: WorkflowStatus[],
  fallbackStatus = 'open',
): string {
  const startStatus = workflowStatuses.find((status) => status.isStart);
  return startStatus?.key || fallbackStatus;
}

function isStartLikeWorkflowStatus(
  status: string,
  workflowStatuses: WorkflowStatus[],
): boolean {
  const normalizedStatus = normalizeWorkflowStatusKey(status);

  if (!normalizedStatus) {
    return false;
  }

  return workflowStatuses.some((workflowStatus) => (
    normalizeWorkflowStatusKey(workflowStatus.key) === normalizedStatus
    && Boolean(workflowStatus.isStart)
  ));
}

function buildStandardWorkflowStatusOptions({
  workflowStatuses,
  currentStatus,
  itemType,
  canClose = true,
}: {
  workflowStatuses: WorkflowStatus[];
  currentStatus: string;
  itemType?: string;
  canClose?: boolean;
}): StatusTransitionOption[] {
  const currentNormalizedStatus = normalizeWorkflowStatusKey(currentStatus);
  const normalizedItemType = normalizeWorkflowStatusKey(itemType || '');

  return workflowStatuses
    .filter((status) => {
      const normalizedStatus = normalizeWorkflowStatusKey(status.key);

      if (!normalizedStatus || normalizedStatus === currentNormalizedStatus) {
        return false;
      }

      // The `close` action-permission cell gates "closed" as a manual target.
      if (normalizedStatus === 'closed' && !canClose) {
        return false;
      }

      // Timing-derived overlay statuses (running_late, over_schedule) are not real
      // workflow statuses and should never appear as manual transition targets.
      if (isTimingOverlayStatus(normalizedStatus)) {
        return false;
      }

      // Hide 'scheduled' status from manual selection for jobs (system-driven status)
      // but keep it for appointments where it's part of the normal workflow
      if (normalizedStatus === 'scheduled' && normalizedItemType === 'job') {
        return false;
      }

      // Per-status transition rules: statuses with explicit allowed_from respect it;
      // statuses without allowed_from (undefined or empty) remain permissive.
      // Do not apply the extra canManuallyTargetWorkflowStatusKey gate when
      // explicit rules are present — configured transitions already encode intent.
      if (status.allowed_from && status.allowed_from.length > 0) {
        const normalizedAllowedFrom = status.allowed_from.map(normalizeWorkflowStatusKey);
        return normalizedAllowedFrom.includes(currentNormalizedStatus);
      }

      // When no transitions are configured, hide admin_reopened from the standard dropdown
      // (it is surfaced through the dedicated admin-reopen flow instead)
      if (normalizedStatus === 'admin_reopened') {
        return false;
      }

      return canManuallyTargetWorkflowStatusKey(normalizedStatus);
    })
    .map((status) => ({
      value: status.key,
      label: status.label || formatWorkflowStatusLabel(status.key),
      color: status.color || 'blue',
      description: status.description || `Move this work item to ${formatWorkflowStatusLabel(status.key).toLowerCase()}.`,
      suggested: Boolean(status.isStart),
    }))
    .sort((left, right) => {
      if (left.suggested === right.suggested) {
        return left.label.localeCompare(right.label);
      }

      return left.suggested ? -1 : 1;
    });
}

function buildAdminReopenStatusOptions({
  workflowStatuses,
}: {
  workflowStatuses: WorkflowStatus[];
}): StatusTransitionOption[] {
  const adminReopenedStatus = getWorkflowStatusOption(workflowStatuses, 'admin_reopened');

  return [{
    value: adminReopenedStatus.key,
    label: adminReopenedStatus.label || 'Admin Re-opened',
    color: adminReopenedStatus.color || 'blue',
    description: adminReopenedStatus.description || 'Unlock this record so normal editing can resume.',
  }];
}

function buildAdminRecloseStatusOptions({
  workflowStatuses,
}: {
  workflowStatuses: WorkflowStatus[];
}): StatusTransitionOption[] {
  const closedStatus = getWorkflowStatusOption(workflowStatuses, 'closed');

  return [{
    value: closedStatus.key,
    label: closedStatus.label || formatWorkflowStatusLabel(closedStatus.key),
    color: closedStatus.color || 'emerald',
    description: closedStatus.description || 'Lock this record again and finish the admin re-open cycle.',
  }];
}

/**
 * Admin lifecycle transitions (resolved → closed, closed → admin_reopened)
 * should not send FCM notifications to assigned field workers because the
 * field work is already complete. The last notification they receive is
 * the resolution transition (e.g. in_progress → resolved).
 */
function isPostResolutionAdminLifecycleTransition(fromStatus: string, toStatus: string): boolean {
  const from = normalizeWorkflowStatusKey(fromStatus);
  const to = normalizeWorkflowStatusKey(toStatus);

  // Terminal/resolution states moving to closed (admin archival)
  if (['resolved', 'partial', 'unresolved', 'rescheduled', 'cancelled'].includes(from) && to === 'closed') {
    return true;
  }

  // Closed moving to admin_reopened (admin unlocking)
  if (from === 'closed' && to === 'admin_reopened') {
    return true;
  }

  return false;
}

function buildAdminAppointmentStatusOptions({
  workflowStatuses,
  currentStatus,
  canClose = true,
}: {
  workflowStatuses: WorkflowStatus[];
  currentStatus: string;
  canClose?: boolean;
}): StatusTransitionOption[] {
  const statusByNormalizedKey = new Map<string, WorkflowStatus>();
  const currentNormalizedStatus = normalizeWorkflowStatusKey(currentStatus);
  const allowedStatuses = new Set(getAllowedAppointmentAdminOverrideStatuses(currentStatus));

  workflowStatuses.forEach((status) => {
    statusByNormalizedKey.set(normalizeWorkflowStatusKey(status.key), status);
  });

  // Per-status transition rules: statuses with explicit allowed_from respect it;
  // statuses without allowed_from (undefined or empty) remain permissive.
  const configuredStatuses = workflowStatuses
    .filter((status) => {
      const normalizedStatus = normalizeWorkflowStatusKey(status.key);
      if (normalizedStatus === currentNormalizedStatus) {
        return false;
      }

      if (normalizedStatus === 'closed' && !canClose) {
        return false;
      }

      if (isTimingOverlayStatus(normalizedStatus)) {
        return false;
      }

      if (status.allowed_from && status.allowed_from.length > 0) {
        const normalizedAllowedFrom = status.allowed_from.map(normalizeWorkflowStatusKey);
        return normalizedAllowedFrom.includes(currentNormalizedStatus);
      }

      // No explicit rules — fall through to hardcoded defaults below
      return false;
    })
    .map((status) => ({
      value: status.key,
      label: ['on_route', 'in_progress'].includes(normalizeWorkflowStatusKey(status.key))
        ? formatAppointmentWorkflowStatusLabel(status.key)
        : status.label || formatWorkflowStatusLabel(status.key),
      color: status.color || 'blue',
      description: status.description || `Move to ${formatWorkflowStatusLabel(status.key).toLowerCase()}.`,
    }));

  if (configuredStatuses.length > 0) {
    return configuredStatuses;
  }

  // Fall back to hardcoded transitions when nothing is configured
  const overrideStatusOrder = [
    {
      key: 'scheduled',
      label: 'Scheduled',
      color: 'gray',
      description: 'Reset the appointment back to a ready-to-start state.',
      aliases: ['scheduled', 'open', 'new'],
    },
    {
      key: 'on_route',
      label: 'On Route / Travelling',
      color: 'violet',
      description: 'Release the field worker into active travel for this appointment.',
      aliases: ['on_route', 'en_route'],
    },
    {
      key: 'in_progress',
      label: 'In Progress / Working',
      color: 'blue',
      description: 'Start or resume active work on this appointment.',
      aliases: ['in_progress', 'checked_in', 'working'],
    },
    {
      key: 'paused',
      label: 'Paused',
      color: 'amber',
      description: 'Pause the active work state without releasing the appointment.',
      aliases: ['paused', 'on_hold'],
    },
    {
      key: 'resolved',
      label: 'Resolved',
      color: 'green',
      description: 'Finish the appointment successfully and release the assignees.',
      aliases: ['resolved'],
    },
    {
      key: 'partial',
      label: 'Partial',
      color: 'orange',
      description: 'End the appointment as a partial completion and release the assignees.',
      aliases: ['partial'],
    },
    {
      key: 'unresolved',
      label: 'Unresolved',
      color: 'red',
      description: 'End the appointment as unresolved and release the assignees.',
      aliases: ['unresolved'],
    },
    {
      key: 'rescheduled',
      label: 'Rescheduled',
      color: 'indigo',
      description: 'End this run and mark the appointment for another scheduled attempt.',
      aliases: ['rescheduled'],
    },
    {
      key: 'cancelled',
      label: 'Cancelled',
      color: 'red',
      description: 'Cancel the appointment and immediately release any active workflow state.',
      aliases: ['cancelled', 'canceled'],
    },
  ] as const;

  return overrideStatusOrder.reduce<StatusTransitionOption[]>((options, preset) => {
    if (
      preset.aliases.some((alias) => alias === currentNormalizedStatus)
      || !allowedStatuses.has(preset.key as AppointmentAdminOverrideStatus)
    ) {
      return options;
    }

    const matchedStatus = preset.aliases
      .map((alias) => statusByNormalizedKey.get(alias))
      .find((status): status is WorkflowStatus => Boolean(status));

    const resolvedValue = matchedStatus?.key || preset.key;
    const resolvedLabel = ['on_route', 'in_progress'].includes(preset.key)
      ? formatAppointmentWorkflowStatusLabel(resolvedValue)
      : matchedStatus?.label || preset.label;
    options.push({
      value: resolvedValue,
      label: resolvedLabel,
      color: matchedStatus?.color || preset.color,
      description: matchedStatus?.description || preset.description,
    });
    return options;
  }, []);
}

function buildAdminAppointmentEntryOption(): StatusTransitionOption {
  return {
    value: 'admin',
    label: 'Admin',
    color: 'slate',
    description: 'Open the guarded admin override actions for this appointment.',
  };
}

function buildPrimaryAppointmentStatusOptions({
  workflowStatuses,
  currentStatus,
  canClose = true,
}: {
  workflowStatuses: WorkflowStatus[];
  currentStatus: string;
  canClose?: boolean;
}): StatusTransitionOption[] {
  const adminOptions = buildAdminAppointmentStatusOptions({ workflowStatuses, currentStatus, canClose });

  // Only include the Admin entry if there are actual admin override options available
  if (adminOptions.length === 0) {
    return [];
  }

  return [
    ...adminOptions,
    buildAdminAppointmentEntryOption(),
  ];
}


function parseDateValue(value?: string | null): Date | null {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function getMinutesBetween(start?: string | null, end?: string | null): number | null {
  const startDate = parseDateValue(start);
  const endDate = parseDateValue(end);
  if (!startDate || !endDate) return null;
  return Math.max(0, Math.round((endDate.getTime() - startDate.getTime()) / 60000));
}

function formatMinutesCompact(minutes?: number | null): string {
  if (!Number.isFinite(minutes) || minutes == null) return '—';
  const rounded = Math.abs(Math.round(minutes));
  const hours = Math.floor(rounded / 60);
  const remainder = rounded % 60;
  if (hours > 0 && remainder > 0) return `${hours}h ${remainder}m`;
  if (hours > 0) return `${hours}h`;
  return `${rounded}m`;
}

function formatDeltaLabel(minutes?: number | null, positiveLabel = 'late', negativeLabel = 'early'): string {
  if (!Number.isFinite(minutes) || minutes == null) return '—';
  if (minutes === 0) return 'On time';
  return minutes > 0
    ? `+${formatMinutesCompact(minutes)} ${positiveLabel}`
    : `${formatMinutesCompact(minutes)} ${negativeLabel}`;
}

function formatRunningLateDuration(minutes: number): string {
  const safeMinutes = Math.max(0, Math.round(minutes));

  if (safeMinutes < 60) {
    return `${safeMinutes}m`;
  }

  const hours = Math.floor(safeMinutes / 60);
  const remainderMinutes = safeMinutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(remainderMinutes).padStart(2, '0')}`;
}

type AppointmentScheduleComparisonCustomFields = {
  arrived_at?: string | null;
  check_in_timestamp?: string | null;
  travel_started_at?: string | null;
  check_out_timestamp?: string | null;
  completed_travel_entry?: {
    distance_km?: number | null;
  } | null;
};

function getAppointmentScheduleComparison(workItem: WorkItem) {
  if (workItem.item_type !== 'appointment') {
    return null;
  }

  const customFields: AppointmentScheduleComparisonCustomFields = typeof workItem.custom_fields === 'string'
    ? (() => {
      try {
        const parsed = JSON.parse(workItem.custom_fields) as AppointmentScheduleComparisonCustomFields | null;
        return parsed && typeof parsed === 'object' ? parsed : {};
      } catch {
        return {};
      }
    })()
    : (typeof workItem.custom_fields === 'object' && workItem.custom_fields
      ? workItem.custom_fields as AppointmentScheduleComparisonCustomFields
      : {});

  const scheduledStart = parseDateValue(workItem.scheduled_start);
  const scheduledEnd = parseDateValue(workItem.scheduled_end);
  const arrivedAt = parseDateValue(customFields?.arrived_at || customFields?.check_in_timestamp);
  const travelStartedAt = parseDateValue(customFields?.travel_started_at);
  const checkInAt = parseDateValue(customFields?.check_in_timestamp);
  const checkOutAt = parseDateValue(customFields?.check_out_timestamp);
  const travelDistanceKm = typeof customFields?.completed_travel_entry?.distance_km === 'number'
    ? customFields.completed_travel_entry.distance_km
    : null;

  const arrivalDeltaMinutes = scheduledStart && arrivedAt
    ? Math.round((arrivedAt.getTime() - scheduledStart.getTime()) / 60000)
    : null;
  const completionDeltaMinutes = scheduledEnd && checkOutAt
    ? Math.round((checkOutAt.getTime() - scheduledEnd.getTime()) / 60000)
    : null;
  const travelDurationMinutes = travelStartedAt && arrivedAt
    ? Math.max(0, Math.round((arrivedAt.getTime() - travelStartedAt.getTime()) / 60000))
    : getMinutesBetween(customFields?.travel_started_at, customFields?.arrived_at || customFields?.check_in_timestamp);
  const workDurationMinutes = getMinutesBetween(customFields?.check_in_timestamp, customFields?.check_out_timestamp);
  const scheduledDurationMinutes = getMinutesBetween(workItem.scheduled_start, workItem.scheduled_end);

  const outsideScheduleReasons: string[] = [];
  if (arrivalDeltaMinutes != null && arrivalDeltaMinutes < 0) {
    outsideScheduleReasons.push('arrived before the scheduled start');
  }
  if (arrivalDeltaMinutes != null && scheduledEnd && arrivedAt && arrivedAt.getTime() > scheduledEnd.getTime()) {
    outsideScheduleReasons.push('arrived after the scheduled window had ended');
  }
  if (completionDeltaMinutes != null && completionDeltaMinutes > 0) {
    outsideScheduleReasons.push('finished after the scheduled end');
  }
  if (checkInAt && scheduledStart && checkInAt.getTime() < scheduledStart.getTime()) {
    outsideScheduleReasons.push('started on-site work before the scheduled start');
  }

  return {
    scheduledDurationMinutes,
    travelDurationMinutes,
    workDurationMinutes,
    travelDistanceKm,
    arrivalDeltaMinutes,
    completionDeltaMinutes,
    outsideScheduleReasons,
    operatedOutsideSchedule: outsideScheduleReasons.length > 0,
  };
}

// ============================================================================
// TYPES
// ============================================================================

interface DetailsSidebarProps {
  workItem: WorkItem;
  onUpdate: (updates: Partial<WorkItem>) => Promise<void>;
  onPersistedUpdate?: (updates: Partial<WorkItem>) => void;
  onAssigneesChange?: (assignees: WorkItemAssignee[]) => Promise<void>;
  onStatusChange?: (newStatus: string) => Promise<void> | void;  // Called after status change with the new status
  onStatusTransitionStart?: () => void;
  onStatusTransitionEnd?: () => void;
  canReopenLockedStatus?: boolean;
  canManageAdminWorkflowOverride?: boolean;
  // `close` action-permission cell (Actions tab matrix): when false, "closed"
  // is removed from the manual status dropdown. Defaults to true so finance /
  // non-ops consumers keep existing behavior.
  canCloseStatus?: boolean;
  lockedStatusCycleMode?: 'default' | 'closed_only' | 'admin_reopen_only' | 'locked_no_options';
  readOnly?: boolean;
  onOpenProcessChecklist?: (targetStatus?: string) => void;
  onOpenResolveChecklist?: () => void;
  canConfigureItemType?: boolean;
  initialData?: DetailsSidebarInitialData | null;
  initialDataLoading?: boolean;
  className?: string;
  activeSolutionKey?: string | null;
  // NEW: Finance document context
  financeContext?: {
    enabled: boolean;
    docType?: string;
    onOpenActivityLog?: () => void;
    hideTimingSection?: boolean;
    enhanceRelationshipTree?: boolean;
    // Render only the Context panel (relationship tree) — no Details panel,
    // no panel toggle. Used by finance documents where status/priority edits
    // are managed by the document lifecycle, not the sidebar.
    contextOnly?: boolean;
  };
}

type SidebarPanel = 'details' | 'context';

export interface HierarchyNode {
  id: string;
  item_number: string;
  item_type: string;
  item_subtype?: string | null;
  title: string;
  status: string;
  parent_id?: string | null;
  relationship_type?: string;
}

export interface HierarchyData {
  ancestors: HierarchyNode[];
  current: HierarchyNode;
  children: HierarchyNode[];
}

export interface TeamMember {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  role?: string;
}

export interface RelatedWorkItem {
  id: string;
  item_number: string;
  title: string;
  status?: string;
  scheduled_start?: string;
}

export interface DetailsSidebarInitialData {
  teamMembers?: TeamMember[];
  reporter?: TeamMember | null;
  hierarchy?: HierarchyData | null;
  linkedAppointments?: { id: string; item_number: string; title: string; status?: string; scheduled_start?: string; scheduled_end?: string }[];
  linkedJobs?: RelatedWorkItem[];
  closeGateStatus?: {
    hasAnyGate: boolean;
    hasForms: boolean;
    hasTemplateGates: boolean;
    allComplete: boolean;
    total: number;
    completed: number;
    gateReleased: boolean;
    isBlocked: boolean;
  } | null;
  assignedTeamMembers?: { user_id: number; first_name: string; last_name: string; email: string; role?: string; is_leader?: boolean }[];
  linkedProject?: { id: string; item_number: string; title: string } | null;
}

type TeamMemberIdentityCandidate = {
  userId?: number | string | null;
  user_id?: number | string | null;
  id?: number | string | null;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  name?: string | null;
  displayName?: string | null;
};

type CloseImpactAppointment = {
  id: string;
  item_number: string;
  title: string;
  status?: string | null;
};

type CloseImpactTimer = {
  id: string;
  work_item_id: string;
  work_item_number: string;
  work_item_title: string;
  work_item_type: string;
  activity_type: string;
  started_at?: string | null;
  user_id: number | null;
  user_name: string;
};

type CloseImpactSummary = {
  appointmentsToClose: CloseImpactAppointment[];
  runningTimers: CloseImpactTimer[];
};

type CloseImpactTimeEntryCandidate = {
  id: string;
  work_item_id?: string;
  source_item_id?: string;
  source_item_number?: string;
  source_item_title?: string;
  source_item_type?: string;
  activity_type?: string;
  started_at?: string;
  is_running?: boolean;
  user_id?: number | string | null;
  user_name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
};

type ProcessGateTemplateEntry = {
  summary?: {
    total?: number | null;
    completed?: number | null;
  } | null;
  gate_released?: boolean | null;
  status?: string | null;
};

type TeamAppointmentMemberResponse = {
  user_id: number;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  role?: string | null;
  team_role?: string | null;
};

type MailboxDelegateResponse = {
  id: number;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
};

type ActiveEmployeeResponse = {
  id?: number | string | null;
  employee_number?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  job_title?: string | null;
  user_id?: number | string | null;
  user?: {
    id?: number | string | null;
    first_name?: string | null;
    last_name?: string | null;
    email?: string | null;
  } | null;
};

type ProjectSearchResult = {
  id: string;
  item_number: string;
  title: string;
};

type GateBlockedError = Error & {
  code?: 'GATE_BLOCKED';
};

function formatCloseImpactDateTime(value?: string | null): string {
  if (!value) {
    return 'unknown start';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return 'unknown start';
  }

  return parsed.toLocaleString('en-ZA', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatCloseImpactAppointmentLine(appointment: CloseImpactAppointment): string {
  return `${appointment.item_number || 'Appointment'} — ${appointment.title || 'Untitled appointment'}`;
}

function formatCloseImpactTimerLine(timer: CloseImpactTimer): string {
  const activityLabel = formatWorkflowStatusLabel(timer.activity_type || 'work');
  const workItemLabel = timer.work_item_number || timer.work_item_title || 'Current item';
  return `${timer.user_name} · ${activityLabel} · ${workItemLabel} · started ${formatCloseImpactDateTime(timer.started_at)}`;
}

function describeCloseImpactTimer(timer: CloseImpactTimer): string {
  return `${timer.user_name} on ${timer.work_item_number || timer.work_item_title || 'the current item'}`;
}

function shouldStopTimersForStatusChange(itemType: string | undefined, nextStatus: string): boolean {
  const normalizedStatus = normalizeWorkflowStatusKey(nextStatus);
  return normalizedStatus === 'closed' || (itemType === 'appointment' && normalizedStatus === 'resolved');
}

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

// User Avatar with initials
function UserAvatar({ user, size = 'md' }: { user: { first_name?: string | null; last_name?: string | null; email?: string | null }; size?: 'sm' | 'md' | 'lg' }) {
  const email = user.email || '';
  const firstName = user.first_name || '';
  const lastName = user.last_name || '';
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim() || firstName || lastName || '';
  const nameParts = fullName.split(/\s+/).filter(Boolean);

  const initials = nameParts.length >= 2
    ? `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase()
    : fullName
      ? fullName.substring(0, 2).toUpperCase()
      : email
        ? email.substring(0, 2).toUpperCase()
        : '??';

  const sizeClasses = {
    sm: 'w-6 h-6 text-xs',
    md: 'w-8 h-8 text-sm',
    lg: 'w-10 h-10 text-base'
  };

  // Generate consistent color from name/email
  const colorSeed = `${fullName}|${email}`;
  const colorIndex = colorSeed.split('').reduce((total, char) => total + char.charCodeAt(0), 0) % 6;
  const colors = [
    'bg-blue-500', 'bg-green-500', 'bg-purple-500',
    'bg-orange-500', 'bg-pink-500', 'bg-teal-500'
  ];

  return (
    <div className={`${sizeClasses[size]} ${colors[colorIndex]} rounded-full flex items-center justify-center text-white font-medium`}>
      {initials}
    </div>
  );
}

// Multi-select user dropdown
function UserMultiSelect({
  selectedUsers,
  availableUsers,
  onAdd,
  onRemove,
  onOpenProfile,
  loading,
  isDark,
  readOnly = false,
  forceOpen = false,
  onForceOpenHandled,
  assigneeRoleOptions,
  onAssigneeRoleChange
}: {
  selectedUsers: SidebarAssignee[];
  availableUsers: TeamMember[];
  onAdd: (user: TeamMember) => void;
  onRemove: (userId: number) => void;
  onOpenProfile?: (userId: number) => void;
  loading: boolean;
  isDark: boolean;
  readOnly?: boolean;
  forceOpen?: boolean;
  onForceOpenHandled?: () => void;
  assigneeRoleOptions?: Array<{ value: string; label: string }>;
  onAssigneeRoleChange?: (userId: number, role: string | null) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Handle forceOpen from parent — use effect to properly handle ref updates
  const prevForceOpenRef = useRef(forceOpen);

  useEffect(() => {
    if (forceOpen && !prevForceOpenRef.current) {
      prevForceOpenRef.current = forceOpen;
      queueMicrotask(() => setIsOpen(true));
    } else if (!forceOpen) {
      prevForceOpenRef.current = false;
    }
  }, [forceOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        // Notify parent that force open was handled (user clicked away)
        if (forceOpen && onForceOpenHandled) {
          onForceOpenHandled();
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [forceOpen, onForceOpenHandled]);

  // Filter out users without valid IDs and already selected users
  const filteredUsers = availableUsers.filter(user => {
    if (!user.id) return false; // Skip users without valid IDs
    const name = `${user.first_name || ''} ${user.last_name || ''} ${user.email || ''} ${user.role || ''}`.toLowerCase();
    const isContractor = user.id < 0;
    const contactId = Math.abs(user.id);
    const alreadySelected = isContractor
      ? selectedUsers.some(s => s.assignee_type === 'contractor' && s.contact_id === contactId)
      : selectedUsers.some(s => s.user_id === user.id);
    return name.includes(search.toLowerCase()) && !alreadySelected;
  });

  const showAssigneeRoles = Boolean(assigneeRoleOptions && assigneeRoleOptions.length > 0);

  const getSelectedUserSecondaryText = (user: SidebarAssignee) => {
    // When a dedicated role editor is rendered, don't repeat the role in the
    // identity line — show email instead.
    if (!showAssigneeRoles && user.role?.trim()) {
      return user.role.trim();
    }

    if ((user.first_name || user.last_name) && user.email?.trim()) {
      return user.email.trim();
    }

    return null;
  };

  const getAvailableUserSecondaryText = (user: TeamMember) => {
    const details = [user.role?.trim()].filter(Boolean) as string[];
    const email = user.email?.trim();
    const hasName = Boolean((user.first_name || '').trim() || (user.last_name || '').trim());

    if (email && (details.length > 0 || hasName)) {
      details.push(email);
    }

    return details.join(' • ');
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Selected Users */}
      <div className="space-y-2 mb-2">
        {selectedUsers.map(user => {
          const isContractor = user.assignee_type === 'contractor';
          const removeId = isContractor ? -(user.contact_id!) : user.user_id!;
          const displayName = isContractor
            ? user.company_name || user.contact_person || `Contractor #${user.contact_id}`
            : (user.first_name && user.last_name
              ? `${user.first_name} ${user.last_name}`
              : user.first_name || user.last_name || user.email || 'Unknown');
          return (
            <div
              key={isContractor ? `contractor-${user.contact_id}` : `user-${user.user_id}`}
              className={`flex items-center gap-2 p-2 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}
            >
              {isContractor ? (
                <div className="flex-1 min-w-0 flex items-center gap-2">
                  <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium ${isDark ? 'bg-blue-900 text-blue-200' : 'bg-blue-100 text-blue-700'}`}>
                    <Briefcase className="w-3.5 h-3.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-medium truncate ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                      {displayName}
                    </div>
                    {user.email?.trim() && (
                      <div className="text-xs text-gray-500 truncate">{user.email}</div>
                    )}
                  </div>
                </div>
              ) : (
                <UserIdentityLink
                  user={{
                    userId: user.user_id,
                    firstName: user.first_name || null,
                    lastName: user.last_name || null,
                    email: user.email || null,
                    secondaryText: getSelectedUserSecondaryText(user),
                  }}
                  isDark={isDark}
                  size="sm"
                  fullWidth
                  onOpenProfile={onOpenProfile}
                />
              )}
              {!isContractor && assigneeRoleOptions && assigneeRoleOptions.length > 0 && user.user_id != null && (
                <select
                  value={user.role || ''}
                  onChange={(e) => onAssigneeRoleChange?.(user.user_id!, e.target.value || null)}
                  disabled={readOnly}
                  title="Assignee role on this work item"
                  className={`flex-shrink-0 text-xs rounded border px-1 py-0.5 max-w-[110px] ${isDark
                    ? 'bg-gray-800 border-gray-600 text-gray-300'
                    : 'bg-white border-gray-300 text-gray-600'
                    } ${readOnly ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <option value="">No role</option>
                  {assigneeRoleOptions.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                  {user.role && !assigneeRoleOptions.some((o) => o.value === user.role) && (
                    <option value={user.role}>{user.role}</option>
                  )}
                </select>
              )}
              <button
                type="button"
                onClick={() => onRemove(removeId)}
                disabled={readOnly}
                className={`p-1 rounded text-red-500 ${readOnly ? 'cursor-not-allowed opacity-40' : 'hover:bg-red-100 dark:hover:bg-red-900/30'}`}
                title={readOnly ? 'Assignee editing is locked in read-only mode' : 'Remove assignee'}
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          );
        })}
      </div>

      {/* Add User Button */}
      <button
        type="button"
        onClick={() => {
          if (readOnly) return;
          setIsOpen(!isOpen);
        }}
        disabled={readOnly}
        className={`w-full flex items-center gap-2 p-2 rounded-lg border-2 border-dashed transition-colors ${readOnly
          ? (isDark
            ? 'border-gray-700 bg-gray-800/70 text-gray-500 cursor-not-allowed'
            : 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed')
          : forceOpen
            ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 animate-pulse'
            : isDark
              ? 'border-gray-600 hover:border-gray-500 text-gray-400 hover:text-gray-300'
              : 'border-gray-300 hover:border-gray-400 text-gray-500 hover:text-gray-600'
          }`}
        title={readOnly ? 'Assignee editing is locked in read-only mode' : 'Add assignee'}
      >
        <Plus className="w-4 h-4" />
        <span className="text-sm">{readOnly ? 'Assignees locked' : forceOpen ? 'Select an assignee' : 'Add assignee'}</span>
      </button>

      {/* Dropdown */}
      {!readOnly && isOpen && (
        <div className={`absolute z-50 w-full mt-1 rounded-lg shadow-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}>
          {/* Search */}
          <div className="p-2 border-b border-gray-200 dark:border-gray-700">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search users..."
                className={`w-full pl-8 pr-3 py-1.5 text-sm rounded border ${isDark
                  ? 'bg-gray-700 border-gray-600 text-gray-200'
                  : 'bg-gray-50 border-gray-200 text-gray-700'
                  }`}
                autoFocus
              />
            </div>
          </div>

          {/* User List */}
          <div className="max-h-48 overflow-y-auto">
            {loading ? (
              <div className="p-3 text-center text-gray-500 text-sm">Loading...</div>
            ) : filteredUsers.length === 0 ? (
              <div className="p-3 text-center text-gray-500 text-sm">
                {search ? 'No matching users' : 'No more users available'}
              </div>
            ) : (
              filteredUsers.map(user => {
                const isContractor = user.id < 0;
                return (
                <button
                  key={`user-${user.id}`}
                  type="button"
                  onClick={() => {
                    onAdd(user);
                    setSearch('');
                    setIsOpen(false);
                  }}
                  className={`w-full flex items-center gap-2 p-2 text-left transition-colors ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                    }`}
                >
                  {isContractor ? (
                    <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${isDark ? 'bg-blue-900 text-blue-200' : 'bg-blue-100 text-blue-700'}`}>
                      <Briefcase className="w-3.5 h-3.5" />
                    </div>
                  ) : (
                    <UserAvatar user={user} size="sm" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-medium truncate ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                      {user.first_name && user.last_name
                        ? `${user.first_name} ${user.last_name}`
                        : user.email}
                    </div>
                    <div className="text-xs text-gray-500 truncate">{getAvailableUserSecondaryText(user) || user.email}</div>
                  </div>
                </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Labels/Tags editor
function LabelsEditor({
  labels,
  onChange,
  isDark
}: {
  labels: string[];
  onChange: (labels: string[]) => void;
  isDark: boolean;
}) {
  const [isAdding, setIsAdding] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const handleAdd = () => {
    if (newLabel.trim() && !labels.includes(newLabel.trim())) {
      onChange([...labels, newLabel.trim()]);
      setNewLabel('');
    }
    setIsAdding(false);
  };

  const handleRemove = (label: string) => {
    onChange(labels.filter(l => l !== label));
  };

  useEffect(() => {
    if (isAdding && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isAdding]);

  // Predefined label colors
  const getLabelColor = (label: string) => {
    const colors = [
      { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-300' },
      { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-300' },
      { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-300' },
      { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-700 dark:text-orange-300' },
      { bg: 'bg-pink-100 dark:bg-pink-900/30', text: 'text-pink-700 dark:text-pink-300' },
      { bg: 'bg-teal-100 dark:bg-teal-900/30', text: 'text-teal-700 dark:text-teal-300' },
    ];
    const index = label.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % colors.length;
    return colors[index];
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1">
        {labels.map(label => {
          const color = getLabelColor(label);
          return (
            <span
              key={label}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${color.bg} ${color.text}`}
            >
              {label}
              <button
                onClick={() => handleRemove(label)}
                className="hover:bg-black/10 dark:hover:bg-white/10 rounded-full p-0.5"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          );
        })}
      </div>

      {isAdding ? (
        <div className="flex gap-1">
          <input
            ref={inputRef}
            type="text"
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleAdd();
              if (e.key === 'Escape') setIsAdding(false);
            }}
            onBlur={handleAdd}
            placeholder="Label name..."
            className={`flex-1 px-2 py-1 text-sm rounded border ${isDark
              ? 'bg-gray-700 border-gray-600 text-gray-200'
              : 'bg-white border-gray-300 text-gray-700'
              }`}
          />
        </div>
      ) : (
        <button
          onClick={() => setIsAdding(true)}
          className={`text-sm flex items-center gap-1 ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'
            }`}
        >
          <Plus className="w-3 h-3" />
          Add labels
        </button>
      )}
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function DetailsSidebar({
  workItem,
  onUpdate,
  onPersistedUpdate,
  onAssigneesChange,
  onStatusChange,
  onStatusTransitionStart,
  onStatusTransitionEnd,
  canReopenLockedStatus = false,
  canManageAdminWorkflowOverride = false,
  canCloseStatus = true,
  lockedStatusCycleMode = 'default',
  readOnly = false,
  onOpenProcessChecklist,
  onOpenResolveChecklist,
  canConfigureItemType = false,
  initialData = null,
  initialDataLoading = false,
  className = '',
  activeSolutionKey = null,
  financeContext,
}: DetailsSidebarProps) {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.mode === 'dark';
  const detailContext = useWorkItemDetailContext();

  // NEW: Finance context handling
  const isFinanceMode = financeContext?.enabled ?? workItem.module_context === 'financial-management';
  const hideOpsTiming = financeContext?.hideTimingSection ?? isFinanceMode;
  const enhanceFinanceRelationships = financeContext?.enhanceRelationshipTree ?? isFinanceMode;
  const openTeamMember = useOpenTeamMember();
  const { users: assignmentUsers, getTeamMemberUserIds, contractors: assignmentContractors } = useOperationsAssignmentOptions({ moduleKey: 'total-operations', activeSolutionKey });
  // Virtual stakeholder entities (contacts/people/locations/documents) use synthetic UUIDs
  // that don't exist in work_items — skip the SSoT status fetch for those to avoid 404s.
  // Recurring job schedule IDs live in work_item_recurring, not work_items — skip for those too.
  // Team member IDs (team-member-*) are virtual work items that don't exist in work_items either.
  const isVirtualStakeholderId = parseStakeholderEntityWorkItemId(String(workItem.id || '')) !== null;
  const isNonWorkItemEntity = isVirtualStakeholderId
    || workItem.item_type === 'recurring_job'
    || workItem.item_type === 'team_member'
    || String(workItem.id || '').startsWith('team-member-');
  const { status: ssotStatus, loading: ssotStatusLoading, refetch: refetchSsotStatus } = useWorkItemStatus(isNonWorkItemEntity ? null : workItem.id);
  const [showItemTypeConfigModal, setShowItemTypeConfigModal] = useState(false);
  const [showAppointmentAdminWorkflowOverride, setShowAppointmentAdminWorkflowOverride] = useState(false);

  // Panel toggle state
  const [activePanel, setActivePanel] = useState<SidebarPanel>('details');

  // Check if this is an appointment (single assignee, read-only assignee, appointment-specific fields)
  const isAppointment = workItem.item_type === 'appointment';
  const isVehicle = workItem.item_subtype === 'vehicle';
  const isTeamMember = workItem.item_type === 'team_member';
  // Voting sessions are lifecycle-managed (open/close endpoints own the status
  // ladder) — status/priority edits and follow-ups don't apply, and assignees
  // are the session's officials (chairperson, secretary, …).
  const isVotingWorkItem = workItem.item_type === 'voting';
  const assigneeRoleOptions = ASSIGNEE_ROLE_OPTIONS_BY_WORKFLOW[
    (workItem.module_context || '').trim().toLowerCase()
  ];
  const stakeholderContext = useMemo(() => getStakeholderContext(workItem), [workItem]);
  const isStakeholderWorkItem = stakeholderContext.isStakeholderEntity;
  const stakeholderContactId = stakeholderContext.contactId;
  const usesStakeholderAccountManager = isStakeholderWorkItem && stakeholderContactId !== null;
  const showContextPanel = !isStakeholderWorkItem;
  // Finance documents can request context-only mode: the Details panel
  // (status/priority/assignees) is suppressed and the sidebar renders the
  // Context panel (relationship tree) unconditionally.
  const contextOnlyPanel = Boolean(financeContext?.contextOnly) && showContextPanel;
  const effectivePanel: SidebarPanel = contextOnlyPanel ? 'context' : activePanel;
  const assigneeLabel = isTeamMember ? 'Vehicle' : isVehicle ? 'Driver' : isVotingWorkItem ? 'Session Officials' : isStakeholderWorkItem ? 'Account Manager' : 'Assignees';
  // Manager applies to the project → job → appointment hierarchy only —
  // stakeholder entities use the account manager, vehicles use the driver.
  const showManagerSection = !isTeamMember && !isVehicle && !isStakeholderWorkItem
    && ['project', 'job', 'appointment'].includes(String(workItem.item_type || '').toLowerCase());
  const teamMemberVehicleLink = useMemo(() => getTeamMemberVehicleLink(workItem), [workItem]);
  const teamMemberIsNonDriver = useMemo(() => {
    const customFields = getWorkItemCustomFields(workItem);
    const currentOperations = customFields.current_operations && typeof customFields.current_operations === 'object' && !Array.isArray(customFields.current_operations)
      ? customFields.current_operations as Record<string, unknown>
      : null;
    return currentOperations?.is_non_driver === true;
  }, [workItem]);
  const appointmentClassification = useMemo<AppointmentClassification | null>(
    () => isAppointment ? getAppointmentClassification(workItem) : null,
    [isAppointment, workItem],
  );
  const appointmentScheduleComparison = useMemo(
    () => getAppointmentScheduleComparison(workItem),
    [workItem]
  );

  // Dynamic priorities from config
  const { priorities: configPriorities, workflowStatuses, hasTransitionRules, requireManager, refresh: refreshJobConfig } = useJobConfig({ itemType: workItem.item_type || 'job', workflowContext: workItem.module_context });
  const openProcessChecklist = onOpenProcessChecklist ?? onOpenResolveChecklist;
  const canOpenItemTypeConfiguration = canConfigureItemType && Boolean(workItem.item_type);

  useEffect(() => {
    if (!showContextPanel && activePanel === 'context') {
      setActivePanel('details');
    }
  }, [activePanel, showContextPanel]);
  const itemTypeLabel = useMemo(() => formatItemTypeLabel(workItem.item_type), [workItem.item_type]);
  const configureItemTypeActionTitle = useMemo(
    () => `Configure ${itemTypeLabel}`,
    [itemTypeLabel],
  );

  // Process checklist status — unified forms gate + template gate status
  const [closeGateStatus, setCloseGateStatus] = useState<{
    hasAnyGate: boolean;
    hasForms: boolean;
    hasTemplateGates: boolean;
    allComplete: boolean;
    total: number;
    completed: number;
    gateReleased: boolean;
    isBlocked: boolean;
  } | null>(null);
  const [loadingCloseGate, setLoadingCloseGate] = useState(false);
  const skipInitialCloseGateFetchRef = useRef(false);

  const fetchCloseGateStatus = useCallback(async () => {
    if (isNonWorkItemEntity) {
      setCloseGateStatus(null);
      return;
    }
    const token = getAuthToken();
    if (!token) {
      setCloseGateStatus(null);
      return;
    }

    try {
      setLoadingCloseGate(true);

      const res = await fetch(`/api/v1/work-items/${workItem.id}/process-gates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;

      const json = await res.json();
      if (!json.success) return;

      const d = json.data;
      if (!d?.has_any_gate) {
        setCloseGateStatus(null);
        return;
      }

      const formsTotal: number = d.forms_gate?.total ?? 0;
      const formsCompleted: number = d.forms_gate?.completed ?? 0;
      const processGateTemplates: ProcessGateTemplateEntry[] = Array.isArray(d.process_gates) ? d.process_gates : [];
      const templateTotal = processGateTemplates.reduce((sum: number, gate) => sum + (gate.summary?.total ?? 0), 0);
      const templateCompleted = processGateTemplates.reduce((sum: number, gate) => sum + (gate.summary?.completed ?? 0), 0);
      const total = formsTotal + templateTotal;
      const completed = formsCompleted + templateCompleted;
      const gateReleased = Boolean(d.forms_gate?.gate_released)
        || (processGateTemplates.length > 0 && processGateTemplates.every((gate) => gate.gate_released || gate.status === 'released' || gate.status === 'complete'));

      setCloseGateStatus({
        hasAnyGate: Boolean(d.has_any_gate),
        hasForms: formsTotal > 0,
        hasTemplateGates: Boolean(d.has_process_gate_templates),
        allComplete: Boolean(d.is_complete),
        total,
        completed,
        gateReleased,
        isBlocked: Boolean(d.is_blocked),
      });
    } catch {
      // non-blocking
    } finally {
      setLoadingCloseGate(false);
    }
  }, [isNonWorkItemEntity, workItem.id]);

  // Refs for callbacks to prevent render loops
  const fetchCloseGateStatusRef = useRef<(() => Promise<void>) | null>(null);
  const loadTeamMembersRef = useRef<(() => Promise<void>) | null>(null);
  const loadReporterRef = useRef<(() => Promise<void>) | null>(null);
  const loadLinkedAppointmentsRef = useRef<(() => Promise<void>) | null>(null);
  const loadLinkedJobsRef = useRef<(() => Promise<void>) | null>(null);
  const loadHierarchyRef = useRef<(() => Promise<void>) | null>(null);
  const loadBillingSubmissionContextRef = useRef<(() => Promise<void>) | null>(null);
  const handleStatusChangeRef = useRef<((newStatus: string) => Promise<void>) | null>(null);
  const handleBeforeStatusChangeRef = useRef<((targetStatus: string) => Promise<boolean>) | null>(null);
  const pendingGateTargetStatusRef = useRef<string | null>(null);
  const pendingAppointmentOverrideRequestRef = useRef<PreparedAdminAppointmentOverrideRequest | null>(null);

  useEffect(() => {
    let cancelled = false;

    if (initialDataLoading) {
      return () => { cancelled = true; };
    }

    if (skipInitialCloseGateFetchRef.current) {
      skipInitialCloseGateFetchRef.current = false;
      return () => { cancelled = true; };
    }

    void (async () => {
      await fetchCloseGateStatusRef.current?.();
      if (cancelled) {
        return;
      }
    })();
    return () => { cancelled = true; };
  }, [initialDataLoading, workItem.id, workItem.status]);

  useEffect(() => {
    const handleChecklistUpdate = (event: Event) => {
      const detail = (event as CustomEvent<{ workItemId?: string }>).detail;
      if (!detail?.workItemId || String(detail.workItemId) !== String(workItem.id)) {
        return;
      }

      void fetchCloseGateStatus();
    };

    window.addEventListener('processChecklistUpdated', handleChecklistUpdate as EventListener);
    return () => {
      window.removeEventListener('processChecklistUpdated', handleChecklistUpdate as EventListener);
    };
  }, [fetchCloseGateStatus, workItem.id]);

  // When the process gate transitions to unblocked, auto-retry the pending status change.
  // WorkItemDetailModal dispatches 'processGateCleared' from its onComplete callback, which
  // fires after ProcessGatePanel detects the blocked→unblocked transition.
  useEffect(() => {
    const handleGateCleared = async (event: Event) => {
      const detail = (event as CustomEvent<{ workItemId?: string; targetStatus?: string }>).detail;
      if (!detail?.workItemId || String(detail.workItemId) !== String(workItem.id)) {
        return;
      }
      const targetStatus = detail.targetStatus;
      if (!targetStatus) {
        return;
      }
      // Show the before-change confirmation (includes timer warning for appointments).
      if (handleBeforeStatusChangeRef.current) {
        const shouldContinue = await handleBeforeStatusChangeRef.current(targetStatus);
        if (!shouldContinue) {
          return;
        }
      }
      void handleStatusChangeRef.current?.(targetStatus);
    };
    window.addEventListener('processGateCleared', handleGateCleared as EventListener);
    return () => {
      window.removeEventListener('processGateCleared', handleGateCleared as EventListener);
    };
  }, [workItem.id]);

  const [teamMembers, setTeamMembers] = useState<TeamMember[]>(() => initialData?.teamMembers || []);
  // True when the assignee roster came from a workflow permission group
  // (module_context → source_workflow_key match) — skips the solution filter
  // and contractor merge since the workflow roster is the authority.
  const [teamMembersWorkflowScoped, setTeamMembersWorkflowScoped] = useState(false);
  const [loadingMembers, setLoadingMembers] = useState(() => initialDataLoading || !Array.isArray(initialData?.teamMembers));
  const [reporter, setReporter] = useState<TeamMember | null>(() => initialData?.reporter || null);
  const [saving, setSaving] = useState<string | null>(null);

  // Hierarchy data for the Context panel
  const [hierarchy, setHierarchy] = useState<HierarchyData | null>(() => initialData?.hierarchy || null);
  const [loadingHierarchy, setLoadingHierarchy] = useState(false);

  // For email_task: Track if we need to auto-transition to 'assigned' after adding an assignee
  const [pendingAssignedStatus, setPendingAssignedStatus] = useState(false);
  // Force assignee dropdown open when trying to assign without an assignee
  const [forceAssigneeDropdownOpen, setForceAssigneeDropdownOpen] = useState(false);
  const [assignedTeamMembers, setAssignedTeamMembers] = useState<{ user_id: number; first_name: string; last_name: string; email: string; role?: string; is_leader?: boolean }[]>([]);
  const [loadingAssignedTeamMembers, setLoadingAssignedTeamMembers] = useState(false);

  // Linked appointments (for jobs)
  const [linkedAppointments, setLinkedAppointments] = useState<{ id: string; item_number: string; title: string; scheduled_start?: string }[]>(() => initialData?.linkedAppointments || []);
  const [linkedJobs, setLinkedJobs] = useState<RelatedWorkItem[]>(() => initialData?.linkedJobs || []);

  // Billing submission source context (source job + its appointments)
  const [billingSourceJob, setBillingSourceJob] = useState<{ id: string; item_number: string; title: string; status?: string } | null>(null);
  const [billingSourceAppointments, setBillingSourceAppointments] = useState<{ id: string; item_number: string; title: string; scheduled_start?: string; scheduled_end?: string; status?: string }[]>([]);
  const [loadingBillingSource, setLoadingBillingSource] = useState(false);

  // Project linking state (for jobs / tasks / appointments)
  const [linkedProject, setLinkedProject] = useState<{ id: string; item_number: string; title: string } | null>(() => initialData?.linkedProject || null);
  const [projectSearchQuery, setProjectSearchQuery] = useState('');
  const [projectSearchResults, setProjectSearchResults] = useState<{ id: string; item_number: string; title: string }[]>([]);
  const [showProjectPicker, setShowProjectPicker] = useState(false);
  const [linkingProject, setLinkingProject] = useState(false);
  const projectPickerRef = useRef<HTMLDivElement>(null);

  // Local assignees state (synced from workItem but updated locally for immediate UI feedback)
  const [localAssignees, setLocalAssignees] = useState<SidebarAssignee[]>(() => getInitialAssigneesFromFields(workItem.assignees, workItem.assigned_to, workItem.allocation));

  // Responsible manager (work_items.manager_id) — synced from workItem
  const [localManagerId, setLocalManagerId] = useState<number | null>(() => workItem.manager_id != null ? Number(workItem.manager_id) : null);
  const [managerEditing, setManagerEditing] = useState(false);
  const [localManagerName, setLocalManagerName] = useState<string | null>(() => workItem.manager_name || null);
  // Curated manager pool — non-empty Set filters the manager <select> to
  // flagged users; null = no pool configured = legacy any-user list.
  const [eligibleManagerIds, setEligibleManagerIds] = useState<Set<number> | null>(null);
  useEffect(() => {
    if (!showManagerSection) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch('/api/v1/work-items/eligible-managers', {
          headers: { Authorization: `Bearer ${getAuthToken()}` },
        });
        const payload = await response.json();
        if (cancelled) return;
        const list = payload?.data?.managers || [];
        setEligibleManagerIds(list.length > 0 ? new Set(list.map((m: { id: number | string }) => Number(m.id))) : (requireManager ? new Set<number>() : null));
      } catch {
        if (!cancelled) setEligibleManagerIds(null);
      }
    })();
    return () => { cancelled = true; };
  }, [showManagerSection, requireManager]);

  // Local labels state (synced from workItem but updated locally for immediate UI feedback)
  const [localLabels, setLocalLabels] = useState<string[]>(workItem.tags || []);
  const [localStatus, setLocalStatus] = useState<string>(workItem.status || '');
  const [localResolutionType, setLocalResolutionType] = useState<string>(() => getWorkItemResolutionType(workItem));
  const resolvedWorkItemStatus = useMemo(() => {
    const nextStatus = localStatus || workItem.status || '';
    const nextCustomFields = typeof workItem.custom_fields === 'object' && workItem.custom_fields
      ? {
        ...workItem.custom_fields,
        resolution_type: localResolutionType || workItem.custom_fields.resolution_type,
      }
      : workItem.custom_fields;

    return resolveWorkItemStatus({
      ...workItem,
      status: nextStatus,
      resolution_type: localResolutionType || undefined,
      custom_fields: nextCustomFields,
    }, {
      privilegedViewer: true,
    });
  }, [localResolutionType, localStatus, workItem]);

  const effectiveWorkItemStatus = useMemo(() => {
    // Prefer single-source-of-truth resolved status (includes workflow state overlay)
    if (ssotStatus?.resolved_status) {
      return ssotStatus.resolved_status;
    }

    const nextStatus = resolvedWorkItemStatus.baseStatusKey || localStatus || workItem.status || '';

    if (!isAppointment) {
      return nextStatus;
    }

    return nextStatus;
  }, [isAppointment, localStatus, resolvedWorkItemStatus.baseStatusKey, ssotStatus, workItem.status]);

  // Use the raw/base status for transition lookups.
  // SSOT resolved_status can map runtime states (e.g. checked_in -> working)
  // but work_item_workflow_transitions uses the base statuses (in_progress, etc.)
  const transitionBaseStatus = useMemo(() => {
    const base = ssotStatus?.base_status || workItem.status || '';
    const normalizedBase = normalizeWorkflowStatusKey(base);

    // running_late is a timing-derived overlay, not a real workflow status.
    // over_schedule is a timing-derived overlay, not a real workflow status.
    // For transition lookups, map them back to their underlying operational status.
    if (isAppointment && normalizedBase === 'running_late') {
      const timingStatus = getAppointmentTimingStatus(workItem as Record<string, unknown>);
      if (timingStatus === 'onroute_late') {
        return 'on_route';
      }
      return 'scheduled';
    }

    if (isAppointment && normalizedBase === 'over_schedule') {
      return 'in_progress';
    }

    return base;
  }, [ssotStatus?.base_status, isAppointment, workItem]);

  const shouldUseAdminAppointmentStatusOverride = isAppointment;
  const normalizedWorkItemStatus = normalizeWorkflowStatusKey(effectiveWorkItemStatus);
  const forceClosedOnlyStatusOption = lockedStatusCycleMode === 'closed_only';
  const forceAdminReopenOnlyStatusOption = lockedStatusCycleMode === 'admin_reopen_only';
  const forceNoLockedStatusOptions = lockedStatusCycleMode === 'locked_no_options';
  const canUseResolvedCloseTransition = !forceClosedOnlyStatusOption
    && !forceAdminReopenOnlyStatusOption
    && !forceNoLockedStatusOptions
    && normalizedWorkItemStatus === 'resolved';
  const canUseAdminReopenStatusOverride = canReopenLockedStatus
    && !forceClosedOnlyStatusOption
    && normalizedWorkItemStatus === 'closed';
  /** When the modal uses default mode for job/appointment/task after admin re-open, do not restrict the dropdown to "closed" only. */
  const allowsFlexibleLockedStatusFollowUp = ['job', 'appointment', 'task'].includes(workItem.item_type || '')
    && lockedStatusCycleMode === 'default';
  const canUseAdminRecloseStatusOverride = canReopenLockedStatus
    && !forceAdminReopenOnlyStatusOption
    && normalizedWorkItemStatus === 'admin_reopened'
    && !allowsFlexibleLockedStatusFollowUp;
  const suppressAdminAppointmentStatusOverride = normalizedWorkItemStatus === 'closed'
    || normalizedWorkItemStatus === 'resolved'
    || (normalizedWorkItemStatus === 'admin_reopened' && lockedStatusCycleMode === 'closed_only')
    || hasTransitionRules; // Don't use admin override when configured transitions exist
  const canUseAdminAppointmentStatusOverride = canManageAdminWorkflowOverride
    && shouldUseAdminAppointmentStatusOverride
    && !suppressAdminAppointmentStatusOverride;
  const adminAppointmentStatusOptions = useMemo(
    () => canUseAdminAppointmentStatusOverride
      ? buildAdminAppointmentStatusOptions({
        workflowStatuses,
        currentStatus: transitionBaseStatus || 'scheduled',
        canClose: canCloseStatus,
      })
      : [],
    [canUseAdminAppointmentStatusOverride, transitionBaseStatus, workflowStatuses, canCloseStatus],
  );
  const primaryAppointmentStatusOptions = useMemo(
    () => canUseAdminAppointmentStatusOverride
      ? buildPrimaryAppointmentStatusOptions({
        workflowStatuses,
        currentStatus: transitionBaseStatus || 'scheduled',
        canClose: canCloseStatus,
      })
      : [],
    [canUseAdminAppointmentStatusOverride, transitionBaseStatus, workflowStatuses, canCloseStatus],
  );
  const adminReopenStatusOptions = useMemo(
    () => (forceAdminReopenOnlyStatusOption || canUseAdminReopenStatusOverride)
      ? buildAdminReopenStatusOptions({ workflowStatuses })
      : [],
    [canUseAdminReopenStatusOverride, forceAdminReopenOnlyStatusOption, workflowStatuses],
  );
  const adminRecloseStatusOptions = useMemo(
    () => (forceClosedOnlyStatusOption || canUseAdminRecloseStatusOverride)
      ? buildAdminRecloseStatusOptions({ workflowStatuses })
      : [],
    [canUseAdminRecloseStatusOverride, forceClosedOnlyStatusOption, workflowStatuses],
  );
  const usingAdminAppointmentStatusOverride = adminAppointmentStatusOptions.length > 0;
  const usingAdminReopenStatusOverride = adminReopenStatusOptions.length > 0;
  const usingAdminRecloseStatusOverride = adminRecloseStatusOptions.length > 0;
  const standardStatusOptions = useMemo(
    () => buildStandardWorkflowStatusOptions({
      workflowStatuses,
      currentStatus: transitionBaseStatus || '',
      itemType: workItem.item_type,
      canClose: canCloseStatus,
    }),
    [transitionBaseStatus, workflowStatuses, workItem.item_type, canCloseStatus],
  );
  const usingStandardStatusOptions = isVehicle ? false : normalizedWorkItemStatus === 'resolved'
    ? (!forceClosedOnlyStatusOption && !forceAdminReopenOnlyStatusOption && !forceNoLockedStatusOptions && !usingAdminRecloseStatusOverride && !usingAdminReopenStatusOverride && standardStatusOptions.length > 0)
    : (!isAppointment && !forceClosedOnlyStatusOption && !forceAdminReopenOnlyStatusOption && !forceNoLockedStatusOptions && !usingAdminRecloseStatusOverride && !usingAdminReopenStatusOverride && !usingAdminAppointmentStatusOverride && standardStatusOptions.length > 0);

  /** Replaces the workflow label on the status dropdown when timing says running late (avoids duplicating the auto banner). */
  const appointmentStatusDropdownOverride = useMemo(() => {
    if (workItem.item_type !== 'appointment') {
      return null;
    }

    // Prefer SSOT resolved label when it differs from base status
    if (ssotStatus?.resolved_status && ssotStatus.resolved_status !== ssotStatus.base_status) {
      return ssotStatus.resolved_label;
    }

    return resolvedWorkItemStatus.statusKey !== resolvedWorkItemStatus.baseStatusKey
      ? resolvedWorkItemStatus.label
      : null;
  }, [resolvedWorkItemStatus, ssotStatus, workItem.item_type]);

  useEffect(() => {
    setShowAppointmentAdminWorkflowOverride(false);
  }, [effectiveWorkItemStatus, workItem.id]);

  const allowReadOnlyAdminReopen = readOnly && (usingAdminReopenStatusOverride || usingAdminRecloseStatusOverride);
  const statusTransitionDisabled = Boolean(
    (readOnly && !allowReadOnlyAdminReopen)
    || saving === 'status'
  );
  const statusTransitionCustomOptions = isVehicle ? undefined : normalizedWorkItemStatus === 'resolved'
    ? standardStatusOptions
    : hasTransitionRules
      ? standardStatusOptions // Use configured transitions when they exist
      : usingAdminAppointmentStatusOverride && showAppointmentAdminWorkflowOverride
        ? adminAppointmentStatusOptions
        : usingAdminAppointmentStatusOverride
          ? primaryAppointmentStatusOptions
          : usingAdminRecloseStatusOverride
            ? adminRecloseStatusOptions
            : usingAdminReopenStatusOverride
              ? adminReopenStatusOptions
              : undefined;
  const appointmentWorkflowOverrideTargets = useMemo(() => {
    if (!isAppointment) {
      return [] as Array<{ user_id: number; display_name: string }>;
    }

    const seenUserIds = new Set<number>();
    const targets: Array<{ user_id: number; display_name: string }> = [];
    const pushTarget = (userId: unknown, displayName?: unknown) => {
      const numericUserId = typeof userId === 'number'
        ? userId
        : typeof userId === 'string' && userId.trim().length > 0
          ? Number(userId)
          : Number.NaN;

      if (!Number.isFinite(numericUserId) || numericUserId <= 0 || seenUserIds.has(numericUserId)) {
        return;
      }

      seenUserIds.add(numericUserId);
      targets.push({
        user_id: numericUserId,
        display_name: typeof displayName === 'string' && displayName.trim().length > 0
          ? displayName.trim()
          : `User ${numericUserId}`,
      });
    };

    if (appointmentClassification === 'team' && assignedTeamMembers.length > 0) {
      assignedTeamMembers.forEach((member) => {
        pushTarget(
          member.user_id,
          `${member.first_name || ''} ${member.last_name || ''}`.trim() || member.email || undefined,
        );
      });
    }

    localAssignees.forEach((assignee) => {
      pushTarget(
        assignee.user_id,
        `${assignee.first_name || ''} ${assignee.last_name || ''}`.trim() || assignee.email || undefined,
      );
    });

    (Array.isArray(workItem.assignees) ? workItem.assignees : []).forEach((assignee) => {
      pushTarget(
        assignee.user_id,
        `${assignee.first_name || ''} ${assignee.last_name || ''}`.trim() || assignee.email || undefined,
      );
    });

    pushTarget(workItem.assigned_to, workItem.assigned_to_name || workItem.assignee_name || undefined);

    const customFields = typeof workItem.custom_fields === 'object' && workItem.custom_fields
      ? workItem.custom_fields as Record<string, unknown>
      : {};

    [
      customFields.assigned_user_ids,
      customFields.participant_user_ids,
      customFields.assigned_users,
      customFields.assigned_users_details,
    ].forEach((value) => {
      if (!Array.isArray(value)) {
        return;
      }

      value.forEach((entry) => {
        if (typeof entry === 'object' && entry) {
          const candidate = entry as {
            user_id?: unknown;
            userId?: unknown;
            id?: unknown;
            name?: unknown;
            display_name?: unknown;
            displayName?: unknown;
            first_name?: unknown;
            last_name?: unknown;
            email?: unknown;
          };
          const resolvedDisplayName = typeof candidate.display_name === 'string' && candidate.display_name.trim().length > 0
            ? candidate.display_name
            : typeof candidate.displayName === 'string' && candidate.displayName.trim().length > 0
              ? candidate.displayName
              : typeof candidate.name === 'string' && candidate.name.trim().length > 0
                ? candidate.name
                : `${typeof candidate.first_name === 'string' ? candidate.first_name : ''} ${typeof candidate.last_name === 'string' ? candidate.last_name : ''}`.trim()
                || (typeof candidate.email === 'string' ? candidate.email : undefined);

          pushTarget(candidate.user_id ?? candidate.userId ?? candidate.id, resolvedDisplayName);
          return;
        }

        pushTarget(entry);
      });
    });

    return targets;
  }, [
    appointmentClassification,
    assignedTeamMembers,
    isAppointment,
    localAssignees,
    workItem.assigned_to,
    workItem.assigned_to_name,
    workItem.assignee_name,
    workItem.assignees,
    workItem.custom_fields,
  ]);
  const realtimeOperationsChannel = useMemo(() => {
    const numericClientId = Number(workItem.client_id);
    return Number.isFinite(numericClientId) && numericClientId > 0
      ? `operations:client:${numericClientId}`
      : undefined;
  }, [workItem.client_id]);
  const {
    status: realtimeConnectionStatus,
    connect: connectRealtime,
    publish: publishRealtime,
    subscribe: subscribeRealtime,
  } = useWebSocketChannel({
    channel: realtimeOperationsChannel,
    autoConnect: true,
    retainMessages: false,
    retainLatestData: false,
  });
  const realtimePublishUnsubscribersRef = useRef<Map<string, () => void>>(new Map());

  useEffect(() => {
    const publishUnsubscribers = realtimePublishUnsubscribersRef.current;

    return () => {
      publishUnsubscribers.forEach((unsubscribe) => unsubscribe());
      publishUnsubscribers.clear();
    };
  }, []);

  const ensureRealtimePublishChannel = useCallback(async (channel: string) => {
    if (!channel) {
      return false;
    }

    if (realtimeConnectionStatus === 'disconnected') {
      await connectRealtime();
    }

    if (!realtimePublishUnsubscribersRef.current.has(channel)) {
      realtimePublishUnsubscribersRef.current.set(
        channel,
        subscribeRealtime(channel, DETAILS_SIDEBAR_WEBSOCKET_NOOP),
      );
    }

    return true;
  }, [connectRealtime, realtimeConnectionStatus, subscribeRealtime]);

  const incomingAssignees = useMemo(
    () => getInitialAssigneesFromFields(workItem.assignees, workItem.assigned_to, workItem.allocation),
    [workItem.assigned_to, workItem.assignees, workItem.allocation],
  );
  const incomingAssigneesKey = useMemo(
    () => getAssigneeStateKey(incomingAssignees),
    [incomingAssignees],
  );
  const incomingAssigneeIdentityKey = useMemo(
    () => getAssigneeIdentityKey(incomingAssignees),
    [incomingAssignees],
  );
  const localAssigneesKey = useMemo(
    () => getAssigneeStateKey(localAssignees),
    [localAssignees],
  );
  const localAssigneeIdentityKey = useMemo(
    () => getAssigneeIdentityKey(localAssignees),
    [localAssignees],
  );

  // Sync local assignees when workItem changes - consider both assignees and assigned_to
  useEffect(() => {
    if (
      incomingAssigneeIdentityKey === localAssigneeIdentityKey
      || incomingAssigneesKey === localAssigneesKey
    ) {
      return;
    }

    setLocalAssignees(incomingAssignees);
  }, [
    incomingAssigneeIdentityKey,
    incomingAssignees,
    incomingAssigneesKey,
    localAssigneeIdentityKey,
    localAssigneesKey,
  ]);

  // Populate assignee details from teamMembers when they load
  useEffect(() => {
    if (teamMembers.length > 0 && localAssignees.length > 0) {
      // Check if any assignees need their details populated
      const needsUpdate = localAssignees.some(a => !a.first_name && !a.email);
      if (needsUpdate) {
        let madeChanges = false;
        const updatedAssignees = localAssignees.map(assignee => {
          if (!assignee.first_name && !assignee.email) {
            const member = teamMembers.find(m => m.id === assignee.user_id);
            if (member) {
              madeChanges = true;
              return {
                ...assignee,
                first_name: member.first_name || '',
                last_name: member.last_name || '',
                email: member.email || ''
              };
            }
          }
          return assignee;
        });
        // Only update state if we actually made changes to avoid infinite loops
        if (madeChanges) {
          setLocalAssignees(updatedAssignees);
        }
      }
    }
  }, [teamMembers, localAssignees]);

  // Sync local manager when workItem changes
  useEffect(() => {
    setLocalManagerId(workItem.manager_id != null ? Number(workItem.manager_id) : null);
    setLocalManagerName(workItem.manager_name || null);
  }, [workItem.manager_id, workItem.manager_name]);

  // Sync local labels when workItem changes
  useEffect(() => {
    setLocalLabels(workItem.tags || []);
  }, [workItem.tags]);

  useEffect(() => {
    const incomingStatus = workItem.status || '';

    if (incomingStatus === localStatus) {
      return;
    }

    setLocalStatus(incomingStatus);
  }, [localStatus, workItem.id, workItem.status]);

  useEffect(() => {
    const incomingResolutionType = getWorkItemResolutionType(workItem);

    if (incomingResolutionType === localResolutionType) {
      return;
    }

    setLocalResolutionType(incomingResolutionType);
  }, [localResolutionType, workItem]);

  // Listen for closeWorkItemAfterInvoice event from WorkItemDetailModal
  useEffect(() => {
    const handleCloseAfterInvoice = (e: CustomEvent<{ workItemId: string; status: string }>) => {
      if (e.detail.workItemId !== workItem.id || e.detail.status !== 'closed') {
        return;
      }
      void (async () => {
        try {
          await handleStatusChangeRef.current?.('closed');
        } catch (err: unknown) {
          if ((err as { code?: string })?.code === 'GATE_BLOCKED') {
            return;
          }
          console.error('[DetailsSidebar] closeWorkItemAfterInvoice failed:', err);
          notify.error(err instanceof Error ? err.message : 'Failed to close job');
        }
      })();
    };

    window.addEventListener('closeWorkItemAfterInvoice', handleCloseAfterInvoice as EventListener);
    return () => {
      window.removeEventListener('closeWorkItemAfterInvoice', handleCloseAfterInvoice as EventListener);
    };
  }, [workItem.id]);

  // Load team members when this is a team-assigned appointment
  useEffect(() => {
    if (appointmentClassification !== 'team') {
      setAssignedTeamMembers([]);
      return;
    }

    if (!initialDataLoading && initialData?.assignedTeamMembers !== undefined) {
      setLoadingAssignedTeamMembers(false);
      return;
    }

    const customFields = typeof workItem.custom_fields === 'object' && workItem.custom_fields
      ? workItem.custom_fields as Record<string, unknown>
      : {};
    const teamId = customFields.assigned_team || customFields.assigned_team_id || workItem.assigned_team;
    if (!teamId) {
      setAssignedTeamMembers([]);
      return;
    }

    let cancelled = false;
    const loadTeamMembers = async () => {
      const token = authService.getTokens()?.access_token;
      if (!token) return;

      try {
        setLoadingAssignedTeamMembers(true);
        const res = await fetch(`/api/v1/teams/${teamId}/members`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json().catch(() => ({}));
        const members: TeamAppointmentMemberResponse[] = Array.isArray(data.data) ? data.data : [];
        if (!cancelled && data.success && members.length > 0) {
          setAssignedTeamMembers(
            members.map((m) => ({
              user_id: m.user_id,
              first_name: m.first_name || '',
              last_name: m.last_name || '',
              email: m.email || '',
              role: m.role || m.team_role || '',
              is_leader: m.role === 'leader' || m.team_role === 'leader',
            }))
          );
        }
      } catch (err) {
        console.error('Error loading team members for team appointment:', err);
        if (!cancelled) setAssignedTeamMembers([]);
      } finally {
        if (!cancelled) setLoadingAssignedTeamMembers(false);
      }
    };

    void loadTeamMembers();
    return () => { cancelled = true; };
  }, [appointmentClassification, initialData?.assignedTeamMembers, initialDataLoading, workItem.assigned_team, workItem.custom_fields, workItem.id]);

  const hasInitialTeamMembers = Array.isArray(initialData?.teamMembers);
  const hasInitialReporter = initialData?.reporter !== undefined;
  const hasInitialHierarchy = initialData?.hierarchy !== undefined;
  const hasInitialLinkedAppointments = initialData?.linkedAppointments !== undefined;
  const hasInitialLinkedJobs = initialData?.linkedJobs !== undefined;
  const reporterUserId = useMemo(() => {
    if (reporter?.id && Number.isFinite(Number(reporter.id))) {
      return Number(reporter.id);
    }

    const createdBy = Number(workItem.created_by);
    return Number.isFinite(createdBy) && createdBy > 0 ? createdBy : null;
  }, [reporter?.id, workItem.created_by]);
  const reporterIdentity = useMemo(() => {
    if (reporter) {
      return {
        userId: reporterUserId,
        firstName: reporter.first_name || null,
        lastName: reporter.last_name || null,
        email: reporter.email || null,
      };
    }

    if (workItem.reporter_name || workItem.reporter_email || reporterUserId) {
      return {
        userId: reporterUserId,
        displayName: workItem.reporter_name || null,
        email: workItem.reporter_email || null,
        secondaryText: workItem.reporter_email || null,
      };
    }

    return null;
  }, [reporter, reporterUserId, workItem.reporter_email, workItem.reporter_name]);

  const platformFeedbackClientName = useMemo(() => {
    const cf = workItem.custom_fields;
    if (typeof cf !== 'object' || !cf) return null;
    const fields = cf as Record<string, unknown>;
    if (fields.source !== 'platform-feedback') return null;
    return String(fields.submitted_by_client_name || '') || null;
  }, [workItem.custom_fields]);

  const knownTeamMemberIdentities = useMemo(() => {
    const byEmail = new Map<string, WorkItemUserIdentityData>();
    const byName = new Map<string, WorkItemUserIdentityData>();

    const addIdentity = (identity: WorkItemUserIdentityData | null | undefined) => {
      if (!identity) {
        return;
      }

      const normalizedUserId = Number(identity.userId);
      const userId = Number.isFinite(normalizedUserId) && normalizedUserId > 0 ? normalizedUserId : null;
      const email = identity.email?.trim() || null;
      const firstName = identity.firstName?.trim() || null;
      const lastName = identity.lastName?.trim() || null;
      const displayName = identity.displayName?.trim()
        || [firstName, lastName].filter(Boolean).join(' ').trim()
        || email
        || null;

      const normalizedIdentity: WorkItemUserIdentityData = {
        userId,
        firstName,
        lastName,
        email,
        displayName,
      };

      if (email) {
        byEmail.set(email.toLowerCase(), normalizedIdentity);
      }

      if (displayName) {
        byName.set(displayName.toLowerCase(), normalizedIdentity);
      }
    };

    teamMembers.forEach((member) => addIdentity({
      userId: member.id,
      firstName: member.first_name || null,
      lastName: member.last_name || null,
      email: member.email || null,
    }));

    assignedTeamMembers.forEach((member) => addIdentity({
      userId: member.user_id,
      firstName: member.first_name || null,
      lastName: member.last_name || null,
      email: member.email || null,
    }));

    localAssignees.forEach((assignee) => addIdentity({
      userId: assignee.user_id,
      firstName: assignee.first_name || null,
      lastName: assignee.last_name || null,
      email: assignee.email || null,
    }));

    if (reporterIdentity) {
      addIdentity(reporterIdentity);
    }

    return { byEmail, byName };
  }, [assignedTeamMembers, localAssignees, reporterIdentity, teamMembers]);

  const resolveTeamMemberIdentity = useCallback((candidate: TeamMemberIdentityCandidate): WorkItemUserIdentityData => {
    const directUserId = [candidate.userId, candidate.user_id, candidate.id]
      .map((value) => Number(value))
      .find((value) => Number.isFinite(value) && value > 0) ?? null;

    const email = candidate.email?.trim() || null;
    const firstName = candidate.first_name?.trim() || null;
    const lastName = candidate.last_name?.trim() || null;
    const displayName = candidate.displayName?.trim()
      || candidate.name?.trim()
      || [firstName, lastName].filter(Boolean).join(' ').trim()
      || email
      || null;

    const matchedByEmail = email ? knownTeamMemberIdentities.byEmail.get(email.toLowerCase()) : null;
    const matchedByName = displayName ? knownTeamMemberIdentities.byName.get(displayName.toLowerCase()) : null;
    const matchedIdentity = matchedByEmail || matchedByName || null;

    return {
      userId: directUserId || matchedIdentity?.userId || null,
      firstName: firstName || matchedIdentity?.firstName || null,
      lastName: lastName || matchedIdentity?.lastName || null,
      email: email || matchedIdentity?.email || null,
      displayName,
    };
  }, [knownTeamMemberIdentities]);

  useEffect(() => {
    setTeamMembers(initialData?.teamMembers || []);
    setLoadingMembers(initialDataLoading || !Array.isArray(initialData?.teamMembers));
    setReporter(initialData?.reporter || null);
    setHierarchy(initialData?.hierarchy || null);
    setLoadingHierarchy(false);
    setLinkedAppointments(initialData?.linkedAppointments || []);
    setLinkedJobs(initialData?.linkedJobs || []);
    setCloseGateStatus(initialData?.closeGateStatus ?? null);
    setAssignedTeamMembers(initialData?.assignedTeamMembers || []);
    setLinkedProject(initialData?.linkedProject || null);
    skipInitialCloseGateFetchRef.current = initialData?.closeGateStatus !== undefined;
  }, [initialData, initialDataLoading, workItem.id]);

  const loadTeamMembers = useCallback(async () => {
    try {
      setLoadingMembers(true);
      const token = getAuthToken();

      if (!token) {
        setTeamMembers([]);
        return;
      }

      // For email_task items, load only delegates of the source mailbox
      if (workItem.item_type === 'email_task') {
        const sourceMailboxId = workItem.custom_fields?.source_mailbox_id;

        if (sourceMailboxId) {
          // Fetch delegates for this specific mailbox
          const response = await fetch('/api/v1/email-domains', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              action: 'getMailboxDelegates',
              mailbox_id: sourceMailboxId
            })
          });

          if (response.ok) {
            const data = await response.json();
            const delegates: MailboxDelegateResponse[] = Array.isArray(data.data) ? data.data : [];
            if (data.success && delegates.length > 0) {
              // Map delegates to TeamMember format
              setTeamMembers(delegates.map((d) => ({
                id: d.id,
                first_name: d.first_name || '',
                last_name: d.last_name || '',
                email: d.email || ''
              })));
              return;
            }
          }
        }
      }

      // Workflow-scoped items (module_context carries the workflow key — e.g.
      // service-operations appointments, fleet-management vehicles,
      // digital-voting sessions) may only be assigned to members of the
      // matching workflow permission group — the same roster BeginActionModal
      // enforces via my-workflow-memberships and GroupsSection administers
      // under `source_workflow_key`. Assigning a non-member would leave them
      // unable to see or act on the workflow tile.
      const workflowContextKey = (workItem.module_context || '').trim().toLowerCase();
      if (workflowContextKey && !usesStakeholderAccountManager && workItem.item_type !== 'email_task') {
        // Single lightweight lookup — the cloud-directory groups listing
        // reconciles/provisions groups on every GET and is heavy enough to
        // stall pickers (observed 524s). /iam/workflow-members reads
        // permission_groups + members directly. A hung/failed request falls
        // back to the module roster below.
        const scopedResponse = await fetch(
          `/api/v1/iam/workflow-members?workflow_key=${encodeURIComponent(workflowContextKey)}`,
          {
            headers: { 'Authorization': `Bearer ${token}` },
            signal: AbortSignal.timeout(15000),
          },
        ).catch(() => null);
        if (scopedResponse?.ok) {
          const scopedData = await scopedResponse.json();
          if (scopedData?.success && scopedData.scoped) {
            const members: Array<{
              user_id: number | null;
              first_name: string | null;
              last_name: string | null;
              email: string | null;
              role_in_group?: string | null;
            }> = Array.isArray(scopedData.members) ? scopedData.members : [];
            const membersById = new Map<number, TeamMember>();
            for (const m of members) {
              const memberId = Number(m.user_id ?? 0);
              if (!Number.isFinite(memberId) || memberId <= 0 || membersById.has(memberId)) continue;
              membersById.set(memberId, {
                id: memberId,
                first_name: m.first_name || '',
                last_name: m.last_name || '',
                email: m.email || '',
                role: m.role_in_group === 'admin' ? 'Admin' : m.role_in_group === 'supervisor' ? 'Supervisor' : undefined,
              });
            }
            setTeamMembers(Array.from(membersById.values()).sort((left, right) => {
              const leftName = `${left.first_name} ${left.last_name}`.trim() || left.email;
              const rightName = `${right.first_name} ${right.last_name}`.trim() || right.email;
              return leftName.localeCompare(rightName);
            }));
            setTeamMembersWorkflowScoped(true);
            return;
          }
        }
        // No workflow group for this context (module-level context like
        // 'total-operations', or an unprovisioned workflow) → fall through to
        // the default module roster; the POST-side guard stays permissive too.
        setTeamMembersWorkflowScoped(false);
      } else {
        setTeamMembersWorkflowScoped(false);
      }

      if (usesStakeholderAccountManager) {
        const membersById = new Map<number, TeamMember>();
        let page = 1;
        let totalPages = 1;

        while (page <= totalPages) {
          const response = await fetch(`/api/v1/employees?page=${page}&limit=250&employment_status=active`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          if (!response.ok) {
            throw new Error('Failed to load active employees');
          }

          const data = await response.json();
          const employees: ActiveEmployeeResponse[] = Array.isArray(data.employees)
            ? data.employees
            : Array.isArray(data.data?.employees)
              ? data.data.employees
              : [];

          employees.forEach((employee) => {
            const employeeId = Number(employee.id ?? 0);
            const linkedUserId = Number(employee.user?.id ?? employee.user_id ?? 0);
            const memberId = Number.isFinite(linkedUserId) && linkedUserId > 0
              ? linkedUserId
              : employeeId;

            if (!Number.isFinite(memberId) || memberId <= 0) {
              return;
            }

            const rawFirstName = String(employee.user?.first_name || employee.first_name || '').trim();
            const rawLastName = String(employee.user?.last_name || employee.last_name || '').trim();
            const employeeReference = String(employee.employee_number || employee.id || '').trim();
            const hasDisplayName = rawFirstName.length > 0 || rawLastName.length > 0;
            const firstName = hasDisplayName ? rawFirstName : 'Employee';
            const lastName = hasDisplayName ? rawLastName : employeeReference;
            const email = String(employee.user?.email || '').trim();

            membersById.set(memberId, {
              id: memberId,
              first_name: firstName,
              last_name: lastName,
              email,
              role: [employee.job_title, employee.employee_number].filter(Boolean).join(' • ') || undefined,
            });
          });

          const nextTotalPages = Number(
            data.pagination?.pages
            ?? data.data?.pagination?.pages
            ?? data.data?.pagination?.total_pages
            ?? 1
          );
          totalPages = Number.isFinite(nextTotalPages) && nextTotalPages > 0 ? nextTotalPages : 1;
          page += 1;
        }

        setTeamMembers(Array.from(membersById.values()).sort((left, right) => {
          const leftName = `${left.first_name} ${left.last_name}`.trim() || left.email;
          const rightName = `${right.first_name} ${right.last_name}`.trim() || right.email;
          return leftName.localeCompare(rightName);
        }));
        return;
      }

      // Default: load all team members with module access (uses shared context to avoid duplicate API call)
      if (detailContext) {
        const users = await detailContext.loadOperationsUsers();
        setTeamMembers(users as TeamMember[]);
      } else {
        // Fallback: fetch directly
        const response = await fetch('/api/v1/module-access/users?module_key=total-operations', {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            setTeamMembers(data.data);
          }
        }
      }
    } catch (error) {
      console.error('Error loading team members:', error);
    } finally {
      setLoadingMembers(false);
    }
  }, [detailContext, usesStakeholderAccountManager, workItem.custom_fields?.source_mailbox_id, workItem.item_type, workItem.module_context]);

  // Filter team members by active solution key, and merge in contractors
  const filteredTeamMembers = useMemo(() => {
    let members = teamMembers;
    if (activeSolutionKey && !teamMembersWorkflowScoped) {
      const teamMemberUserIds = getTeamMemberUserIds();
      members = teamMembers.filter((member) => teamMemberUserIds.includes(member.id));
    }

    // Merge in contractors as available assignees (only for non-stakeholder,
    // non-email-task items — workflow-scoped items must be members, except
    // service-operations appointments where contractors are a legitimate
    // external-assignee target).
    const serviceOpsAppointmentContractorsAllowed = teamMembersWorkflowScoped
      && (workItem.module_context || '').trim().toLowerCase() === 'service-operations'
      && workItem.item_type === 'appointment';
    if ((!teamMembersWorkflowScoped || serviceOpsAppointmentContractorsAllowed)
      && !usesStakeholderAccountManager && workItem.item_type !== 'email_task') {
      const contractorMembers: TeamMember[] = assignmentContractors.map((c) => ({
        id: -c.id, // Use negative ID to distinguish from real user IDs
        first_name: c.company_name || c.contact_person || '',
        last_name: c.contact_person && c.company_name ? '(Contractor)' : '',
        email: c.email || '',
        role: 'Contractor',
      }));
      return [...members, ...contractorMembers];
    }

    return members;
  }, [teamMembers, teamMembersWorkflowScoped, activeSolutionKey, getTeamMemberUserIds, assignmentContractors, usesStakeholderAccountManager, workItem.item_type, workItem.module_context]);

  const loadReporter = useCallback(async () => {
    if (!workItem.created_by) return;

    try {
      const response = await fetch(`/api/v1/users/${workItem.created_by}`, {
        headers: { 'Authorization': `Bearer ${getAuthToken()}` }
      });

      if (response.ok) {
        const data = await response.json();
        const reporterData = data.data || data.user;
        if (data.success && reporterData) {
          setReporter(reporterData);
        }
      }
    } catch (error) {
      console.error('Error loading reporter:', error);
    }
  }, [workItem.created_by]);

  // Load linked appointments for a job (via work_item_relationships)
  const loadLinkedAppointments = useCallback(async () => {
    try {
      const response = await fetch('/api/v1/work-item-relationships', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'getLinkedAppointments',
          jobId: workItem.id
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setLinkedAppointments(data.data);
        }
      }
    } catch (error) {
      console.error('Error loading linked appointments:', error);
    }
  }, [workItem.id]);

  const loadLinkedJobs = useCallback(async () => {
    try {
      const response = await fetch('/api/v1/work-item-relationships', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'getLinkedJobs',
          appointmentId: workItem.id
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setLinkedJobs(data.data);
        }
      }
    } catch (error) {
      console.error('Error loading linked jobs:', error);
    }
  }, [workItem.id]);

  // Load billing submission source context (source job + appointments)
  const loadBillingSubmissionContext = useCallback(async () => {
    if (workItem.item_type !== 'billing_submission') return;

    const customFields = getWorkItemCustomFields(workItem);
    const sourceWorkItemId = customFields.source_work_item_id as string | undefined;
    if (!sourceWorkItemId) return;

    setLoadingBillingSource(true);
    try {
      const token = getAuthToken();
      if (!token) return;

      // Fetch source job details
      const jobResponse = await fetch(`/api/v1/work-items/${sourceWorkItemId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (jobResponse.ok) {
        const jobData = await jobResponse.json();
        if (jobData.success && jobData.data) {
          setBillingSourceJob({
            id: String(jobData.data.id),
            item_number: String(jobData.data.item_number || ''),
            title: String(jobData.data.title || ''),
            status: String(jobData.data.status || ''),
          });
        }
      }

      // Fetch appointments linked to the source job
      const apptResponse = await fetch('/api/v1/work-item-relationships', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'getLinkedAppointments',
          jobId: sourceWorkItemId,
        }),
      });
      if (apptResponse.ok) {
        const apptData = await apptResponse.json();
        if (apptData.success && apptData.data) {
          setBillingSourceAppointments(apptData.data);
        }
      }
    } catch (error) {
      console.error('Error loading billing submission source context:', error);
    } finally {
      setLoadingBillingSource(false);
    }
  }, [workItem]);

  // Load hierarchy data for the Context panel
  const loadHierarchy = useCallback(async () => {
    try {
      setLoadingHierarchy(true);
      const response = await fetch('/api/v1/work-item-relationships', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'getHierarchy',
          workItemId: workItem.id
        })
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setHierarchy(data.data);
        }
      }
    } catch (error) {
      console.error('Error loading hierarchy:', error);
    } finally {
      setLoadingHierarchy(false);
    }
  }, [workItem.id]);

  loadTeamMembersRef.current = loadTeamMembers;
  loadReporterRef.current = loadReporter;
  loadLinkedAppointmentsRef.current = loadLinkedAppointments;
  loadLinkedJobsRef.current = loadLinkedJobs;
  loadHierarchyRef.current = loadHierarchy;
  loadBillingSubmissionContextRef.current = loadBillingSubmissionContext;

  // Load team members for assignment
  useEffect(() => {
    if (initialDataLoading) {
      return;
    }

    // Workflow-scoped items (module_context = workflow key) must resolve the
    // workflow group's roster via loadTeamMembers — a preloaded generic
    // operations roster in initialData would bypass the membership filter.
    const mayBeWorkflowScoped = Boolean((workItem.module_context || '').trim())
      && workItem.item_type !== 'email_task'
      && !usesStakeholderAccountManager;

    if (!hasInitialTeamMembers || mayBeWorkflowScoped) {
      void loadTeamMembersRef.current?.();
    } else {
      setLoadingMembers(false);
    }

    if (!hasInitialReporter) {
      void loadReporterRef.current?.();
    }

    if (workItem.item_type === 'job') {
      if (!hasInitialLinkedAppointments) {
        void loadLinkedAppointmentsRef.current?.();
      }
    } else {
      setLinkedAppointments([]);
    }

    if (workItem.item_type === 'appointment') {
      if (!hasInitialLinkedJobs) {
        void loadLinkedJobsRef.current?.();
      }
    } else {
      setLinkedJobs([]);
    }

    if (['project', 'job', 'task', 'appointment'].includes(workItem.item_type || '')) {
      if (!hasInitialHierarchy) {
        void loadHierarchyRef.current?.();
      }
    } else {
      setHierarchy(null);
    }

    if (workItem.item_type === 'billing_submission') {
      void loadBillingSubmissionContextRef.current?.();
    } else {
      setBillingSourceJob(null);
      setBillingSourceAppointments([]);
    }
  }, [
    hasInitialHierarchy,
    hasInitialLinkedAppointments,
    hasInitialLinkedJobs,
    hasInitialReporter,
    hasInitialTeamMembers,
    initialDataLoading,
    usesStakeholderAccountManager,
    workItem.item_type,
    workItem.module_context,
  ]);

  // Load hierarchy when switching to context panel or when workItem changes
  useEffect(() => {
    if (initialDataLoading) {
      return;
    }

    if (effectivePanel === 'context' && (!hierarchy || String(hierarchy.current?.id) !== String(workItem.id))) {
      void loadHierarchyRef.current?.();
    }
  }, [effectivePanel, hierarchy, initialDataLoading, workItem.id]);

  const publishRealtimeMessage = useCallback(async (
    message: ReturnType<typeof buildOperationsRealtimeEvent>,
    channels: string[],
  ) => {
    const uniqueChannels = Array.from(new Set(channels.filter(Boolean)));
    if (uniqueChannels.length === 0) {
      return;
    }

    await Promise.all(uniqueChannels.map(async (channel) => {
      try {
        const readyToPublish = await ensureRealtimePublishChannel(channel);
        if (!readyToPublish) {
          return;
        }

        await publishRealtime(channel, message);
      } catch (error) {
        console.warn(`[DetailsSidebar] Failed to publish realtime work item refresh to ${channel}:`, error);
      }
    }));
  }, [ensureRealtimePublishChannel, publishRealtime]);

  const emitRealtimeRefreshForTarget = useCallback(({
    targetWorkItemId,
    targetItemType,
    status,
    eventType,
    parentJobId,
    targetUserIds,
    extraPayload,
    dispatchTimeEntriesChanged = false,
  }: {
    targetWorkItemId: string;
    targetItemType?: string | null;
    status?: string | null;
    eventType?: string;
    parentJobId?: string | null;
    targetUserIds?: Array<number | string | null | undefined>;
    extraPayload?: Record<string, unknown>;
    dispatchTimeEntriesChanged?: boolean;
  }) => {
    const normalizedItemType = (targetItemType || 'work_item').trim() || 'work_item';
    const normalizedStatus = typeof status === 'string' && status.trim().length > 0
      ? status.trim()
      : undefined;
    const isAppointment = normalizedItemType === 'appointment';
    const isJob = normalizedItemType === 'job';
    const numericClientId = Number(workItem.client_id);
    const normalizedTargetUserIds = normalizeRealtimeUserIds(targetUserIds || []);
    const normalizedRealtimeMessage = buildOperationsRealtimeEvent({
      eventType: eventType || (normalizedStatus
        ? (isAppointment
          ? 'appointment_status_changed'
          : isJob
            ? 'job_status_changed'
            : 'work_item_updated')
        : 'work_item_updated'),
      clientId: Number.isFinite(numericClientId) ? numericClientId : null,
      entityType: isAppointment ? 'appointment' : isJob ? 'job' : 'work_item',
      entityId: String(targetWorkItemId),
      actorUserId: authService.getUser()?.id ?? null,
      source: 'details-sidebar',
      sourceModule: 'details_sidebar',
      payload: {
        client_id: Number.isFinite(numericClientId) ? numericClientId : null,
        work_item_id: String(targetWorkItemId),
        appointment_id: isAppointment ? String(targetWorkItemId) : undefined,
        appointment_ids: isAppointment ? [String(targetWorkItemId)] : undefined,
        job_id: isJob ? String(targetWorkItemId) : undefined,
        parent_job_id: parentJobId || undefined,
        item_type: normalizedItemType,
        status: normalizedStatus,
        new_status: normalizedStatus,
        target_user_ids: normalizedTargetUserIds.length > 0 ? normalizedTargetUserIds : undefined,
        emitted_at: new Date().toISOString(),
        source: 'details-sidebar',
        source_module: 'details_sidebar',
        ...(extraPayload || {}),
      },
    });

    if (typeof window !== 'undefined') {
      const eventDetail = {
        workItemId: String(targetWorkItemId),
        workItemIds: [String(targetWorkItemId)],
        itemType: normalizedItemType,
        latestMessage: normalizedRealtimeMessage,
        message: normalizedRealtimeMessage,
        messages: [normalizedRealtimeMessage],
        status: normalizedStatus,
        targetUserIds: normalizedTargetUserIds,
        timestamp: Date.now(),
      };

      window.dispatchEvent(new CustomEvent('workItemStatusChange', { detail: eventDetail }));
      if (dispatchTimeEntriesChanged || normalizedRealtimeMessage.event === 'appointment_status_changed') {
        window.dispatchEvent(new CustomEvent('workItemTimeEntriesChanged', { detail: eventDetail }));
      }
      window.dispatchEvent(new CustomEvent(WORK_ITEM_LIST_CHANGED_EVENT, { detail: eventDetail }));
    }

    const realtimeChannels = buildAppointmentRealtimeFanoutChannels(numericClientId, normalizedTargetUserIds);

    void publishRealtimeMessage(normalizedRealtimeMessage, realtimeChannels);
  }, [publishRealtimeMessage, workItem.client_id]);

  const emitWorkItemRefreshEvents = useCallback((detail: Record<string, unknown> = {}) => {
    emitRealtimeRefreshForTarget({
      targetWorkItemId: String(workItem.id),
      targetItemType: workItem.item_type || 'work_item',
      status: typeof detail.status === 'string' ? detail.status : null,
      targetUserIds: isAppointment ? appointmentWorkflowOverrideTargets.map((target) => target.user_id) : [],
      extraPayload: detail,
    });
  }, [appointmentWorkflowOverrideTargets, emitRealtimeRefreshForTarget, isAppointment, workItem.id, workItem.item_type]);

  const emitAssigneeRefreshEvents = useCallback(({
    action,
    nextAssignees,
    addedUserIds = [],
    removedUserIds = [],
  }: {
    action: 'added' | 'removed' | 'updated';
    nextAssignees: SidebarAssignee[];
    addedUserIds?: Array<number | string | null | undefined>;
    removedUserIds?: Array<number | string | null | undefined>;
  }) => {
    const nextAssigneeUserIds = normalizeRealtimeUserIds(nextAssignees.map((assignee) => assignee.user_id));
    const normalizedAddedUserIds = normalizeRealtimeUserIds(addedUserIds);
    const normalizedRemovedUserIds = normalizeRealtimeUserIds(removedUserIds);
    const primaryUserId = nextAssigneeUserIds[0] ?? null;

    const eventDetail = {
      workItemId: String(workItem.id),
      itemType: workItem.item_type || 'work_item',
      action,
      addedUserIds: normalizedAddedUserIds,
      removedUserIds: normalizedRemovedUserIds,
      nextAssigneeUserIds,
      primaryUserId,
      assignees: nextAssignees,
      timestamp: Date.now(),
    };

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('workItemAssigneesChanged', { detail: eventDetail }));
    }

    emitRealtimeRefreshForTarget({
      targetWorkItemId: String(workItem.id),
      targetItemType: workItem.item_type || 'work_item',
      status: effectiveWorkItemStatus,
      extraPayload: {
        action: 'assignees_changed',
        assignee_action: action,
        next_assignee_user_ids: nextAssigneeUserIds,
        added_user_ids: normalizedAddedUserIds,
        removed_user_ids: normalizedRemovedUserIds,
        primary_user_id: primaryUserId,
      },
      targetUserIds: [...nextAssigneeUserIds, ...normalizedAddedUserIds, ...normalizedRemovedUserIds],
    });
  }, [effectiveWorkItemStatus, emitRealtimeRefreshForTarget, workItem.id, workItem.item_type]);

  const parentJob = hierarchy?.ancestors.findLast((node) => node.item_type === 'job') || null;
  const childJobs = hierarchy?.children.filter((node) => node.item_type === 'job') || [];

  // ========== PROJECT LINKING (for jobs / tasks / appointments) ==========
  const isLinkableType = ['job', 'task', 'appointment'].includes(workItem.item_type || '');

  // Resolve linked project from hierarchy ancestors on load
  useEffect(() => {
    if (!hierarchy || hierarchy.ancestors.length === 0) {
      setLinkedProject(null);
      return;
    }

    const projAncestor = hierarchy.ancestors.find(a => a.item_type === 'project');
    if (projAncestor) {
      setLinkedProject({ id: projAncestor.id, item_number: projAncestor.item_number, title: projAncestor.title });
    } else {
      setLinkedProject(null);
    }
  }, [hierarchy]);

  // Search projects debounced
  useEffect(() => {
    if (!showProjectPicker || projectSearchQuery.length < 1) {
      setProjectSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`/api/v1/work-items?item_type=project&search=${encodeURIComponent(projectSearchQuery)}&limit=10`, {
          headers: { Authorization: `Bearer ${getAuthToken()}` },
        });
        if (res.ok) {
          const j = await res.json();
          const projects: ProjectSearchResult[] = Array.isArray(j.data) ? j.data : [];
          if (j.success) setProjectSearchResults(projects.map((project) => ({ id: project.id, item_number: project.item_number, title: project.title })));
        }
      } catch { /* ignore */ }
    }, 300);
    return () => clearTimeout(timer);
  }, [projectSearchQuery, showProjectPicker]);

  // Close picker on outside click
  useEffect(() => {
    if (!showProjectPicker) return;
    const handler = (e: MouseEvent) => {
      if (projectPickerRef.current && !projectPickerRef.current.contains(e.target as Node)) setShowProjectPicker(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showProjectPicker]);

  const handleLinkToProject = async (project: { id: string; item_number: string; title: string }) => {
    setLinkingProject(true);
    try {
      const res = await fetch('/api/v1/work-item-relationships', {
        method: 'POST',
        headers: { Authorization: `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'create', sourceItemId: workItem.id, targetItemId: project.id, relationshipType: 'child_of' }),
      });
      if (res.ok) {
        setLinkedProject(project);
        setShowProjectPicker(false);
        setProjectSearchQuery('');
        // Refresh hierarchy to reflect the new relationship
        loadHierarchy();
      }
    } catch (err) {
      console.error('Failed to link project', err);
    } finally {
      setLinkingProject(false);
    }
  };

  const handleUnlinkProject = async () => {
    if (!linkedProject) return;
    setLinkingProject(true);
    try {
      const res = await fetch('/api/v1/work-item-relationships', {
        method: 'POST',
        headers: { Authorization: `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'unlink', sourceItemId: workItem.id, targetItemId: linkedProject.id, relationshipType: 'child_of' }),
      });
      if (res.ok) {
        setLinkedProject(null);
        loadHierarchy();
      }
    } catch (err) {
      console.error('Failed to unlink project', err);
    } finally {
      setLinkingProject(false);
    }
  };

  // Handle changing the responsible manager (work_items.manager_id).
  // The API cascades the change to children that still carry the inherited value.
  const handleManagerChange = async (nextManagerId: number | null) => {
    if (readOnly) return;
    // Client-configured enforcement: a responsible manager cannot be cleared
    // once required for this item type.
    if (requireManager && nextManagerId == null) {
      notify.error('A responsible manager is required for this work item type');
      return;
    }
    const previousId = localManagerId;
    const previousName = localManagerName;
    const nextMember = nextManagerId != null
      ? teamMembers.find((member) => member.id === nextManagerId) || null
      : null;

    setLocalManagerId(nextManagerId);
    setLocalManagerName(
      nextMember
        ? (`${nextMember.first_name || ''} ${nextMember.last_name || ''}`.trim() || nextMember.email || null)
        : null,
    );
    setSaving('manager');
    try {
      const response = await fetch(`/api/v1/work-items/${workItem.id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ manager_id: nextManagerId }),
      });

      if (!response.ok) {
        throw new Error(await getResponseErrorMessage(response, 'Failed to update manager'));
      }

      emitWorkItemRefreshEvents({ manager_id: nextManagerId });
    } catch (error) {
      setLocalManagerId(previousId);
      setLocalManagerName(previousName);
      notify.error(error instanceof Error ? error.message : 'Failed to update manager');
    } finally {
      setSaving(null);
    }
  };

  // ==========================================================================
  // AGENT ASSIGNMENT — work_items.assigned_persona: one owning agent persona
  // alongside human assignees. Options come from agent_identities (AgentsHub's
  // enable/disable SSOT). Handoff/recall always writes an internal comment.
  // ==========================================================================
  const [assignableAgents, setAssignableAgents] = useState<Array<{ persona_id: string; name: string; tagline: string | null; color: string | null }>>([]);
  const [localAssignedPersona, setLocalAssignedPersona] = useState<string | null>(
    () => (workItem as { assigned_persona?: string | null }).assigned_persona || null,
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/v1/work-items/assignable-agents', {
          headers: { Authorization: `Bearer ${getAuthToken()}` },
        });
        const data = await res.json();
        if (!cancelled && data?.success && Array.isArray(data.agents)) {
          // Only the support persona is a handoff target — other personas
          // own different surfaces, not customer tickets.
          setAssignableAgents(data.agents.filter((a: { persona_id: string }) => a.persona_id === 'support'));
        }
      } catch {
        // Agent list is optional — hide the section on failure.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    setLocalAssignedPersona((workItem as { assigned_persona?: string | null }).assigned_persona || null);
  }, [workItem]);

  const handleAssignAgent = async (personaId: string) => {
    if (!personaId) return;
    try {
      setSaving('assignees');
      const comment = (await notify.prompt({
        title: 'Hand off to agent',
        message: 'Handoff note (optional — recorded as a comment):',
        confirmText: 'Hand off',
        cancelText: 'Cancel',
      })) ?? '';
      const res = await fetch(`/api/v1/work-items/${workItem.id}/assignees`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ persona: personaId, comment }),
      });
      if (res.ok) {
        setLocalAssignedPersona(personaId);
        notify.success('Handed off to agent');
        onAssigneesChange?.(localAssignees);
      } else {
        notify.error(await getResponseErrorMessage(res, 'Failed to assign agent'));
      }
    } catch (error) {
      console.error('Error assigning agent:', error);
    } finally {
      setSaving(null);
    }
  };

  const handleClearAgent = async () => {
    try {
      setSaving('assignees');
      const res = await fetch(`/api/v1/work-items/${workItem.id}/assignees`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ clear_persona: true }),
      });
      if (res.ok) {
        setLocalAssignedPersona(null);
        notify.success('Agent unassigned');
      } else {
        notify.error(await getResponseErrorMessage(res, 'Failed to unassign agent'));
      }
    } catch (error) {
      console.error('Error unassigning agent:', error);
    } finally {
      setSaving(null);
    }
  };

  const assignedAgent = assignableAgents.find((a) => a.persona_id === localAssignedPersona) || null;
  // Agent handoff applies to the support/dev delivery item types only (TKT,
  // BUG, FEA, TAS) — not every item type the sidebar serves.
  const AGENT_ELIGIBLE_TYPES = ['ticket', 'support_ticket', 'bug', 'feature', 'task'];
  const showAgentSection = AGENT_ELIGIBLE_TYPES.includes(String(workItem.item_type || '').toLowerCase())
    && !isVehicle && !isTeamMember && !usesStakeholderAccountManager
    && (assignableAgents.length > 0 || localAssignedPersona);

  // Handle adding an assignee
  const handleAddAssignee = async (user: TeamMember) => {
    try {
      setSaving('assignees');

      if (usesStakeholderAccountManager && stakeholderContactId) {
        const response = await fetch(`/api/v1/contacts/${stakeholderContactId}`, {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${getAuthToken()}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ account_manager_id: user.id }),
        });

        if (!response.ok) {
          throw new Error(await getResponseErrorMessage(response, 'Failed to update account manager'));
        }

        const updatedAssignees: SidebarAssignee[] = [{
          user_id: user.id,
          first_name: user.first_name || '',
          last_name: user.last_name || '',
          email: user.email || '',
          role: user.role || '',
        }];

        setLocalAssignees(updatedAssignees);
        await onAssigneesChange?.(updatedAssignees);
        emitAssigneeRefreshEvents({
          action: 'added',
          nextAssignees: updatedAssignees,
          addedUserIds: [user.id],
        });
        setForceAssigneeDropdownOpen(false);
        return;
      }

      // When adding a driver to a vehicle, verify their driving licence and fleet designation
      if (isVehicle) {
        const { hasValidLicense, isDesignatedDriver } = await checkUserDriverEligibility(Number(user.id), getAuthToken());
        if (!hasValidLicense || !isDesignatedDriver) {
          const name = [user.first_name, user.last_name].filter(Boolean).join(' ').trim() || 'This user';
          const issues: string[] = [];
          if (!hasValidLicense) issues.push('has no valid driver\u2019s licence on record');
          if (!isDesignatedDriver) issues.push('is not designated as a fleet driver');
          const confirmed = await notify.confirm({
            title: 'Driver eligibility warning',
            message: `${name} ${issues.join(' and ')}. Assigning an unqualified driver to a fleet vehicle may create a liability risk. Proceed anyway?`,
            confirmText: 'Assign anyway',
            cancelText: 'Cancel',
            variant: 'warning',
          });
          if (!confirmed) {
            setSaving(null);
            return;
          }
        }
      }

      // Role-scoped workflows (e.g. digital-voting officials) start new
      // assignees at the neutral 'member' role — editable via the role picker.
      const defaultAssigneeRole = assigneeRoleOptions?.length ? 'member' : undefined;
      const response = await fetch(`/api/v1/work-items/${workItem.id}/assignees`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(
          user.id < 0
            ? { contact_id: Math.abs(user.id) }
            : { user_id: user.id, ...(defaultAssigneeRole ? { role: defaultAssigneeRole } : {}) }
        )
      });

      if (response.ok) {
        const isContractor = user.id < 0;
        const newAssignee: SidebarAssignee = isContractor
          ? {
              user_id: null,
              contact_id: Math.abs(user.id),
              assignee_type: 'contractor',
              company_name: user.first_name,
              contact_person: user.last_name === '(Contractor)' ? '' : user.last_name,
              email: user.email || '',
              role: 'Contractor',
            }
          : {
              user_id: user.id,
              first_name: user.first_name || '',
              last_name: user.last_name || '',
              email: user.email || '',
              // For role-scoped workflows this is the persisted assignment
              // role; otherwise the member's display hint (job title/tier).
              role: defaultAssigneeRole ?? user.role ?? '',
            };

        // For vehicles, a single driver is the expected state — replace
        // any existing assignees rather than appending.
        let updatedAssignees: SidebarAssignee[];
        if (isVehicle) {
          // Fire-and-forget DELETE calls for the old assignees so the
          // junction table stays in sync with the single-driver UI.
          for (const old of localAssignees) {
            if (old.user_id != null && Number(old.user_id) !== Number(user.id)) {
              fetch(
                `/api/v1/work-items/${workItem.id}/assignees/${old.user_id}`,
                { method: 'DELETE', headers: { 'Authorization': `Bearer ${getAuthToken()}` } },
              ).catch(() => {});
            }
          }
          updatedAssignees = [newAssignee];
        } else {
          updatedAssignees = [...localAssignees, newAssignee];
        }
        setLocalAssignees(updatedAssignees); // Update local state immediately
        await onAssigneesChange?.(updatedAssignees);
        emitAssigneeRefreshEvents({
          action: 'added',
          nextAssignees: updatedAssignees,
          addedUserIds: [user.id],
        });

        // Close the forced dropdown if it was open
        setForceAssigneeDropdownOpen(false);

        // For email_task items: Auto-transition status when first assignee is added
        // If status is 'open' or 'new', change to 'assigned'
        const isEmailTask = workItem.item_type === 'email_task';
        const isUnassignedStatus = isStartLikeWorkflowStatus(effectiveWorkItemStatus, workflowStatuses)
          || ['open', 'new'].includes(normalizeWorkflowStatusKey(effectiveWorkItemStatus));
        const wasFirstAssignee = localAssignees.length === 0;

        if (isEmailTask && isUnassignedStatus && wasFirstAssignee) {
          // Auto-change status to 'assigned'
          await handleStatusChange('assigned');
        }

        // If we had a pending 'assigned' status waiting for an assignee, apply it now
        if (pendingAssignedStatus) {
          setPendingAssignedStatus(false);
          await handleStatusChange('assigned');
        }
      } else {
        notify.error(await getResponseErrorMessage(response, 'Failed to add assignee'));
      }
    } catch (error) {
      console.error('Error adding assignee:', error);
    } finally {
      setSaving(null);
    }
  };

  // Handle removing an assignee
  const handleRemoveAssignee = async (userId: number) => {
    try {
      setSaving('assignees');

      if (usesStakeholderAccountManager && stakeholderContactId) {
        const response = await fetch(`/api/v1/contacts/${stakeholderContactId}`, {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${getAuthToken()}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ account_manager_id: null }),
        });

        if (!response.ok) {
          throw new Error(await getResponseErrorMessage(response, 'Failed to remove account manager'));
        }

        const updatedAssignees: SidebarAssignee[] = [];
        setLocalAssignees(updatedAssignees);
        await onAssigneesChange?.(updatedAssignees);
        emitAssigneeRefreshEvents({
          action: 'removed',
          nextAssignees: updatedAssignees,
          removedUserIds: localAssignees.map((assignee) => assignee.user_id),
        });
        return;
      }

      // Determine if this is a contractor removal (negative userId convention)
      const isContractorRemoval = userId < 0;
      const contactId = isContractorRemoval ? Math.abs(userId) : null;
      const deleteUrl = isContractorRemoval
        ? `/api/v1/work-items/${workItem.id}/assignees/contact-${contactId}`
        : `/api/v1/work-items/${workItem.id}/assignees/${userId}`;

      const response = await fetch(deleteUrl, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${getAuthToken()}` }
      });

      if (response.ok) {
        const updatedAssignees = isContractorRemoval
          ? localAssignees.filter(a => a.contact_id !== contactId)
          : localAssignees.filter(a => a.user_id !== userId);
        setLocalAssignees(updatedAssignees); // Update local state immediately
        await onAssigneesChange?.(updatedAssignees);
        emitAssigneeRefreshEvents({
          action: 'removed',
          nextAssignees: updatedAssignees,
          removedUserIds: [userId],
        });

        // For email_task items: If removing the last assignee and status is 'assigned',
        // revert status to 'open' (unassigned)
        const isEmailTask = workItem.item_type === 'email_task';
        const currentStatusIsAssigned = effectiveWorkItemStatus === 'assigned';
        const noMoreAssignees = updatedAssignees.length === 0;

        if (isEmailTask && currentStatusIsAssigned && noMoreAssignees) {
          // Revert status to 'open' since there are no more assignees
          await handleStatusChange(getPrimaryStartWorkflowStatusKey(workflowStatuses, 'open'));
        }
      }
    } catch (error) {
      console.error('Error removing assignee:', error);
    } finally {
      setSaving(null);
    }
  };

  // Update an assignee's named role (chairperson, secretary, driver, ...) —
  // work_item_assignees.role via the dedicated PATCH endpoint.
  const handleAssigneeRoleChange = async (assigneeUserId: number, role: string | null) => {
    const previousAssignees = localAssignees;
    const updatedAssignees = localAssignees.map((a) =>
      Number(a.user_id) === Number(assigneeUserId) ? { ...a, role } : a
    );
    setLocalAssignees(updatedAssignees);
    await onAssigneesChange?.(updatedAssignees);

    try {
      const response = await fetch(`/api/v1/work-items/${workItem.id}/assignees/${assigneeUserId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ role })
      });

      if (!response.ok) {
        throw new Error(await getResponseErrorMessage(response, 'Failed to update assignee role'));
      }
    } catch (error) {
      setLocalAssignees(previousAssignees);
      await onAssigneesChange?.(previousAssignees);
      notify.error(error instanceof Error ? error.message : 'Failed to update assignee role');
    }
  };

  // Team members: remove the linked vehicle by releasing the driver
  // allocation on the vehicle work item. The assignees DELETE route is the
  // SSOT path — it releases vehicle_driver_allocations and re-syncs
  // field_worker_workflow_state.current_vehicle_id.
  const handleRemoveTeamMemberVehicle = async () => {
    const vehicleId = teamMemberVehicleLink.vehicleId;
    const memberUserId = Number(getWorkItemCustomFields(workItem).user_id ?? workItem.assigned_to);

    if (!vehicleId || !Number.isFinite(memberUserId) || memberUserId <= 0) {
      return;
    }

    const confirmed = await notify.confirm({
      title: 'Remove vehicle',
      message: `Remove ${teamMemberVehicleLink.vehicleLabel || 'this vehicle'} from ${workItem.title || 'this team member'}?`,
      confirmText: 'Remove',
      cancelText: 'Cancel',
      variant: 'warning',
    });
    if (!confirmed) return;

    try {
      setSaving('vehicle');
      const response = await fetch(`/api/v1/work-items/${vehicleId}/assignees/${memberUserId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });

      if (!response.ok) {
        throw new Error(await getResponseErrorMessage(response, 'Failed to remove vehicle'));
      }

      // Clear the vehicle link on the virtual team-member item so the sidebar
      // reflects the release immediately (next profile fetch reads the SSOT).
      const nextCustomFields = { ...getWorkItemCustomFields(workItem) };
      const nextOperations = nextCustomFields.current_operations
        && typeof nextCustomFields.current_operations === 'object'
        && !Array.isArray(nextCustomFields.current_operations)
        ? { ...(nextCustomFields.current_operations as Record<string, unknown>) }
        : {};
      delete nextOperations.current_vehicle_id;
      delete nextOperations.current_vehicle_registration;
      if (Object.keys(nextOperations).length > 0) {
        nextCustomFields.current_operations = nextOperations;
      } else {
        delete nextCustomFields.current_operations;
      }
      delete nextCustomFields.current_vehicle_id;
      delete nextCustomFields.current_vehicle_registration;
      delete nextCustomFields.current_vehicle_title;
      delete nextCustomFields.vehicle_registration;
      delete nextCustomFields.current_vehicle;

      onPersistedUpdate?.({ custom_fields: nextCustomFields });
      notify.success('Vehicle removed');
    } catch (error) {
      console.error('Error removing team member vehicle:', error);
      notify.error(error instanceof Error ? error.message : 'Failed to remove vehicle');
    } finally {
      setSaving(null);
    }
  };

  // Vehicle picker options for team members without a linked vehicle.
  // Loaded lazily — only fetched when the empty vehicle slot renders.
  const [vehicleOptions, setVehicleOptions] = useState<Array<{ id: string; label: string; driverName: string | null }>>([]);
  const [vehicleOptionsLoading, setVehicleOptionsLoading] = useState(false);

  const loadVehicleOptions = useCallback(async () => {
    const token = getAuthToken();
    if (!token) return;
    try {
      setVehicleOptionsLoading(true);
      const response = await fetch('/api/v1/work-items/fleet', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ action: 'getVehicles' }),
      });
      if (!response.ok) return;
      const payload = await response.json().catch(() => null);
      const list = Array.isArray(payload?.vehicles)
        ? payload.vehicles
        : (Array.isArray(payload?.data?.vehicles) ? payload.data.vehicles : []);

      setVehicleOptions(list
        .map((vehicle: { id?: unknown; title?: unknown; custom_fields?: unknown; assigned_to_name?: unknown }) => {
          const id = toNonEmptyString(vehicle.id);
          if (!id) return null;
          const vehicleFields = vehicle.custom_fields && typeof vehicle.custom_fields === 'object' && !Array.isArray(vehicle.custom_fields)
            ? vehicle.custom_fields as Record<string, unknown>
            : {};
          return {
            id,
            label: toNonEmptyString(vehicleFields.registration) || toNonEmptyString(vehicle.title) || 'Vehicle',
            driverName: toNonEmptyString(vehicle.assigned_to_name),
          };
        })
        .filter((option: { id: string; label: string; driverName: string | null } | null): option is { id: string; label: string; driverName: string | null } => option !== null));
    } catch {
      // Non-fleet environments may not have the endpoint — leave the list empty.
    } finally {
      setVehicleOptionsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isTeamMember || readOnly || teamMemberIsNonDriver || teamMemberVehicleLink.vehicleLabel) return;
    void loadVehicleOptions();
  }, [isTeamMember, readOnly, teamMemberIsNonDriver, teamMemberVehicleLink.vehicleLabel, loadVehicleOptions]);

  // Assign a vehicle to the team member via the fleet SSOT — assignVehicleDriver
  // creates the allocation, syncs work_items.assigned_to, and publishes the
  // realtime event so the member's Smart Task refreshes.
  const handleAssignTeamMemberVehicle = async (vehicleId: string) => {
    const memberUserId = Number(getWorkItemCustomFields(workItem).user_id ?? workItem.assigned_to);
    if (!vehicleId || !Number.isFinite(memberUserId) || memberUserId <= 0) {
      return;
    }

    try {
      setSaving('vehicle');
      const response = await fetch('/api/v1/work-items/fleet', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'assignDriver',
          vehicleId,
          driverId: memberUserId,
          driverName: workItem.title || null,
          driverType: 'user',
        }),
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok || payload?.success === false) {
        throw new Error(typeof payload?.error === 'string' ? payload.error : 'Failed to assign vehicle');
      }

      // Reflect the assignment on the virtual team-member item so the sidebar
      // flips back to the linked-vehicle row immediately.
      const vehicleOption = vehicleOptions.find((option) => option.id === vehicleId);
      const nextCustomFields = { ...getWorkItemCustomFields(workItem) };
      const nextOperations = nextCustomFields.current_operations
        && typeof nextCustomFields.current_operations === 'object'
        && !Array.isArray(nextCustomFields.current_operations)
        ? { ...(nextCustomFields.current_operations as Record<string, unknown>) }
        : {};
      nextOperations.current_vehicle_id = vehicleId;
      nextOperations.current_vehicle_registration = vehicleOption?.label || null;
      nextCustomFields.current_operations = nextOperations;

      onPersistedUpdate?.({ custom_fields: nextCustomFields });
      notify.success('Vehicle assigned');
    } catch (error) {
      console.error('Error assigning team member vehicle:', error);
      notify.error(error instanceof Error ? error.message : 'Failed to assign vehicle');
    } finally {
      setSaving(null);
    }
  };

  // Handle updating labels
  const handleLabelsChange = async (labels: string[]) => {
    try {
      setSaving('labels');
      setLocalLabels(labels); // Update local state immediately
      await onUpdate({ tags: labels });
    } catch (error) {
      console.error('Error updating labels:', error);
      setLocalLabels(workItem.tags || []); // Revert on error
    } finally {
      setSaving(null);
    }
  };

  // Handle updating due date
  const handleDueDateChange = async (date: string) => {
    try {
      setSaving('due_date');
      await onUpdate({ due_date: date || null });
    } catch (error) {
      console.error('Error updating due date:', error);
    } finally {
      setSaving(null);
    }
  };

  // Handle updating priority
  const handlePriorityChange = async (priority: string) => {
    try {
      setSaving('priority');
      await onUpdate({ priority: priority as WorkItem['priority'] });
    } catch (error) {
      console.error('Error updating priority:', error);
    } finally {
      setSaving(null);
    }
  };

  const loadJobAppointmentsForClose = useCallback(async (jobId: string): Promise<CloseImpactAppointment[]> => {
    const token = getAuthToken();
    if (!token) {
      throw new Error('Authentication expired. Please sign in again.');
    }

    const response = await fetch('/api/v1/work-item-relationships', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: 'getLinkedAppointments',
        jobId,
      }),
    });

    if (!response.ok) {
      throw new Error(await getResponseErrorMessage(response, 'Failed to load linked appointments'));
    }

    const payload = await response.json().catch(() => null) as
      | { success?: boolean; data?: Array<CloseImpactAppointment> }
      | null;

    if (!payload?.success) {
      throw new Error('Failed to load linked appointments');
    }

    return Array.isArray(payload.data) ? payload.data : [];
  }, []);

  const loadRunningTimersForClose = useCallback(async (): Promise<CloseImpactTimer[]> => {
    const token = getAuthToken();
    if (!token) {
      throw new Error('Authentication expired. Please sign in again.');
    }

    const includeRelated = workItem.item_type === 'job';
    const response = await fetch(
      `/api/v1/work-items/${workItem.id}/time-entries${includeRelated ? '?include_related=true' : ''}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (!response.ok) {
      throw new Error(await getResponseErrorMessage(response, 'Failed to load running timers'));
    }

    const payload = await response.json().catch(() => null) as
      | { success?: boolean; data?: Array<CloseImpactTimeEntryCandidate> }
      | Array<CloseImpactTimeEntryCandidate>
      | null;

    const entries = Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.data)
        ? payload.data
        : [];

    return entries
      .filter((entry) => entry?.is_running)
      .map((entry) => {
        const fullName = `${entry.first_name || ''} ${entry.last_name || ''}`.trim();
        const resolvedWorkItemId = String(entry.source_item_id || entry.work_item_id || workItem.id);
        return {
          id: String(entry.id),
          work_item_id: resolvedWorkItemId,
          work_item_number: String(entry.source_item_number || '').trim(),
          work_item_title: String(entry.source_item_title || '').trim(),
          work_item_type: String(entry.source_item_type || workItem.item_type || '').trim(),
          activity_type: String(entry.activity_type || 'work').trim(),
          started_at: entry.started_at || null,
          user_id: entry.user_id != null && Number.isFinite(Number(entry.user_id)) ? Number(entry.user_id) : null,
          user_name: fullName || String(entry.user_name || '').trim() || 'Unknown user',
        };
      });
  }, [workItem.id, workItem.item_type]);

  const buildCloseImpactSummary = useCallback(async (): Promise<CloseImpactSummary> => {
    const [appointments, runningTimers] = await Promise.all([
      workItem.item_type === 'job'
        ? loadJobAppointmentsForClose(String(workItem.id))
        : Promise.resolve([] as CloseImpactAppointment[]),
      loadRunningTimersForClose(),
    ]);

    return {
      appointmentsToClose: appointments.filter((appointment) => {
        const normalized = normalizeWorkflowStatusKey(appointment.status);
        return normalized !== 'closed' && !isProtectedTerminalAppointmentStatus(normalized);
      }),
      runningTimers,
    };
  }, [loadJobAppointmentsForClose, loadRunningTimersForClose, workItem.id, workItem.item_type]);

  const prepareCloseImpactForStatusChange = useCallback(async (): Promise<{
    confirmed: boolean;
    impact: CloseImpactSummary | null;
  }> => {
    const impact = await buildCloseImpactSummary();
    const hasAppointmentsToClose = impact.appointmentsToClose.length > 0;
    const hasRunningTimers = impact.runningTimers.length > 0;

    if (!hasAppointmentsToClose && !hasRunningTimers) {
      return { confirmed: true, impact: null };
    }

    const appointmentLines = impact.appointmentsToClose.slice(0, 8).map((appointment) => `• ${formatCloseImpactAppointmentLine(appointment)}`);
    const timerLines = impact.runningTimers.slice(0, 8).map((timer) => `• ${formatCloseImpactTimerLine(timer)}`);
    const messageParts: string[] = [];

    if (hasAppointmentsToClose) {
      messageParts.push(
        `The following appointment${impact.appointmentsToClose.length === 1 ? '' : 's'} will also be closed:`,
        ...appointmentLines,
      );

      if (impact.appointmentsToClose.length > appointmentLines.length) {
        messageParts.push(`• …and ${impact.appointmentsToClose.length - appointmentLines.length} more appointment${impact.appointmentsToClose.length - appointmentLines.length === 1 ? '' : 's'}`);
      }
    }

    if (hasRunningTimers) {
      if (messageParts.length > 0) {
        messageParts.push('');
      }

      messageParts.push(
        `The following running timer${impact.runningTimers.length === 1 ? '' : 's'} will be stopped:`,
        ...timerLines,
      );

      if (impact.runningTimers.length > timerLines.length) {
        messageParts.push(`• …and ${impact.runningTimers.length - timerLines.length} more running timer${impact.runningTimers.length - timerLines.length === 1 ? '' : 's'}`);
      }
    }

    messageParts.push('', 'Do you want to continue?');

    const confirmed = await confirm({
      title: workItem.item_type === 'job' ? 'Close job?' : 'Close appointment?',
      message: messageParts.join('\n'),
      confirmText: 'Continue closing',
      cancelText: 'Cancel',
      variant: 'warning',
      onConfirm: () => { },
    });

    return {
      confirmed,
      impact: confirmed ? impact : null,
    };
  }, [buildCloseImpactSummary, workItem.item_type]);

  const stopRunningTimersForClose = useCallback(async (timers: CloseImpactTimer[]) => {
    if (timers.length === 0) {
      return;
    }

    const token = getAuthToken();
    if (!token) {
      throw new Error('Authentication expired. Please sign in again.');
    }

    const endedAt = new Date().toISOString();
    const results = await Promise.allSettled(timers.map(async (timer) => {
      const response = await fetch(`/api/v1/work-items/${timer.work_item_id}/time-entries/${timer.id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ended_at: endedAt,
          is_running: false,
        }),
      });

      const payload = await response.json().catch(() => null) as
        | { success?: boolean; error?: string; message?: string }
        | null;

      if (!response.ok || payload?.success === false) {
        throw new Error(payload?.error || payload?.message || `Failed to stop timer for ${describeCloseImpactTimer(timer)}`);
      }

      return timer;
    }));

    const failedTimers = results.flatMap((result, index) => (
      result.status === 'rejected'
        ? [describeCloseImpactTimer(timers[index])]
        : []
    ));

    const refreshedTimers = results.flatMap((result) => (
      result.status === 'fulfilled'
        ? [result.value]
        : []
    ));

    refreshedTimers.forEach((timer) => {
      const timerItemType = typeof timer.work_item_type === 'string' && timer.work_item_type.trim().length > 0
        ? timer.work_item_type.trim()
        : null;
      const parentJobId = workItem.item_type === 'job'
        ? String(workItem.id)
        : null;

      emitRealtimeRefreshForTarget({
        targetWorkItemId: String(timer.work_item_id),
        targetItemType: timerItemType,
        parentJobId,
        eventType: 'work_item_time_entries_changed',
        dispatchTimeEntriesChanged: true,
        extraPayload: {
          time_entry_id: String(timer.id),
          activity_type: timer.activity_type || null,
          timer_running: false,
          stopped_at: endedAt,
          appointment_id: timerItemType === 'appointment' ? String(timer.work_item_id) : undefined,
          appointment_ids: timerItemType === 'appointment' ? [String(timer.work_item_id)] : undefined,
        },
      });
    });

    if (failedTimers.length > 0) {
      throw new Error(
        failedTimers.length === 1
          ? `Failed to stop the running timer for ${failedTimers[0]}.`
          : `Failed to stop ${failedTimers.length} running timers (${failedTimers.slice(0, 3).join(', ')}${failedTimers.length > 3 ? ', …' : ''}).`,
      );
    }
  }, [emitRealtimeRefreshForTarget, workItem.id, workItem.item_type]);

  // Close all children appointments when a job is closed
  const closeChildrenAppointments = async (jobId: string) => {
    try {
      const children = await loadJobAppointmentsForClose(jobId);

      if (!Array.isArray(children) || children.length === 0) {
        return;
      }

      // Close each child appointment — skip closed and protected terminal statuses (missed, cancelled, no_show)
      // that are system-determined final states and should not be overridden to closed
      const closeableChildren = children.filter((child) => {
        const normalized = normalizeWorkflowStatusKey(child.status);
        return normalized !== 'closed' && !isProtectedTerminalAppointmentStatus(normalized);
      });
      const closeResults = await Promise.allSettled(closeableChildren.map(async (child) => {
        const childStatus = child.status;
        const normalizedChildStatus = childStatus ? normalizeWorkflowStatusKey(childStatus) : '';

        // Only close if not already closed
        if (normalizedChildStatus === 'closed') {
          return child;
        }

        const response = await fetch(`/api/v1/work-items/${child.id}`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${getAuthToken()}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ status: 'closed' })
        });

        if (!response.ok) {
          throw new Error(await getResponseErrorMessage(response, `Failed to close ${child.item_number || 'appointment'}`));
        }

        return child;
      }));

      const closedChildren = closeResults.flatMap((result) => result.status === 'fulfilled' ? [result.value] : []);
      const failedChildren = closeResults.flatMap((result, index) => result.status === 'rejected' ? [closeableChildren[index]] : []);

      closedChildren.forEach((child) => {
        emitRealtimeRefreshForTarget({
          targetWorkItemId: String(child.id),
          targetItemType: 'appointment',
          status: 'closed',
          eventType: 'appointment_status_changed',
          parentJobId: String(jobId),
          extraPayload: {
            job_id: String(jobId),
            parent_job_id: String(jobId),
          },
        });
      });

      if (closedChildren.length > 0) {
        console.log(`[DetailsSidebar] Closed ${closedChildren.length} child appointments for job ${jobId}`);
        notify.success(`Closed ${closedChildren.length} appointment${closedChildren.length > 1 ? 's' : ''}`);
      }

      if (failedChildren.length > 0) {
        console.error('[DetailsSidebar] Failed to close some child appointments:', failedChildren);
        notify.error(`Failed to close ${failedChildren.length} linked appointment${failedChildren.length > 1 ? 's' : ''}`);
      }
    } catch (error) {
      console.error('[DetailsSidebar] Error closing child appointments:', error);
      notify.error('Failed to close some child appointments');
    }
  };

  // Handle updating status via API
  async function handleStatusChange(newStatus: string) {
    // For email_task: Check if transitioning to 'assigned' without an assignee
    const isEmailTask = workItem.item_type === 'email_task';
    const transitioningToAssigned = newStatus === 'assigned';
    const hasNoAssignees = localAssignees.length === 0;

    if (isEmailTask && transitioningToAssigned && hasNoAssignees) {
      // Cannot transition to 'assigned' without an assignee
      // Open the assignee dropdown and wait for user to add one
      setPendingAssignedStatus(true);
      setForceAssigneeDropdownOpen(true);
      // Don't proceed with status change yet
      return;
    }

    const targetStatusKey = newStatus.toLowerCase().replace(/[\s-]+/g, '_');
    const normalizedStatus = normalizeWorkflowStatusKey(newStatus);
    if (normalizedStatus === 'closed') {
      const closeImpact = await prepareCloseImpactForStatusChange();
      if (!closeImpact.confirmed) {
        return;
      }

      if (closeImpact.impact?.runningTimers.length) {
        await stopRunningTimersForClose(closeImpact.impact.runningTimers);
      }
    } else if (shouldStopTimersForStatusChange(workItem.item_type, newStatus)) {
      const closeImpact = await buildCloseImpactSummary();
      if (closeImpact.runningTimers.length) {
        await stopRunningTimersForClose(closeImpact.runningTimers);
      }
    }

    const canUseAppointmentWorkflowOverride = shouldUseAdminAppointmentStatusOverride
      && [
        'scheduled',
        'confirmed',
        'on_route',
        'en_route',
        'in_progress',
        'checked_in',
        'working',
        'paused',
        'on_hold',
        'resolved',
        'partial',
        'unresolved',
        'rescheduled',
        'cancelled',
      ].includes(normalizedStatus);

    // Process checklist enforcement — block transition only when the target status has active
    // template-driven checklists and the admin override path is NOT being used.
    if (!canUseAppointmentWorkflowOverride) {
      try {
        const checkRes = await fetch(
          getApiUrl(`/api/v1/work-items/${workItem.id}/process-gates?target_status=${encodeURIComponent(targetStatusKey)}`),
          { headers: { Authorization: `Bearer ${getAuthToken()}` } },
        );
        const checkData = await checkRes.json();
        // Block on ANY gate (process gate templates OR forms-only gates), not just template-driven ones
        if (checkRes.ok && checkData.success && checkData.data?.is_blocked) {
          pendingGateTargetStatusRef.current = targetStatusKey;
          openProcessChecklist?.(targetStatusKey);
          const gateType = checkData.data?.has_process_gate_templates
            ? 'process checklist'
            : 'required form';
          notify.warning(`The ${gateType} must be completed before this item can be moved to "${newStatus}".`);
          const gateErr: GateBlockedError = new Error(`${gateType === 'process checklist' ? 'Process checklist' : 'Required form'} must be completed first`);
          gateErr.code = 'GATE_BLOCKED';
          throw gateErr;
        }
      } catch (checkError: unknown) {
        if ((checkError as GateBlockedError)?.code === 'GATE_BLOCKED') throw checkError;
        // Non-blocking: if the fetch fails, allow the transition anyway
        if (!isSilentFetchError(checkError)) console.error('[DetailsSidebar] Failed to check process checklist:', checkError);
      }
    }

    // Billing gate — runs after the process gates clear (including the
    // processGateCleared re-entry). Warn when a job is closed with no
    // invoice-requested billing submission: draft/pending/approved/
    // converted/returned submissions mean billing is in flight; only
    // rejected/cancelled/void are ignored.
    if (normalizedStatus === 'closed' && workItem.item_type === 'job') {
      try {
        const subRes = await fetch(
          getApiUrl(`/api/v1/work-items/${workItem.id}/billing-submission?scope=all`),
          { headers: { Authorization: `Bearer ${getAuthToken()}` } },
        );
        const subData = await subRes.json().catch(() => null) as {
          success?: boolean;
          data?: Array<{ status?: string | null; requested_doc_type?: string | null }> | null;
        } | null;
        if (subRes.ok && subData?.success && Array.isArray(subData.data)) {
          const deadStatuses = new Set(['rejected', 'cancelled', 'void', 'deleted']);
          const active = subData.data.filter(
            (s) => !deadStatuses.has(String(s?.status || '').toLowerCase()),
          );
          const invoiceRequested = active.filter(
            (s) => String(s?.requested_doc_type || 'invoice').toLowerCase() === 'invoice',
          );

          let billingWarning: string | null = null;
          if (active.length === 0) {
            billingWarning = 'No billing submission or invoice has been generated for this job. Any billable time, materials and expenses will not be invoiced.';
          } else if (invoiceRequested.length === 0) {
            billingWarning = 'Billing submissions exist for this job, but none request an invoice — no invoice will be generated from them.';
          }

          if (billingWarning) {
            const proceedAnyway = await confirm({
              title: 'Close without invoicing?',
              message: `${billingWarning}\n\nAre you sure you want to close this job?`,
              confirmText: 'Close anyway',
              cancelText: 'Go back',
              variant: 'warning',
              onConfirm: () => { },
            });
            if (!proceedAnyway) return;
          }
        }
      } catch (billingCheckError) {
        // Informational only — never blocks the transition.
        if (!isSilentFetchError(billingCheckError)) console.error('[DetailsSidebar] Failed to check billing submissions:', billingCheckError);
      }
    }

    if (canUseAppointmentWorkflowOverride) {
      try {
        onStatusTransitionStart?.();
        setSaving('status');

        // Use cached request if available and matches the target status, otherwise prepare fresh
        const pendingRequestedStatus = typeof pendingAppointmentOverrideRequestRef.current?.requestBody?.requested_status === 'string'
          ? pendingAppointmentOverrideRequestRef.current.requestBody.requested_status
          : null;
        const normalizedPendingStatus = pendingRequestedStatus ? normalizeWorkflowStatusKey(pendingRequestedStatus) : null;

        const preparedOverride = pendingAppointmentOverrideRequestRef.current
          && normalizedPendingStatus === normalizedStatus
          ? pendingAppointmentOverrideRequestRef.current
          : await prepareAdminAppointmentOverride({
            authToken: getAuthToken() || '',
            workItemId: workItem.id,
            requestedStatus: newStatus,
            currentStatus: transitionBaseStatus,
            subjectLabel: appointmentWorkflowOverrideTargets.length === 1
              ? appointmentWorkflowOverrideTargets[0].display_name
              : appointmentWorkflowOverrideTargets.length > 1
                ? `${appointmentWorkflowOverrideTargets.length} field workers`
                : 'the assigned field worker',
            targetUserIds: appointmentWorkflowOverrideTargets.map((target) => target.user_id),
          });

        pendingAppointmentOverrideRequestRef.current = null;
        if (!preparedOverride) {
          setSaving(null);
          return;
        }

        console.log('[DetailsSidebar] Sending override request:', {
          requestBody: preparedOverride.requestBody,
          hasMetadata: !!preparedOverride.requestBody.metadata,
          metadataKeys: preparedOverride.requestBody.metadata ? Object.keys(preparedOverride.requestBody.metadata) : [],
          hasOverrideReason: typeof (preparedOverride.requestBody.metadata as Record<string, unknown>)?.override_reason === 'string',
          overrideReasonLength: String((preparedOverride.requestBody.metadata as Record<string, unknown>)?.override_reason || '').length,
        });

        const response = await fetch('/api/v1/work-items/workflow-state', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${getAuthToken()}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(preparedOverride.requestBody)
        });

        const responseData = await response.json().catch(() => null);
        if (!response.ok || !responseData?.success) {
          throw new Error(responseData?.error || 'Failed to override appointment workflow status');
        }

        const persistedStatus = typeof responseData?.data?.status === 'string'
          ? responseData.data.status
          : newStatus;
        const persistedUpdates = responseData?.data && typeof responseData.data === 'object'
          ? responseData.data as Partial<WorkItem>
          : { status: persistedStatus };

        setLocalStatus(persistedStatus);
        setLocalResolutionType(typeof responseData?.data?.resolution_type === 'string' ? responseData.data.resolution_type : '');
        onPersistedUpdate?.(persistedUpdates);
        await Promise.resolve(onStatusChange?.(persistedStatus));
        notify.success(formatAdminAppointmentOverrideSuccessMessage({
          requestedStatus: persistedStatus,
          successContext: preparedOverride.successContext,
        }));

        const previousStatus = effectiveWorkItemStatus || workItem.status || '';
        const suppressUserNotifications = isPostResolutionAdminLifecycleTransition(previousStatus, persistedStatus);

        emitRealtimeRefreshForTarget({
          targetWorkItemId: String(workItem.id),
          targetItemType: workItem.item_type || 'work_item',
          status: persistedStatus,
          targetUserIds: suppressUserNotifications
            ? []
            : Array.isArray(responseData?.data?.target_user_ids)
              ? responseData.data.target_user_ids
              : appointmentWorkflowOverrideTargets.map((target) => target.user_id),
          dispatchTimeEntriesChanged: true,
          extraPayload: {
            source: 'details-sidebar',
            target_user_ids: Array.isArray(responseData?.data?.target_user_ids) ? responseData.data.target_user_ids : undefined,
            suppress_user_notifications: suppressUserNotifications,
          },
        });

        setShowAppointmentAdminWorkflowOverride(false);
        void refetchSsotStatus();

        return;
      } catch (error) {
        console.error('Error overriding appointment workflow status:', error);
        throw error;
      } finally {
        onStatusTransitionEnd?.();
        setSaving(null);
      }
    }

    try {
      onStatusTransitionStart?.();
      setSaving('status');

      const stakeholderFields = stakeholderContext.customFields;
      const stakeholderContactId = stakeholderContext.contactId;
      const isStakeholderContact = stakeholderContext.isStakeholderContact;

      if (isStakeholderContact) {
        const progressPercentage = getSuggestedProgressFromStatus(newStatus, workItem.progress_percentage);
        const persistedUpdates: Partial<WorkItem> = {
          status: newStatus,
          progress_percentage: progressPercentage ?? undefined,
          custom_fields: {
            ...(stakeholderFields as Record<string, unknown>),
            account_status: newStatus,
          },
        };

        setLocalStatus(newStatus);
        onPersistedUpdate?.(persistedUpdates);

        if (onStatusChange) {
          await Promise.resolve(onStatusChange(newStatus));
        } else {
          const { mapStakeholderStatusToAccountStatus } = await import('@/lib/work-items/stakeholderStatusConfig');
          const accountStatus = mapStakeholderStatusToAccountStatus(newStatus);
          if (accountStatus) {
            const token = getAuthToken();
            const response = await fetch(`/api/v1/contacts/${stakeholderContactId}`, {
              method: 'PUT',
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                account_status: accountStatus,
                is_active: isActiveContactAccountStatus(accountStatus),
              }),
            });

            if (!response.ok) {
              throw new Error(await getResponseErrorMessage(response, 'Failed to update contact account status'));
            }
          }
        }

        return;
      }

      // Admin re-open transitions must be status-only — the API blocks non-status fields
      // on locked items. Progress_percentage would cause a 423 rejection.
      const isAdminReopenTransition = normalizeWorkflowStatusKey(newStatus) === 'admin_reopened';
      const progressPercentage = isAdminReopenTransition
        ? undefined
        : getSuggestedProgressFromStatus(newStatus, workItem.progress_percentage);

      // Call API to update the work item status and optionally progress
      const patchBody: Record<string, unknown> = { status: newStatus };
      if (progressPercentage != null) {
        patchBody.progress_percentage = progressPercentage;
      }

      const response = await fetch(`/api/v1/work-items/${workItem.id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(patchBody)
      });

      if (response.ok) {
        const responseData = await response.json().catch(() => null);
        const persistedStatus = typeof responseData?.data?.status === 'string'
          ? responseData.data.status
          : newStatus;
        const persistedUpdates = responseData?.data && typeof responseData.data === 'object'
          ? responseData.data as Partial<WorkItem>
          : {
            status: persistedStatus,
            progress_percentage: progressPercentage ?? undefined,
          };

        setLocalStatus(persistedStatus);
        if (typeof responseData?.data?.resolution_type === 'string') {
          setLocalResolutionType(responseData.data.resolution_type);
        }

        onPersistedUpdate?.(persistedUpdates);
        const normalizedStatus = normalizeWorkflowStatusKey(persistedStatus);
        // Close child appointments when job status changes to cancelled, resolved, closed, or admin_reopened
        // Note: admin_reopened closing children is intentional - this ensures all child appointments reflect the parent's terminal state
        const shouldCloseLinkedAppointments = workItem.item_type === 'job'
          && ['cancelled', 'resolved', 'closed', 'admin_reopened'].includes(normalizedStatus);

        if (shouldCloseLinkedAppointments) {
          await closeChildrenAppointments(String(workItem.id));
        }

        // Status saved directly via API - notify parent with new status
        await Promise.resolve(onStatusChange?.(persistedStatus));

        const previousStatusBeforePatch = effectiveWorkItemStatus || workItem.status || '';
        const suppressUserNotificationsPatch = isPostResolutionAdminLifecycleTransition(previousStatusBeforePatch, persistedStatus);

        emitRealtimeRefreshForTarget({
          targetWorkItemId: String(workItem.id),
          targetItemType: workItem.item_type || 'work_item',
          status: persistedStatus,
          targetUserIds: suppressUserNotificationsPatch
            ? []
            : isAppointment
              ? appointmentWorkflowOverrideTargets.map((target) => target.user_id)
              : [],
          extraPayload: {
            status: persistedStatus,
            source: 'details-sidebar',
            suppress_user_notifications: suppressUserNotificationsPatch,
          },
        });

        if (workItem.item_type === 'job') {
          void loadLinkedAppointments();
        }
        if (['project', 'job', 'task', 'appointment'].includes(workItem.item_type || '')) {
          void loadHierarchy();
        }

        // Refresh process checklist status whenever status changes (requirements may have been triggered/released)
        if (closeGateStatus?.hasTemplateGates) {
          setCloseGateStatus(null);
        }
        void refetchSsotStatus();
      } else {
        const errorMessage = await getResponseErrorMessage(response, 'Failed to update status');
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Error updating status:', error);
      throw error; // Re-throw so StatusTransition can handle it
    } finally {
      onStatusTransitionEnd?.();
      setSaving(null);
    }
  }

  handleStatusChangeRef.current = handleStatusChange;
  fetchCloseGateStatusRef.current = fetchCloseGateStatus;

  const handleBeforeAppointmentStatusChange = useCallback(async (targetStatus: string) => {
    if (targetStatus === 'admin') {
      setShowAppointmentAdminWorkflowOverride(true);
      return false;
    }

    if (!shouldUseAdminAppointmentStatusOverride) {
      return true;
    }

    pendingAppointmentOverrideRequestRef.current = null;

    const normalizedTargetStatus = normalizeWorkflowStatusKey(targetStatus);
    const usesWorkflowOverride = [
      'scheduled',
      'confirmed',
      'on_route',
      'en_route',
      'in_progress',
      'checked_in',
      'working',
      'paused',
      'on_hold',
      'resolved',
      'partial',
      'unresolved',
      'rescheduled',
      'cancelled',
    ].includes(normalizedTargetStatus);

    if (!usesWorkflowOverride) {
      return true;
    }

    if (appointmentWorkflowOverrideTargets.length === 0) {
      notify.warning(
        loadingAssignedTeamMembers
          ? 'Still loading the assigned field workers for this appointment. Please try again in a moment.'
          : 'No assigned field workers could be resolved for this appointment. Add or sync the assignee first, then try again.',
      );
      return false;
    }

    const runningTimerImpact = normalizedTargetStatus === 'resolved'
      ? await buildCloseImpactSummary()
      : null;
    const extraMessageLines: string[] = [];

    if (runningTimerImpact?.runningTimers.length) {
      const timerLines = runningTimerImpact.runningTimers.slice(0, 8).map((timer) => `• ${formatCloseImpactTimerLine(timer)}`);
      extraMessageLines.push(
        `Resolving this appointment will stop the following running timer${runningTimerImpact.runningTimers.length === 1 ? '' : 's'}:`,
        ...timerLines,
      );

      if (runningTimerImpact.runningTimers.length > timerLines.length) {
        extraMessageLines.push(`• …and ${runningTimerImpact.runningTimers.length - timerLines.length} more running timer${runningTimerImpact.runningTimers.length - timerLines.length === 1 ? '' : 's'}`);
      }
    }

    try {
      const audienceSelection = await resolveAppointmentAdminOverrideAudience({
        requestedStatus: targetStatus,
        candidates: appointmentWorkflowOverrideTargets.map((target) => ({
          userId: target.user_id,
          displayName: target.display_name,
        })),
      });

      if (!audienceSelection || audienceSelection.targetUserIds.length === 0) {
        return false;
      }

      const preparedOverride = await prepareAdminAppointmentOverride({
        authToken: getAuthToken() || '',
        workItemId: workItem.id,
        requestedStatus: targetStatus,
        currentStatus: transitionBaseStatus,
        subjectLabel: audienceSelection.subjectLabel,
        audienceSummaryLabel: audienceSelection.audienceSummaryLabel ?? undefined,
        targetUserId: audienceSelection.targetUserId ?? undefined,
        targetUserIds: audienceSelection.targetUserIds,
        driverUserId: audienceSelection.driverUserId ?? undefined,
        driverLabel: audienceSelection.driverLabel ?? undefined,
        extraMessageLines: audienceSelection.driverLabel
          ? [...extraMessageLines, `Vehicle mileage will follow ${audienceSelection.driverLabel}.`]
          : extraMessageLines,
      });

      console.log('[DetailsSidebar] Before status change - prepareAdminAppointmentOverride result:', {
        hasPreparedOverride: preparedOverride !== null,
        requestedStatus: targetStatus,
        willCache: preparedOverride !== null,
      });

      // Only cache the request if it was successfully prepared (validation passed)
      if (preparedOverride !== null) {
        pendingAppointmentOverrideRequestRef.current = preparedOverride;
        console.log('[DetailsSidebar] Request cached successfully');
      } else {
        console.log('[DetailsSidebar] Request not cached (validation failed or user cancelled)');
      }
      return preparedOverride !== null;
    } catch (error) {
      console.error('[DetailsSidebar] Failed to prepare appointment override:', error);
      notify.error(error instanceof Error ? error.message : 'Failed to prepare appointment override');
      return false;
    }
  }, [appointmentWorkflowOverrideTargets, buildCloseImpactSummary, transitionBaseStatus, loadingAssignedTeamMembers, shouldUseAdminAppointmentStatusOverride, workItem.id]);

  // Keep ref in sync so the processGateCleared event handler always calls the latest version.
  handleBeforeStatusChangeRef.current = handleBeforeAppointmentStatusChange;

  // Format date for display
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format date for input
  const formatDateForInput = (dateString: string | null | undefined) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  const stakeholderProfileOpener = usesStakeholderAccountManager ? undefined : openTeamMember;

  // ============================================================================
  // HELPER: Item type icon + color for the hierarchy tree
  // ============================================================================
  const getItemTypeStyle = (itemType: string): { emoji: string; bg: string; text: string } => {
    const styles: Record<string, { emoji: string; bg: string; text: string }> = {
      project: { emoji: '📁', bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-300' },
      job: { emoji: '🔧', bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-300' },
      appointment: { emoji: '📅', bg: 'bg-teal-100 dark:bg-teal-900/30', text: 'text-teal-700 dark:text-teal-300' },
      task: { emoji: '✅', bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-300' },
      ticket: { emoji: '🎫', bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-700 dark:text-orange-300' },
      invoice: { emoji: '💰', bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-300' },
      quote: { emoji: '📄', bg: 'bg-indigo-100 dark:bg-indigo-900/30', text: 'text-indigo-700 dark:text-indigo-300' },
      expense: { emoji: '🧾', bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-300' },
      purchase_order: { emoji: '📦', bg: 'bg-violet-100 dark:bg-violet-900/30', text: 'text-violet-700 dark:text-violet-300' },
      sales_order: { emoji: '🛒', bg: 'bg-cyan-100 dark:bg-cyan-900/30', text: 'text-cyan-700 dark:text-cyan-300' },
      credit_note: { emoji: '↩️', bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-300' },
      delivery_note: { emoji: '🚚', bg: 'bg-sky-100 dark:bg-sky-900/30', text: 'text-sky-700 dark:text-sky-300' },
      proforma: { emoji: '📝', bg: 'bg-slate-100 dark:bg-slate-900/30', text: 'text-slate-700 dark:text-slate-300' },
      grn: { emoji: '📥', bg: 'bg-lime-100 dark:bg-lime-900/30', text: 'text-lime-700 dark:text-lime-300' },
    };
    return styles[itemType] || { emoji: '📋', bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-700 dark:text-gray-300' };
  };

  const openWorkItem = (id: string) => {
    if (typeof window !== 'undefined') {
      // Dispatch event to open work item in modal
      emit('openWorkItem', { id });
    }
  };

  const renderReadOnlyAppointmentAssigneeList = (assignees: WorkItemAssignee[]) => {
    if (assignees.length === 0) {
      return <span className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Unassigned</span>;
    }

    return (
      <div className="space-y-2">
        {assignees.map((assignee, index) => (
          <div
            key={`${assignee.user_id}-${assignee.email || index}`}
            className={`rounded-lg p-2 transition-colors ${isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-white hover:bg-gray-50'}`}
          >
            <UserIdentityLink
              user={resolveTeamMemberIdentity({
                userId: assignee.user_id,
                first_name: assignee.first_name || null,
                last_name: assignee.last_name || null,
                email: assignee.email || null,
              })}
              isDark={isDark}
              size="sm"
              fullWidth
              onOpenProfile={openTeamMember}
            />
          </div>
        ))}
      </div>
    );
  };

  const renderAppointmentAssigneeContent = () => {
    if (appointmentClassification === 'team') {
      if (loadingAssignedTeamMembers) {
        return (
          <div className={`flex items-center gap-2 p-2 rounded-lg ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-white text-gray-600'}`}>
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            <span className="text-sm">Loading team members…</span>
          </div>
        );
      }

      if (assignedTeamMembers.length > 0) {
        return (
          <div className="space-y-2">
            {[...assignedTeamMembers].sort((a, b) => (b.is_leader ? 1 : 0) - (a.is_leader ? 1 : 0)).map((member) => (
              <div
                key={member.user_id}
                className={`w-full rounded-lg p-2 transition-colors ${isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-white hover:bg-gray-50'}`}
              >
                <UserIdentityLink
                  user={resolveTeamMemberIdentity({
                    userId: member.user_id,
                    first_name: member.first_name || null,
                    last_name: member.last_name || null,
                    email: member.email || null,
                  })}
                  isDark={isDark}
                  size="sm"
                  fullWidth
                  onOpenProfile={openTeamMember}
                />
                {member.is_leader && (
                  <div className="mt-1 pl-8">
                    <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                      Team Leader
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        );
      }

      return renderReadOnlyAppointmentAssigneeList(localAssignees);
    }

    return renderReadOnlyAppointmentAssigneeList(localAssignees);
  };

  return (
    <div className={`w-full max-w-full h-full flex-shrink-0 overflow-y-auto rounded-2xl border lg:w-[380px] lg:rounded-none lg:border-y-0 lg:border-r-0 lg:border-l ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'
      } ${className}`} data-tour-id="work-item-details-sidebar">
      {/* ================================================================
          PANEL TOGGLE HEADER
          ================================================================ */}
      <div className={`sticky top-0 z-10 ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
        <div className="flex items-center justify-between px-4 pt-2.5 pb-1">
          <h3 className={`text-xs font-semibold ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
            {showContextPanel && effectivePanel === 'context' ? 'Context' : 'Details'} <UserDocsLink docsPath="/total-operations/workflows/service-operations/job-details#the-details-sidebar" label="User guide: Job details" className="ml-1 align-middle" />
          </h3>
          {canOpenItemTypeConfiguration ? (
            <button
              type="button"
              onClick={() => setShowItemTypeConfigModal(true)}
              className={`inline-flex items-center justify-center rounded-lg border p-2 shadow-sm transition-colors ${isDark
                ? 'border-gray-600 bg-gray-700/80 text-gray-300 hover:bg-gray-600 hover:text-white'
                : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }`}
              title={configureItemTypeActionTitle}
              aria-label={configureItemTypeActionTitle}
            >
              <Settings className="w-4 h-4" />
            </button>
          ) : null}
        </div>
        {/* Toggle tabs — hidden in context-only mode (finance documents) */}
        {!contextOnlyPanel && (
        <div className="px-4 pb-1.5">
          <div data-tour-id="details-sidebar-panel-toggle" className={`flex rounded-lg p-0.5 ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
            <button
              onClick={() => setActivePanel('details')}
              className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-medium transition-all ${activePanel === 'details'
                ? isDark ? 'bg-gray-600 text-white shadow-sm' : 'bg-white text-gray-900 shadow-sm'
                : isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              <Activity className="w-3.5 h-3.5" />
              Details
            </button>
            {showContextPanel && (
              <button
                onClick={() => setActivePanel('context')}
                className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-medium transition-all ${activePanel === 'context'
                  ? isDark ? 'bg-gray-600 text-white shadow-sm' : 'bg-white text-gray-900 shadow-sm'
                  : isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
                  }`}
              >
                <Network className="w-3.5 h-3.5" />
                Context
              </button>
            )}
          </div>
        </div>
        )}
      </div>

      {/* ================================================================
          PANEL: DETAILS (Status, Priority, Assignees, Labels, etc.)
          Uses ToolsEquipmentView-style section cards
          ================================================================ */}
      {effectivePanel === 'details' && (
        <div className="p-3 space-y-4">
          {/* ── STATUS ── */}
          <div data-tour-id="details-sidebar-status">
            <h3 className={`text-xs font-semibold uppercase tracking-wider mb-1.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              Status
              {isAppointment && (
                <span className="ml-1 normal-case tracking-normal text-gray-400" title="Dynamic status info is computed from schedule">
                  (auto)
                </span>
              )}
              {saving === 'status' && <span className="ml-2 normal-case tracking-normal text-blue-500">Saving…</span>}
            </h3>
            <div className={`rounded-lg p-2.5 space-y-2 ${isDark ? 'bg-gray-900/50' : 'bg-gray-100/80'}`}>
              {isAppointment ? (
                <>
                  {/* Computed timing badge */}
                  {(() => {
                    const now = new Date();
                    const status = effectiveWorkItemStatus || 'scheduled';
                    const reviewStatuses = ['ai_review', 'ai_on_hold'];
                    const completionResolution = typeof workItem.custom_fields?.resolution === 'string'
                      ? workItem.custom_fields.resolution.toLowerCase()
                      : null;
                    const hasCheckedOut = Boolean(workItem.custom_fields?.check_out_timestamp);
                    const isOperationallyComplete = hasCheckedOut || isTerminalWorkItemStatus(completionResolution, workItem.item_type);
                    const isWorking = isActivelyWorkedStatus(status);
                    const isAtSite = isOnSiteWorkItemStatus(status);
                    const isActive = isWorking || isAtSite;
                    const isOverSchedule = isActive && workItem.scheduled_end && new Date(workItem.scheduled_end) < now;
                    const isOnRoute = isOnRouteWorkItemStatus(status);
                    const isInReviewWorkflow = reviewStatuses.includes(status);
                    const isAdminReopened = normalizeWorkflowStatusKey(status) === 'admin_reopened';
                    const isLate = !isActive
                      && !isOnRoute
                      && !isInReviewWorkflow
                      && !isAdminReopened
                      && !isOperationallyComplete
                      && workItem.scheduled_start
                      && new Date(workItem.scheduled_start) < now
                      && !isTerminalWorkItemStatus(status, workItem.item_type);
                    const hasDynamicInfo = isOverSchedule || isOnRoute || isAtSite || isWorking || isLate;
                    if (!hasDynamicInfo) return null;

                    let displayLabel = '';
                    let colorClasses = isDark ? 'bg-gray-700 border-gray-600 text-gray-300' : 'bg-gray-200 border-gray-300 text-gray-600';
                    // Only show the timing badge for warning/alert conditions that add context
                    // beyond what the StatusTransition dropdown already shows
                    if (isOverSchedule) { displayLabel = '⏰ Over Schedule'; colorClasses = isDark ? 'bg-red-900/40 border-red-700 text-red-300' : 'bg-red-100 border-red-400 text-red-700'; }
                    else if (isOnRoute && isLate) { displayLabel = '🚗⚠️ On Route · Late'; colorClasses = isDark ? 'bg-orange-900/40 border-orange-700 text-orange-300' : 'bg-orange-100 border-orange-400 text-orange-700'; }
                    else if (isOnRoute) { return null; /* Skip — StatusTransition dropdown already shows On Route */ }
                    else if (isAtSite) { displayLabel = '📍 At Site — Not Checked In'; colorClasses = isDark ? 'bg-teal-900/40 border-teal-700 text-teal-300' : 'bg-teal-100 border-teal-400 text-teal-700'; }
                    else if (isWorking) { return null; /* Skip — StatusTransition dropdown already shows the working status */ }
                    else if (isLate) {
                      return null;
                    }
                    return (
                      <div className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-lg border mb-1 ${colorClasses}`}>
                        <span className="text-xs font-medium">{displayLabel}</span>
                      </div>
                    );
                  })()}
                  <StatusTransition
                    workItemId={workItem.id}
                    currentStatus={transitionBaseStatus || 'scheduled'}
                    itemType={workItem.item_type || 'appointment'}
                    itemSubtype={workItem.item_subtype || null}
                    workflowContext={workItem.module_context}
                    onStatusChange={handleStatusChange}
                    onBeforeStatusChange={handleBeforeAppointmentStatusChange}
                    isDark={isDark}
                    disabled={statusTransitionDisabled}
                    customOptions={statusTransitionCustomOptions}
                    workflowStatuses={workflowStatuses}
                    hasConfiguredTransitionRules={hasTransitionRules}
                    useAppointmentNextStepFlow
                    currentStatusDisplayOverride={appointmentStatusDropdownOverride}
                    dropdownLabel={usingAdminAppointmentStatusOverride && showAppointmentAdminWorkflowOverride
                      ? 'Admin workflow override'
                      : usingAdminAppointmentStatusOverride
                        ? 'Available statuses'
                        : usingAdminRecloseStatusOverride
                          ? 'Close again'
                          : usingAdminReopenStatusOverride
                            ? 'Admin Re-open'
                            : undefined}
                    emptyStateTitle={usingAdminAppointmentStatusOverride && showAppointmentAdminWorkflowOverride
                      ? 'No admin overrides available'
                      : usingAdminAppointmentStatusOverride
                        ? 'No statuses available'
                        : usingAdminRecloseStatusOverride
                          ? 'No close status available'
                          : usingAdminReopenStatusOverride
                            ? 'No admin reopen status available'
                            : undefined}
                    emptyStateDescription={usingAdminAppointmentStatusOverride && showAppointmentAdminWorkflowOverride
                      ? 'This appointment workflow does not expose any overrideable states right now.'
                      : usingAdminAppointmentStatusOverride
                        ? 'This appointment has no admin-controlled next steps available right now.'
                        : usingAdminRecloseStatusOverride
                          ? 'This workflow does not currently expose the Closed status.'
                          : usingAdminReopenStatusOverride
                            ? 'This workflow does not currently expose the Admin Re-opened status.'
                            : usingStandardStatusOptions
                              ? 'This workflow does not currently expose any alternate manual statuses.'
                              : undefined}
                  />
                </>
              ) : suppressesFinancePaymentStatusDropdown(workItem.item_type || '') ? (
                <div className="space-y-2">
                  <div
                    className={`rounded-md border px-3 py-2 text-xs ${isDark ? 'border-slate-700 bg-slate-900/60 text-slate-200' : 'border-gray-200 bg-white text-gray-800'}`}
                  >
                    <span className="font-semibold">Status</span>{' '}
                    {formatWorkflowStatusLabel(effectiveWorkItemStatus || workItem.status || 'open')}
                    {typeof workItem.payment_status === 'string' && workItem.payment_status.trim() ? (
                      <span className={isDark ? ' text-slate-400' : ' text-gray-500'}>
                        {' '}· Payment {formatWorkflowStatusLabel(workItem.payment_status)}
                      </span>
                    ) : null}
                  </div>
                  <p className={`text-[11px] leading-snug ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                    Paid balances and overdue states follow payments and journal postings. Use Payment Management or journal flows
                    in Total Finance rather than manual workflow transitions here.
                  </p>
                </div>
              ) : isVotingWorkItem ? (
                <div className="space-y-2">
                  <div
                    className={`rounded-md border px-3 py-2 text-xs ${isDark ? 'border-slate-700 bg-slate-900/60 text-slate-200' : 'border-gray-200 bg-white text-gray-800'}`}
                  >
                    <span className="font-semibold">Status</span>{' '}
                    {formatWorkflowStatusLabel(effectiveWorkItemStatus || workItem.status || 'draft')}
                  </div>
                  <p className={`text-[11px] leading-snug ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                    Session status is lifecycle-managed — use Launch, Open Voting or Close in the
                    session overview rather than a manual transition here.
                  </p>
                </div>
              ) : (
                <StatusTransition
                  workItemId={workItem.id}
                  currentStatus={transitionBaseStatus || 'open'}
                  itemType={workItem.item_type || 'job'}
                  itemSubtype={workItem.item_subtype || null}
                  workflowContext={workItem.module_context}
                  onStatusChange={handleStatusChange}
                  isDark={isDark}
                  customOptions={usingAdminRecloseStatusOverride ? adminRecloseStatusOptions : usingAdminReopenStatusOverride ? adminReopenStatusOptions : usingStandardStatusOptions ? standardStatusOptions : undefined}
                  workflowStatuses={workflowStatuses}
                  hasConfiguredTransitionRules={hasTransitionRules}
                  disabled={statusTransitionDisabled}
                  dropdownLabel={usingAdminRecloseStatusOverride ? 'Close again' : usingAdminReopenStatusOverride ? 'Admin Re-open' : usingStandardStatusOptions ? 'Available statuses' : undefined}
                  emptyStateTitle={usingAdminRecloseStatusOverride ? 'No close status available' : usingAdminReopenStatusOverride ? 'No admin reopen status available' : usingStandardStatusOptions ? 'No alternate statuses available' : undefined}
                  emptyStateDescription={usingAdminRecloseStatusOverride ? 'This workflow does not currently expose the Closed status.' : usingAdminReopenStatusOverride ? 'This workflow does not currently expose the Admin Re-opened status.' : usingStandardStatusOptions ? 'This workflow does not currently expose any alternate manual statuses.' : undefined}
                />
              )}
              {/* Finance document workflow info */}
              {isFinanceDocument(workItem.item_type || '') && (
                <div className={`flex items-start gap-2 pt-2 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                  <Info className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${isDark ? 'text-blue-400' : 'text-blue-500'}`} />
                  <span className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    {getDocumentTypeInfo(workItem.item_type || '').workflow}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* ── FOLLOW-UPS ── */}
          {workItem.id && workItem.item_type !== 'follow_up' && !isNonWorkItemEntity && !isVotingWorkItem && (
            <FollowUpSection
              workItemId={workItem.id}
              workItemTitle={workItem.title}
              isDark={isDark}
            />
          )}

          {/* ── PARENT ITEM (follow-ups: read-only parent status + link) ── */}
          {workItem.item_type === 'follow_up' && workItem.parent_id && (
            <ParentItemSection
              workItem={workItem}
              isDark={isDark}
            />
          )}

          {/* ── PROCESS CHECKLIST ── */}
          {closeGateStatus?.hasAnyGate && openProcessChecklist && (() => {
            const gateBlocked = closeGateStatus?.isBlocked ?? false;
            const gatePassed = closeGateStatus ? (closeGateStatus.allComplete || closeGateStatus.gateReleased) : false;
            const totalChecklistItems = closeGateStatus?.total ?? 0;
            const completedChecklistItems = closeGateStatus?.completed ?? 0;
            const overallPercentage = totalChecklistItems > 0
              ? Math.round((completedChecklistItems / totalChecklistItems) * 100)
              : 0;
            const containerClass = isDark
              ? 'border-gray-700 bg-gray-800/50 hover:bg-gray-800/70'
              : 'border-gray-200 bg-gray-50 hover:bg-gray-100/80';
            const titleClass = gatePassed
              ? 'text-green-500'
              : isDark
                ? 'text-blue-400'
                : 'text-blue-600';
            const statusAccentClass = gatePassed
              ? 'text-green-500'
              : gateBlocked
                ? 'text-amber-500'
                : 'text-blue-500';
            const progressBarClass = gatePassed ? 'bg-green-500' : 'bg-blue-600';

            return (
              <div>
                <button
                  onClick={() => openProcessChecklist()}
                  disabled={loadingCloseGate}
                  className={`w-full rounded-lg border p-4 text-left transition-colors ${containerClass}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <ClipboardCheck className={`h-5 w-5 ${titleClass}`} />
                      <h3 className={`text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Process Checklist
                      </h3>
                    </div>
                    {loadingCloseGate ? (
                      <Loader2 className="h-4 w-4 animate-spin opacity-60" />
                    ) : (
                      <span className={`text-sm font-bold ${titleClass}`}>
                        {closeGateStatus?.gateReleased ? 'Released' : `${overallPercentage}%`}
                      </span>
                    )}
                  </div>
                  <div className={`mt-2 w-full rounded-full h-2 ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
                    <div
                      className={`h-2 rounded-full transition-all duration-300 ${progressBarClass}`}
                      style={{ width: `${closeGateStatus?.gateReleased ? 100 : overallPercentage}%` }}
                    />
                  </div>
                  {!loadingCloseGate && (
                    <p className={`mt-1.5 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      {completedChecklistItems} of {totalChecklistItems} item{totalChecklistItems !== 1 ? 's' : ''} complete
                      {gateBlocked && (
                        <span className={`ml-2 font-medium ${statusAccentClass}`}>· Required for a status transition</span>
                      )}
                      {gatePassed && !closeGateStatus?.gateReleased && totalChecklistItems > 0 && (
                        <span className="ml-2 text-green-500 font-medium">· All requirements met</span>
                      )}
                      {closeGateStatus?.gateReleased && (
                        <span className="ml-2 text-green-500 font-medium">· Checklist released</span>
                      )}
                      {!gateBlocked && !gatePassed && totalChecklistItems > 0 && (
                        <span className="ml-2 text-blue-500 font-medium">· Advisory only</span>
                      )}
                    </p>
                  )}
                </button>
              </div>
            );
          })()}

          {!isStakeholderWorkItem && !isNonWorkItemEntity && !isVehicle && !isVotingWorkItem && (
            <div data-tour-id="details-sidebar-priority" className={readOnly ? 'pointer-events-none select-none opacity-70' : ''}>
              <h3 className={`text-xs font-semibold uppercase tracking-wider mb-1.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                Priority
                {saving === 'priority' && <span className="ml-2 normal-case tracking-normal text-blue-500">Saving…</span>}
              </h3>
              <div className={`rounded-lg p-2.5 ${isDark ? 'bg-gray-900/50' : 'bg-gray-100/80'}`}>
                <div className="relative">
                  <select
                    value={workItem.priority || 'medium'}
                    onChange={(e) => handlePriorityChange(e.target.value)}
                    className={`w-full px-3 py-2 text-sm rounded-lg border appearance-none cursor-pointer ${isDark ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-700'
                      }`}
                  >
                    {configPriorities.map(opt => (
                      <option key={opt.key} value={opt.key}>{opt.label}</option>
                    ))}
                  </select>
                  <div className="absolute right-8 top-1/2 -translate-y-1/2 pointer-events-none">
                    <div className={`w-2.5 h-2.5 rounded-full ${getPriorityDot(configPriorities, workItem.priority || 'medium')}`} />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── PEOPLE ── */}
          <div data-tour-id="details-sidebar-people">
            <h3 className={`text-xs font-semibold uppercase tracking-wider mb-1.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              {isTeamMember ? 'People & Vehicle' : 'People'}
            </h3>
            <div className={`rounded-lg p-2.5 space-y-3 ${isDark ? 'bg-gray-900/50' : 'bg-gray-100/80'}`}>
              {/* Assignee(s) */}
              {isTeamMember ? (
                <div>
                  <div className="flex items-center gap-2 mb-1.5">
                    <Truck className="w-3.5 h-3.5 text-gray-400" />
                    <span className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{assigneeLabel}</span>
                  </div>
                  {teamMemberIsNonDriver ? (
                    <span className={`inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-white text-gray-500'}`}>
                      <UserCheck className="w-3.5 h-3.5" />
                      Non Driver
                    </span>
                  ) : teamMemberVehicleLink.vehicleLabel ? (
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => teamMemberVehicleLink.vehicleId && openWorkItem(teamMemberVehicleLink.vehicleId)}
                        className={`flex flex-1 min-w-0 items-center justify-between gap-2 rounded-lg p-2 text-left transition-colors ${isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-white hover:bg-gray-200'}`}
                      >
                        <span className={`text-sm font-medium truncate ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                          {teamMemberVehicleLink.vehicleLabel}
                        </span>
                        <ExternalLink className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                      </button>
                      {!readOnly && teamMemberVehicleLink.vehicleId && (
                        <button
                          type="button"
                          onClick={() => void handleRemoveTeamMemberVehicle()}
                          disabled={saving === 'vehicle'}
                          title="Remove vehicle"
                          className={`flex-shrink-0 rounded-lg p-2 transition-colors disabled:opacity-50 ${isDark ? 'text-gray-400 hover:bg-gray-700 hover:text-rose-300' : 'text-gray-400 hover:bg-rose-50 hover:text-rose-600'}`}
                        >
                          {saving === 'vehicle'
                            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            : <X className="w-3.5 h-3.5" />}
                        </button>
                      )}
                    </div>
                  ) : readOnly ? (
                    <span className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No vehicle currently linked</span>
                  ) : (
                    <select
                      value=""
                      disabled={saving === 'vehicle' || vehicleOptionsLoading}
                      onChange={(event) => {
                        const nextVehicleId = event.target.value;
                        if (nextVehicleId) void handleAssignTeamMemberVehicle(nextVehicleId);
                      }}
                      className={`w-full rounded-lg border px-3 py-2 text-sm disabled:opacity-50 ${isDark ? 'border-gray-600 bg-gray-700 text-gray-200' : 'border-gray-300 bg-white text-gray-700'}`}
                    >
                      <option value="">
                        {vehicleOptionsLoading ? 'Loading vehicles…' : 'Assign a vehicle…'}
                      </option>
                      {vehicleOptions.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.label}{option.driverName ? ` · ${option.driverName}` : ''}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              ) : isAppointment && appointmentClassification === 'individual' ? (
                <div className={`${pendingAssignedStatus ? 'ring-2 ring-amber-500 rounded-lg p-1 -m-1' : ''}`}>
                  <div className="flex items-center gap-2 mb-1.5">
                    <Users className="w-3.5 h-3.5 text-gray-400" />
                    <span className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{assigneeLabel}</span>
                    {saving === 'assignees' && <span className="text-xs text-blue-500">Saving…</span>}
                    {pendingAssignedStatus && (
                      <span className="text-xs text-amber-500 font-medium animate-pulse">← Select assignee</span>
                    )}
                  </div>
                  <UserMultiSelect
                    selectedUsers={localAssignees}
                    availableUsers={filteredTeamMembers}
                    onAdd={handleAddAssignee}
                    onRemove={handleRemoveAssignee}
                    onOpenProfile={stakeholderProfileOpener}
                    loading={loadingMembers}
                    isDark={isDark}
                    readOnly={readOnly}
                    forceOpen={forceAssigneeDropdownOpen}
                    onForceOpenHandled={() => { setForceAssigneeDropdownOpen(false); setPendingAssignedStatus(false); }}
                    assigneeRoleOptions={assigneeRoleOptions}
                    onAssigneeRoleChange={handleAssigneeRoleChange}
                  />
                </div>
              ) : isAppointment ? (
                <div>
                  <div className="flex items-center gap-2 mb-1.5">
                    {appointmentClassification === 'team' ? <Users className="w-3.5 h-3.5 text-gray-400" /> : <User className="w-3.5 h-3.5 text-gray-400" />}
                    <span className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      {appointmentClassification === 'team'
                        ? 'Participants'
                        : localAssignees.length > 1
                          ? 'Participants'
                          : 'Assigned'}
                    </span>
                    <Lock className="w-3 h-3 text-gray-400" />
                  </div>
                  {renderAppointmentAssigneeContent()}
                </div>
              ) : (
                <div className={`${pendingAssignedStatus ? 'ring-2 ring-amber-500 rounded-lg p-1 -m-1' : ''}`}>
                  <div className="flex items-center gap-2 mb-1.5">
                    <Users className="w-3.5 h-3.5 text-gray-400" />
                    <span className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{assigneeLabel}</span>
                    {saving === 'assignees' && <span className="text-xs text-blue-500">Saving…</span>}
                    {pendingAssignedStatus && (
                      <span className="text-xs text-amber-500 font-medium animate-pulse">← Select assignee</span>
                    )}
                  </div>
                  <UserMultiSelect
                    selectedUsers={localAssignees}
                    availableUsers={filteredTeamMembers}
                    onAdd={handleAddAssignee}
                    onRemove={handleRemoveAssignee}
                    onOpenProfile={stakeholderProfileOpener}
                    loading={loadingMembers}
                    isDark={isDark}
                    readOnly={readOnly}
                    forceOpen={forceAssigneeDropdownOpen}
                    onForceOpenHandled={() => { setForceAssigneeDropdownOpen(false); setPendingAssignedStatus(false); }}
                    assigneeRoleOptions={assigneeRoleOptions}
                    onAssigneeRoleChange={handleAssigneeRoleChange}
                  />
                </div>
              )}

              {showAgentSection && (
                <div>
                  <div className="flex items-center gap-2 mb-1.5">
                    <Bot className="w-3.5 h-3.5 text-gray-400" />
                    <span className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Agent</span>
                  </div>
                  {localAssignedPersona ? (
                    <div className="flex items-center gap-1.5">
                      <div
                        className={`flex flex-1 min-w-0 items-center gap-2 rounded-lg p-2 ${isDark ? 'bg-gray-700' : 'bg-white border border-gray-200'}`}
                      >
                        <span
                          className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 text-white"
                          style={{ backgroundColor: assignedAgent?.color || '#64748b' }}
                        >
                          <Bot className="w-3 h-3" />
                        </span>
                        <span className={`text-sm font-medium truncate ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                          {assignedAgent?.name || localAssignedPersona}
                        </span>
                        <span className={`ml-auto text-[10px] px-1.5 py-0.5 rounded-full shrink-0 ${isDark ? 'bg-cyan-900/50 text-cyan-300' : 'bg-cyan-50 text-cyan-600'}`}>
                          agent
                        </span>
                      </div>
                      {!readOnly && (
                        <button
                          type="button"
                          onClick={() => void handleClearAgent()}
                          disabled={saving === 'assignees'}
                          title="Unassign agent"
                          className={`flex-shrink-0 rounded-lg p-2 transition-colors disabled:opacity-50 ${isDark ? 'text-gray-400 hover:bg-gray-700 hover:text-rose-300' : 'text-gray-400 hover:bg-rose-50 hover:text-rose-600'}`}
                        >
                          {saving === 'assignees'
                            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            : <X className="w-3.5 h-3.5" />}
                        </button>
                      )}
                    </div>
                  ) : readOnly ? (
                    <span className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No agent assigned</span>
                  ) : (
                    assignableAgents[0] ? (
                      <button
                        type="button"
                        disabled={saving === 'assignees'}
                        onClick={() => void handleAssignAgent(assignableAgents[0].persona_id)}
                        className={`flex w-full items-center gap-2 rounded-lg border px-3 py-2 text-sm text-left transition-colors disabled:opacity-50 ${isDark ? 'border-gray-600 bg-gray-700 text-gray-200 hover:border-cyan-600 hover:bg-cyan-900/20' : 'border-gray-300 bg-white text-gray-700 hover:border-cyan-400 hover:bg-cyan-50'}`}
                      >
                        <span
                          className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 text-white"
                          style={{ backgroundColor: assignableAgents[0].color || '#64748b' }}
                        >
                          <Bot className="w-3 h-3" />
                        </span>
                        <span className="truncate font-medium">Hand off to {assignableAgents[0].name}</span>
                        {saving === 'assignees' && <Loader2 className="w-3.5 h-3.5 animate-spin ml-auto" />}
                      </button>
                    ) : (
                      <span className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No agent assigned</span>
                    )
                  )}
                </div>
              )}

              {showManagerSection && (
                <div>
                  <div className="flex items-center gap-2 mb-1.5">
                    <UserCheck className="w-3.5 h-3.5 text-gray-400" />
                    <span className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Manager{requireManager && <span className="text-red-500"> *</span>}</span>
                    {saving === 'manager' && <span className="text-xs text-blue-500">Saving…</span>}
                    {localManagerId != null && !managerEditing && !readOnly && (
                      <button
                        type="button"
                        onClick={() => setManagerEditing(true)}
                        className="ml-auto text-xs text-blue-500 hover:text-blue-600"
                      >
                        Change
                      </button>
                    )}
                  </div>
                  {localManagerId != null && (
                    <div className={`rounded-lg p-2 mb-1.5 transition-colors ${isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-white hover:bg-gray-50'}`}>
                      <UserIdentityLink
                        user={resolveTeamMemberIdentity((() => {
                          const member = filteredTeamMembers.find((m) => m.id === localManagerId);
                          const nameParts = (localManagerName || '').trim().split(/\s+/);
                          return {
                            userId: localManagerId,
                            first_name: member?.first_name ?? nameParts[0] ?? null,
                            last_name: member?.last_name ?? (nameParts.length > 1 ? nameParts.slice(1).join(' ') : null),
                            email: member?.email ?? null,
                          };
                        })())}
                        isDark={isDark}
                        size="sm"
                        fullWidth
                        onOpenProfile={openTeamMember}
                      />
                    </div>
                  )}
                  {(localManagerId == null || managerEditing) && (
                  <select
                    value={localManagerId != null ? String(localManagerId) : ''}
                    onChange={(e) => { setManagerEditing(false); handleManagerChange(e.target.value ? Number(e.target.value) : null); }}
                    disabled={readOnly || loadingMembers}
                    title={readOnly ? 'Manager editing is locked in read-only mode' : 'Responsible manager — inherited by jobs and appointments created under this item'}
                    className={`w-full text-sm rounded-lg border px-2 py-1.5 ${isDark
                      ? 'bg-gray-800 border-gray-600 text-gray-200'
                      : 'bg-white border-gray-300 text-gray-700'
                      } ${readOnly || loadingMembers ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <option value="" disabled={requireManager}>{loadingMembers ? 'Loading…' : requireManager ? 'Select a manager…' : 'No manager'}</option>
                    {filteredTeamMembers
                      .filter((member) => member.id > 0 && (eligibleManagerIds === null || eligibleManagerIds.has(member.id)))
                      .map((member) => (
                        <option key={member.id} value={member.id}>
                          {(`${member.first_name || ''} ${member.last_name || ''}`.trim() || member.email || `User #${member.id}`)}
                        </option>
                      ))}
                    {localManagerId != null
                      && !filteredTeamMembers.some((member) => member.id === localManagerId)
                      && (
                        <option value={localManagerId}>
                          {localManagerName || `User #${localManagerId}`}
                        </option>
                      )}
                  </select>
                  )}
                </div>
              )}

              {!isStakeholderWorkItem && !isVehicle && (
                <>
                  <hr className={isDark ? 'border-gray-700' : 'border-gray-200'} />

                  <div data-tour-id="details-sidebar-reporter">
                    <div className="flex items-center gap-2 mb-1.5">
                      <User className="w-3.5 h-3.5 text-gray-400" />
                      <span className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Reporter</span>
                    </div>
                    {reporterIdentity ? (
                      <div className={`p-2 rounded-lg ${reporterUserId ? `transition-colors ${isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-white hover:bg-gray-50'}` : isDark ? 'bg-gray-700' : 'bg-white'}`}>
                        <UserIdentityLink
                          user={reporterIdentity}
                          isDark={isDark}
                          size="sm"
                          fullWidth
                          onOpenProfile={openTeamMember}
                          showExternalIcon={Boolean(reporterUserId)}
                        />
                      </div>
                    ) : (
                      <span className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Not specified</span>
                    )}
                  </div>

                  {platformFeedbackClientName && (
                    <div>
                      <div className="flex items-center gap-2 mb-1.5">
                        <Users className="w-3.5 h-3.5 text-gray-400" />
                        <span className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Submitted from</span>
                      </div>
                      <div className={`p-2 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-white'}`}>
                        <span className={`block truncate text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{platformFeedbackClientName}</span>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <div className={readOnly ? 'pointer-events-none select-none opacity-70' : ''}>

            {/* ── SCHEDULE (appointments only) ── */}
            {isAppointment && (workItem.scheduled_start || workItem.scheduled_end) && (
              <div>
                <h3 className={`text-xs font-semibold uppercase tracking-wider mb-1.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  Schedule
                </h3>
                <div className={`rounded-lg p-3 space-y-2 text-sm ${isDark ? 'bg-gray-900/50' : 'bg-gray-100/80'}`}>
                  {workItem.scheduled_start && (
                    <div className="flex justify-between">
                      <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Start</span>
                      <span className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        {new Date(workItem.scheduled_start).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}{' '}
                        {new Date(workItem.scheduled_start).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  )}
                  {workItem.scheduled_end && (
                    <div className="flex justify-between">
                      <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>End</span>
                      <span className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        {new Date(workItem.scheduled_end).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}{' '}
                        {new Date(workItem.scheduled_end).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  )}
                  {workItem.estimated_hours && (
                    <div className="flex justify-between pt-1 border-t border-gray-200 dark:border-gray-700">
                      <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Duration</span>
                      <span className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>{Number(workItem.estimated_hours)}h</span>
                    </div>
                  )}
                  {appointmentScheduleComparison && (
                    <>
                      <div className="flex justify-between pt-1 border-t border-gray-200 dark:border-gray-700">
                        <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Arrival vs Start</span>
                        <span className={`font-medium ${appointmentScheduleComparison.arrivalDeltaMinutes != null && appointmentScheduleComparison.arrivalDeltaMinutes > 0
                          ? (isDark ? 'text-amber-300' : 'text-amber-700')
                          : isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                          {formatDeltaLabel(appointmentScheduleComparison.arrivalDeltaMinutes)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Travel</span>
                        <span className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                          {formatMinutesCompact(appointmentScheduleComparison.travelDurationMinutes)}
                          {appointmentScheduleComparison.travelDistanceKm != null ? ` • ${appointmentScheduleComparison.travelDistanceKm} km` : ''}
                        </span>
                      </div>
                      {appointmentScheduleComparison.operatedOutsideSchedule && (
                        <div className="pt-1 border-t border-amber-200 dark:border-amber-800">
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${isDark ? 'bg-amber-900/50 text-amber-300' : 'bg-amber-100 text-amber-700'}`}>
                            ⚠ Operating outside schedule
                          </span>
                        </div>
                      )}
                    </>
                  )}
                  {workItem.scheduled_end
                    && (isActivelyWorkedStatus(effectiveWorkItemStatus) || isOnSiteWorkItemStatus(effectiveWorkItemStatus))
                    && new Date(workItem.scheduled_end) < new Date()
                    && (
                      <div className="pt-1 border-t border-red-200 dark:border-red-800">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${isDark ? 'bg-red-900/50 text-red-300' : 'bg-red-100 text-red-700'}`}>
                          ⏰ Over Schedule
                        </span>
                      </div>
                    )}
                </div>
              </div>
            )}

            {/* ── LABELS ── */}
            <div>
              <h3 className={`text-xs font-semibold uppercase tracking-wider mb-1.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                Labels
                {saving === 'labels' && <span className="ml-2 normal-case tracking-normal text-blue-500">Saving…</span>}
              </h3>
              <LabelsEditor labels={localLabels} onChange={handleLabelsChange} isDark={isDark} />
            </div>

            {!isStakeholderWorkItem && (
              <div>
                <h3 className={`text-xs font-semibold uppercase tracking-wider mb-1.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  Dates
                  {!isVehicle && saving === 'due_date' && <span className="ml-2 normal-case tracking-normal text-blue-500">Saving…</span>}
                </h3>
                <div className={`rounded-lg p-3 space-y-2 text-sm ${isDark ? 'bg-gray-900/50' : 'bg-gray-100/80'}`}>
                  {!isVehicle && (
                    <div className="flex justify-between items-center">
                      <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Due Date</span>
                      <input
                        type="date"
                        value={formatDateForInput(workItem.due_date)}
                        onChange={(e) => handleDueDateChange(e.target.value)}
                        className={`px-2 py-1 text-xs rounded border ${isDark ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-700'
                          }`}
                      />
                    </div>
                  )}
                  {workItem.start_date && (
                    <div className="flex justify-between">
                      <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Start Date</span>
                      <span className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>{formatDate(workItem.start_date)}</span>
                    </div>
                  )}
                  {(!isVehicle || Boolean(workItem.start_date)) && <hr className={isDark ? 'border-gray-700' : 'border-gray-200'} />}
                  <div className="flex justify-between">
                    <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Created</span>
                    <span className={isDark ? 'text-gray-300' : 'text-gray-600'}>{formatDate(workItem.created_at)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Updated</span>
                    <span className={isDark ? 'text-gray-300' : 'text-gray-600'}>{formatDate(workItem.updated_at)}</span>
                  </div>
                </div>
              </div>
            )}

            {/* ── FINANCE ── */}
            {isFinanceDocument(workItem.item_type || '') && (workItem.grand_total || workItem.balance_due !== undefined) && (
              <div>
                <h3 className={`text-xs font-semibold uppercase tracking-wider mb-1.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  {getDocumentTypeInfo(workItem.item_type || '').label} Details
                </h3>
                <div className={`rounded-lg p-3 space-y-2 text-sm ${isDark ? 'bg-gray-900/50' : 'bg-gray-100/80'}`}>
                  {workItem.grand_total !== undefined && workItem.grand_total !== null && (
                    <div className="flex justify-between">
                      <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Total</span>
                      <span className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        {workItem.currency || 'ZAR'} {Number(workItem.grand_total).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  )}
                  {workItem.balance_due !== undefined && workItem.balance_due !== null && (
                    <div className="flex justify-between">
                      <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Balance Due</span>
                      <span className={`font-semibold ${Number(workItem.balance_due) > 0 ? 'text-orange-500' : 'text-green-500'}`}>
                        {workItem.currency || 'ZAR'} {Number(workItem.balance_due).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  )}
                  {workItem.payment_status && (
                    <div className="flex justify-between items-center">
                      <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Payment</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${workItem.payment_status === 'paid' ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400'
                        : workItem.payment_status === 'partially_paid' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-400'
                          : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-400'
                        }`}>
                        {workItem.payment_status.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}
                      </span>
                    </div>
                  )}
                  {workItem.contact_name && (
                    <div className="flex justify-between pt-1 border-t border-gray-200 dark:border-gray-700">
                      <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>
                        {['expense', 'purchase_order', 'grn', 'supplier_credit'].includes(workItem.item_type || '') ? 'Supplier' : 'Customer'}
                      </span>
                      <span className={`font-medium truncate max-w-[150px] ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>{workItem.contact_name}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* ── LINKED APPOINTMENTS (for jobs) ── */}
          {workItem.item_type === 'job' && linkedAppointments.length > 0 && (
            <div data-tour-id="details-sidebar-linked-appointments">
              <h3 className={`text-xs font-semibold uppercase tracking-wider mb-1.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                Appointments
              </h3>
              <div className="space-y-2">
                {linkedAppointments.map(appt => (
                  <button
                    key={appt.id}
                    onClick={() => openWorkItem(appt.id)}
                    className={`w-full text-left p-2.5 rounded-lg transition-colors ${isDark ? 'bg-gray-900/50 hover:bg-gray-700' : 'bg-gray-100/80 hover:bg-gray-200'}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className={`text-xs font-mono ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{appt.item_number}</span>
                      <ExternalLink className="w-3 h-3 text-gray-400" />
                    </div>
                    {appt.title && <div className={`text-sm truncate mt-0.5 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>{appt.title}</div>}
                    {appt.scheduled_start && (
                      <div className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        {new Date(appt.scheduled_start).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ── BILLING SUBMISSION SOURCE CONTEXT ── */}
          {workItem.item_type === 'billing_submission' && (billingSourceJob || billingSourceAppointments.length > 0) && (
            <div>
              <h3 className={`text-xs font-semibold uppercase tracking-wider mb-1.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                Source Context
              </h3>
              <div className="space-y-2">
                {billingSourceJob && (
                  <button
                    onClick={() => openWorkItem(billingSourceJob.id)}
                    className={`w-full text-left p-2.5 rounded-lg transition-colors ${isDark ? 'bg-gray-900/50 hover:bg-gray-700' : 'bg-gray-100/80 hover:bg-gray-200'}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className={`text-xs font-mono ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{billingSourceJob.item_number}</span>
                      <ExternalLink className="w-3 h-3 text-gray-400" />
                    </div>
                    <div className={`text-sm truncate mt-0.5 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>{billingSourceJob.title}</div>
                    {billingSourceJob.status && (
                      <div className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        Source {billingSourceJob.status.replace(/_/g, ' ')}
                      </div>
                    )}
                  </button>
                )}

                {billingSourceAppointments.map((appt) => (
                  <button
                    key={appt.id}
                    onClick={() => openWorkItem(appt.id)}
                    className={`w-full text-left p-2.5 rounded-lg transition-colors ${isDark ? 'bg-gray-900/50 hover:bg-gray-700' : 'bg-gray-100/80 hover:bg-gray-200'}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className={`text-xs font-mono ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{appt.item_number}</span>
                      <ExternalLink className="w-3 h-3 text-gray-400" />
                    </div>
                    {appt.title && <div className={`text-sm truncate mt-0.5 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>{appt.title}</div>}
                    {appt.scheduled_start && (
                      <div className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        {new Date(appt.scheduled_start).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ── JOBS (for projects / appointments / child work) ── */}
          {((workItem.item_type === 'project' && childJobs.length > 0)
            || (workItem.item_type === 'appointment' && linkedJobs.length > 0)
            || ((workItem.item_type === 'task' || workItem.item_type === 'appointment') && parentJob)) && (
              <div>
                <h3 className={`text-xs font-semibold uppercase tracking-wider mb-1.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  {workItem.item_type === 'project' ? 'Jobs' : 'Job'}
                </h3>
                <div className="space-y-2">
                  {workItem.item_type === 'project' && childJobs.map((job) => (
                    <button
                      key={job.id}
                      onClick={() => openWorkItem(job.id)}
                      className={`w-full text-left p-2.5 rounded-lg transition-colors ${isDark ? 'bg-gray-900/50 hover:bg-gray-700' : 'bg-gray-100/80 hover:bg-gray-200'}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <div className={`text-xs font-mono ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{job.item_number}</div>
                          <div className={`text-sm truncate mt-0.5 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>{job.title}</div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full capitalize ${isSuccessfulCompletionStatus(job.status)
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400'
                            : isNegativeOutcomeStatus(job.status)
                              ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400'
                              : 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                            }`}>
                            {job.status.replace(/_/g, ' ')}
                          </span>
                          <ExternalLink className="w-3 h-3 text-gray-400" />
                        </div>
                      </div>
                    </button>
                  ))}

                  {workItem.item_type === 'appointment' && linkedJobs.map((job) => (
                    <button
                      key={job.id}
                      onClick={() => openWorkItem(job.id)}
                      className={`w-full text-left p-2.5 rounded-lg transition-colors ${isDark ? 'bg-gray-900/50 hover:bg-gray-700' : 'bg-gray-100/80 hover:bg-gray-200'}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className={`text-xs font-mono ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{job.item_number}</span>
                        <ExternalLink className="w-3 h-3 text-gray-400" />
                      </div>
                      <div className={`text-sm truncate mt-0.5 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>{job.title}</div>
                      {job.status && (
                        <div className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          {job.status.replace(/_/g, ' ')}
                        </div>
                      )}
                    </button>
                  ))}

                  {(workItem.item_type === 'task' || (workItem.item_type === 'appointment' && linkedJobs.length === 0)) && parentJob && (
                    <button
                      onClick={() => openWorkItem(parentJob.id)}
                      className={`w-full text-left p-2.5 rounded-lg transition-colors ${isDark ? 'bg-gray-900/50 hover:bg-gray-700' : 'bg-gray-100/80 hover:bg-gray-200'}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className={`text-xs font-mono ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{parentJob.item_number}</span>
                        <ExternalLink className="w-3 h-3 text-gray-400" />
                      </div>
                      <div className={`text-sm truncate mt-0.5 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>{parentJob.title}</div>
                      <div className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        Parent job
                      </div>
                    </button>
                  )}
                </div>
              </div>
            )}

          {/* ── JOB FINANCIAL SUMMARY (compact card for jobs) ── */}
          {workItem.item_type === 'job' && (
            <div>
              <h3 className={`text-xs font-semibold uppercase tracking-wider mb-1.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                Financials
              </h3>
              <div className={`rounded-lg p-3 space-y-2 text-sm ${isDark ? 'bg-gray-900/50' : 'bg-gray-100/80'}`}>
                {workItem.estimated_cost || workItem.actual_cost ? (
                  <>
                    <div className="flex justify-between">
                      <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Est. Cost</span>
                      <span className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        R {Number(workItem.estimated_cost || 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Actual Cost</span>
                      <span className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        R {Number(workItem.actual_cost || 0).toFixed(2)}
                      </span>
                    </div>
                    {(Number(workItem.actual_cost || 0) - Number(workItem.estimated_cost || 0)) !== 0 && (
                      <div className="flex justify-between">
                        <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Variance</span>
                        <span className={`font-medium ${(Number(workItem.actual_cost || 0) - Number(workItem.estimated_cost || 0)) > 0
                          ? 'text-red-500'
                          : 'text-green-500'
                          }`}>
                          {(Number(workItem.actual_cost || 0) - Number(workItem.estimated_cost || 0)) > 0 ? '+' : ''}
                          R {(Number(workItem.actual_cost || 0) - Number(workItem.estimated_cost || 0)).toFixed(2)}
                        </span>
                      </div>
                    )}
                  </>
                ) : (
                  <div className={`flex items-center gap-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    <DollarSign className="w-4 h-4" />
                    <span>No cost data yet</span>
                  </div>
                )}
                {(workItem.estimated_hours || workItem.actual_hours) && (
                  <>
                    <hr className={isDark ? 'border-gray-700' : 'border-gray-200'} />
                    <div className="flex justify-between">
                      <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Hours</span>
                      <span className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        {Number(workItem.actual_hours || 0).toFixed(1)} / {Number(workItem.estimated_hours || 0).toFixed(1)}h
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ================================================================
          PANEL: TIMING & CONTEXT (Actual Timing + Relationship Hierarchy)
          ================================================================ */}
      {showContextPanel && effectivePanel === 'context' && (
        <div className="p-3 space-y-4">
          {/* ── ACTUAL TIMING (Hidden for finance documents) ── */}
          {!hideOpsTiming && (
          <div data-tour-id="details-sidebar-timing">
            <h3 className={`text-xs font-semibold uppercase tracking-wider mb-1.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              Actual Timing
            </h3>
            {(workItem.custom_fields?.check_in_timestamp || workItem.custom_fields?.check_out_timestamp || workItem.custom_fields?.travel_started_at || workItem.custom_fields?.arrived_at) ? (
              <div className={`rounded-lg p-3 space-y-2 text-sm ${isDark ? 'bg-gray-900/50' : 'bg-gray-100/80'}`}>
                {workItem.custom_fields?.travel_started_at && (
                  <div className="flex justify-between items-center">
                    <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>🚗 Travel Started</span>
                    <span className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                      {new Date(workItem.custom_fields.travel_started_at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                )}
                {workItem.custom_fields?.arrived_at && (
                  <div className="flex justify-between items-center">
                    <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>📍 Arrived</span>
                    <span className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                      {new Date(workItem.custom_fields.arrived_at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                )}
                {workItem.custom_fields?.check_in_timestamp && (
                  <div className="flex justify-between items-center">
                    <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>✅ Check In</span>
                    <span className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                      {new Date(workItem.custom_fields.check_in_timestamp).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                )}
                {workItem.custom_fields?.check_out_timestamp && (
                  <div className="flex justify-between items-center">
                    <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>🏁 Check Out</span>
                    <span className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                      {new Date(workItem.custom_fields.check_out_timestamp).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                )}
                {/* Travel stats */}
                {(workItem.custom_fields?.completed_travel_entry?.duration_minutes || workItem.custom_fields?.completed_travel_entry?.distance_km) && (
                  <>
                    <hr className={isDark ? 'border-gray-700' : 'border-gray-200'} />
                    {workItem.custom_fields.completed_travel_entry.duration_minutes && (
                      <div className="flex justify-between">
                        <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Travel Duration</span>
                        <span className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>{workItem.custom_fields.completed_travel_entry.duration_minutes}m</span>
                      </div>
                    )}
                    {workItem.custom_fields.completed_travel_entry.distance_km && (
                      <div className="flex justify-between">
                        <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Travel Distance</span>
                        <span className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>{workItem.custom_fields.completed_travel_entry.distance_km} km</span>
                      </div>
                    )}
                  </>
                )}
                {/* Calculated work duration */}
                {workItem.custom_fields?.check_in_timestamp && workItem.custom_fields?.check_out_timestamp && (
                  <>
                    <hr className={isDark ? 'border-gray-700' : 'border-gray-200'} />
                    <div className="flex justify-between">
                      <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Work Duration</span>
                      <span className={`font-semibold ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                        {(() => {
                          const diff = new Date(workItem.custom_fields.check_out_timestamp).getTime() - new Date(workItem.custom_fields.check_in_timestamp).getTime();
                          const hrs = Math.floor(diff / 3600000);
                          const mins = Math.round((diff % 3600000) / 60000);
                          return hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
                        })()}
                      </span>
                    </div>
                  </>
                )}
                {appointmentScheduleComparison && (
                  <>
                    <hr className={isDark ? 'border-gray-700' : 'border-gray-200'} />
                    <div className="flex justify-between">
                      <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Arrival vs Schedule</span>
                      <span className={`font-medium ${appointmentScheduleComparison.arrivalDeltaMinutes != null && appointmentScheduleComparison.arrivalDeltaMinutes > 0
                        ? (isDark ? 'text-amber-300' : 'text-amber-700')
                        : isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        {formatDeltaLabel(appointmentScheduleComparison.arrivalDeltaMinutes)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Finish vs Schedule</span>
                      <span className={`font-medium ${appointmentScheduleComparison.completionDeltaMinutes != null && appointmentScheduleComparison.completionDeltaMinutes > 0
                        ? (isDark ? 'text-red-300' : 'text-red-700')
                        : isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        {formatDeltaLabel(appointmentScheduleComparison.completionDeltaMinutes, 'over', 'early')}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Travel Logged</span>
                      <span className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        {formatMinutesCompact(appointmentScheduleComparison.travelDurationMinutes)}
                        {appointmentScheduleComparison.travelDistanceKm != null ? ` • ${appointmentScheduleComparison.travelDistanceKm} km` : ''}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>On-site Logged</span>
                      <span className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        {formatMinutesCompact(appointmentScheduleComparison.workDurationMinutes)}
                        {appointmentScheduleComparison.scheduledDurationMinutes != null ? ` of ${formatMinutesCompact(appointmentScheduleComparison.scheduledDurationMinutes)} scheduled` : ''}
                      </span>
                    </div>
                    {appointmentScheduleComparison.operatedOutsideSchedule && (
                      <div className={`rounded-lg px-2.5 py-2 text-xs ${isDark ? 'bg-amber-900/30 text-amber-300' : 'bg-amber-50 text-amber-700'}`}>
                        <div className="font-semibold">Operating outside schedule</div>
                        <div className="mt-1">{appointmentScheduleComparison.outsideScheduleReasons.join(' • ')}</div>
                      </div>
                    )}
                  </>
                )}
              </div>
            ) : (
              <div className={`rounded-lg p-3 text-sm ${isDark ? 'bg-gray-900/50 text-gray-500' : 'bg-gray-100/80 text-gray-400'}`}>
                <div className="flex items-center gap-2">
                  <Timer className="w-4 h-4" />
                  <span>No timing data recorded yet</span>
                </div>
              </div>
            )}
          </div>
        )}

          {/* ── SCHEDULE SUMMARY (Hidden for finance documents) ── */}
          {!hideOpsTiming && (workItem.scheduled_start || workItem.scheduled_end) && (
            <div>
              <h3 className={`text-xs font-semibold uppercase tracking-wider mb-1.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                Schedule
              </h3>
              <div className={`rounded-lg p-3 space-y-2 text-sm ${isDark ? 'bg-gray-900/50' : 'bg-gray-100/80'}`}>
                {workItem.scheduled_start && (
                  <div className="flex justify-between">
                    <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Scheduled Start</span>
                    <span className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                      {new Date(workItem.scheduled_start).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                )}
                {workItem.scheduled_end && (
                  <div className="flex justify-between">
                    <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Scheduled End</span>
                    <span className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                      {new Date(workItem.scheduled_end).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                )}
                {workItem.estimated_hours && (
                  <div className="flex justify-between">
                    <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Estimated</span>
                    <span className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>{Number(workItem.estimated_hours)}h</span>
                  </div>
                )}
                {workItem.actual_hours && (
                  <div className="flex justify-between">
                    <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Actual</span>
                    <span className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>{Number(workItem.actual_hours)}h</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── PROJECT LINK (jobs / tasks / appointments) ── */}
          {isLinkableType && (
            <div>
              <h3 className={`text-xs font-semibold uppercase tracking-wider mb-1.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                Parent Project
              </h3>
              {linkedProject ? (
                <div className={`rounded-lg p-2.5 ${isDark ? 'bg-gray-900/50' : 'bg-gray-100/80'}`}>
                  <div className="flex items-center justify-between">
                    <button
                      onClick={() => openWorkItem(linkedProject.id)}
                      className={`flex items-center gap-2 text-left group ${isDark ? 'hover:text-blue-400' : 'hover:text-blue-600'}`}
                    >
                      <span className="text-base">📁</span>
                      <div className="min-w-0">
                        <div className={`text-xs font-mono ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{linkedProject.item_number}</div>
                        <div className={`text-sm truncate ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>{linkedProject.title}</div>
                      </div>
                      <ExternalLink className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100" />
                    </button>
                    <button
                      onClick={handleUnlinkProject}
                      disabled={linkingProject}
                      className={`p-1 rounded transition ${isDark ? 'hover:bg-gray-700 text-gray-500' : 'hover:bg-gray-200 text-gray-400'}`}
                      title="Unlink from project"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ) : (
                <div ref={projectPickerRef} className="relative">
                  {showProjectPicker ? (
                    <div className={`rounded-lg border overflow-hidden ${isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`}>
                      <div className="p-2">
                        <input
                          type="text"
                          value={projectSearchQuery}
                          onChange={e => setProjectSearchQuery(e.target.value)}
                          placeholder="Search projects…"
                          autoFocus
                          className={`w-full px-2.5 py-1.5 text-sm rounded border ${isDark ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'} focus:ring-1 focus:ring-blue-500 focus:border-blue-500`}
                        />
                      </div>
                      {projectSearchResults.length > 0 && (
                        <div className="max-h-48 overflow-y-auto">
                          {projectSearchResults.map(p => (
                            <button
                              key={p.id}
                              onClick={() => handleLinkToProject(p)}
                              disabled={linkingProject}
                              className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm transition ${isDark ? 'hover:bg-gray-800 text-gray-300' : 'hover:bg-gray-50 text-gray-700'}`}
                            >
                              <span className="text-base">📁</span>
                              <div className="min-w-0 flex-1">
                                <span className={`text-xs font-mono ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{p.item_number}</span>
                                <div className="truncate">{p.title}</div>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                      {projectSearchQuery.length >= 1 && projectSearchResults.length === 0 && (
                        <div className={`px-3 py-2 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No projects found</div>
                      )}
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowProjectPicker(true)}
                      className={`w-full flex items-center gap-2 p-2.5 rounded-lg border border-dashed text-sm transition ${isDark ? 'border-gray-600 text-gray-400 hover:border-gray-500 hover:text-gray-300' : 'border-gray-300 text-gray-500 hover:border-gray-400 hover:text-gray-600'}`}
                    >
                      <Plus className="w-4 h-4" />
                      <span>Link to Project</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── RELATIONSHIP HIERARCHY ── */}
          <div data-tour-id="details-sidebar-relationship-tree">
            <h3 className={`text-xs font-semibold uppercase tracking-wider mb-1.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              Relationship Tree
            </h3>
            {loadingHierarchy ? (
              <div className={`rounded-lg p-4 flex items-center justify-center ${isDark ? 'bg-gray-900/50' : 'bg-gray-100/80'}`}>
                <div className="animate-spin w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full" />
                <span className={`ml-2 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Loading hierarchy…</span>
              </div>
            ) : hierarchy ? (
              <div className={`rounded-lg overflow-hidden ${isDark ? 'bg-gray-900/50' : 'bg-gray-100/80'}`}>
                {/* Ancestors */}
                {hierarchy.ancestors.map((node, idx) => {
                  const style = getItemTypeStyle(node.item_type);
                  return (
                    <button
                      key={node.id}
                      onClick={() => openWorkItem(node.id)}
                      className={`w-full flex items-center gap-2 p-2.5 text-left transition-colors border-b ${isDark ? 'border-gray-700 hover:bg-gray-700' : 'border-gray-200 hover:bg-gray-200'
                        }`}
                      style={{ paddingLeft: `${(idx) * 12 + 12}px` }}
                    >
                      {/* Connector line */}
                      {idx > 0 && (
                        <div className={`w-3 border-l-2 border-b-2 h-3 rounded-bl -ml-1 mr-0.5 flex-shrink-0 ${isDark ? 'border-gray-600' : 'border-gray-300'}`} />
                      )}
                      <span className="text-base flex-shrink-0">{style.emoji}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className={`text-xs font-mono ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{node.item_number}</span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full capitalize ${style.bg} ${style.text}`}>{node.item_type.replace(/_/g, ' ')}</span>
                        </div>
                        <div className={`text-xs truncate mt-0.5 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{node.title}</div>
                      </div>
                      <ExternalLink className="w-3 h-3 text-gray-400 flex-shrink-0" />
                    </button>
                  );
                })}

                {/* Current Item (highlighted) */}
                {(() => {
                  const style = getItemTypeStyle(hierarchy.current.item_type);
                  const indent = hierarchy.ancestors.length;
                  return (
                    <div
                      className={`w-full flex items-center gap-2 p-2.5 border-b ${isDark ? 'bg-blue-900/20 border-blue-800' : 'bg-blue-50 border-blue-200'
                        }`}
                      style={{ paddingLeft: `${indent * 12 + 12}px` }}
                    >
                      {indent > 0 && (
                        <div className={`w-3 border-l-2 border-b-2 h-3 rounded-bl -ml-1 mr-0.5 flex-shrink-0 ${isDark ? 'border-blue-600' : 'border-blue-300'}`} />
                      )}
                      <span className="text-base flex-shrink-0">{style.emoji}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className={`text-xs font-mono font-bold ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>{hierarchy.current.item_number}</span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full capitalize font-semibold ${isDark ? 'bg-blue-800 text-blue-200' : 'bg-blue-200 text-blue-800'}`}>
                            {hierarchy.current.item_type.replace(/_/g, ' ')} (current)
                          </span>
                        </div>
                        <div className={`text-xs truncate mt-0.5 font-medium ${isDark ? 'text-blue-200' : 'text-blue-700'}`}>{hierarchy.current.title}</div>
                      </div>
                    </div>
                  );
                })()}

                {/* Children */}
                {hierarchy.children.length > 0 ? (
                  hierarchy.children.map((node) => {
                    const style = getItemTypeStyle(node.item_type);
                    const indent = hierarchy.ancestors.length + 1;
                    return (
                      <button
                        key={node.id}
                        onClick={() => openWorkItem(node.id)}
                        className={`w-full flex items-center gap-2 p-2.5 text-left transition-colors border-b last:border-b-0 ${isDark ? 'border-gray-700 hover:bg-gray-700' : 'border-gray-200 hover:bg-gray-200'
                          }`}
                        style={{ paddingLeft: `${indent * 12 + 12}px` }}
                      >
                        <div className={`w-3 border-l-2 border-b-2 h-3 rounded-bl -ml-1 mr-0.5 flex-shrink-0 ${isDark ? 'border-gray-600' : 'border-gray-300'}`} />
                        <span className="text-base flex-shrink-0">{style.emoji}</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className={`text-xs font-mono ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{node.item_number}</span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full capitalize ${style.bg} ${style.text}`}>{node.item_type.replace(/_/g, ' ')}</span>
                            {/* Status badge */}
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full capitalize ${isSuccessfulCompletionStatus(node.status)
                              ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400'
                              : isNegativeOutcomeStatus(node.status)
                                ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400'
                                : 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                              }`}>
                              {node.status.replace(/_/g, ' ')}
                            </span>
                          </div>
                          <div className={`text-xs truncate mt-0.5 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{node.title}</div>
                        </div>
                        <ExternalLink className="w-3 h-3 text-gray-400 flex-shrink-0" />
                      </button>
                    );
                  })
                ) : null}

                {/* No relationships message */}
                {hierarchy.ancestors.length === 0 && hierarchy.children.length === 0 && (
                  <div className={`p-3 text-center ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    <Network className="w-5 h-5 mx-auto mb-1 opacity-50" />
                    <span className="text-xs">This is a standalone {hierarchy.current.item_type.replace(/_/g, ' ')}</span>
                  </div>
                )}
              </div>
            ) : (
              <div className={`rounded-lg p-3 text-sm ${isDark ? 'bg-gray-900/50 text-gray-500' : 'bg-gray-100/80 text-gray-400'}`}>
                <div className="flex items-center gap-2">
                  <Network className="w-4 h-4" />
                  <span>Unable to load relationships</span>
                </div>
              </div>
            )}

            {/* NEW: Finance-specific source attachments */}
            {isFinanceMode && enhanceFinanceRelationships && hierarchy && (
              <FinanceRelationshipEnhancements
                workItem={workItem}
                isDark={isDark}
                onOpenAttachment={(attachmentId) => {
                  window.dispatchEvent(new CustomEvent('finance:view-attachment', {
                    detail: { attachmentId, workItemId: workItem.id }
                  }));
                }}
              />
            )}
          </div>

          {/* ── ACTIVITY LOG (Finance mode only) ── */}
          {isFinanceMode && (
            <div>
              <h3 className={`text-xs font-semibold uppercase tracking-wider mb-1.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                Audit Trail
              </h3>
              <button
                onClick={() => {
                  if (financeContext?.onOpenActivityLog) {
                    financeContext.onOpenActivityLog();
                  } else {
                    // Dispatch custom event for external modal handling
                    window.dispatchEvent(new CustomEvent('finance:open-activity-log', {
                      detail: { workItemId: workItem.id }
                    }));
                  }
                }}
                className={`w-full flex items-center gap-3 p-3 rounded-lg border transition ${isDark
                  ? 'border-gray-700 hover:bg-gray-900/50 text-gray-300 hover:text-blue-400'
                  : 'border-gray-300 hover:bg-gray-50 text-gray-700 hover:text-blue-600'
                  }`}
              >
                <Activity className="w-4 h-4 flex-shrink-0" />
                <div className="text-left flex-1">
                  <div className="text-sm font-medium">View Activity Log</div>
                  <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    Document changes, approvals, comments
                  </div>
                </div>
                <ExternalLink className="w-4 h-4 flex-shrink-0 opacity-50" />
              </button>
            </div>
          )}

          {/* ── SUPPORTING DOCUMENTS (Finance mode only) ── */}
          {isFinanceMode && (
            <div>
              <h3 className={`text-xs font-semibold uppercase tracking-wider mb-1.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                Supporting Documents
              </h3>
              <button
                onClick={() => {
                  window.dispatchEvent(new CustomEvent('finance:open-supporting-documents', {
                    detail: { workItemId: workItem.id }
                  }));
                }}
                className={`w-full flex items-center gap-3 p-3 rounded-lg border transition ${isDark
                  ? 'border-gray-700 hover:bg-gray-900/50 text-gray-300 hover:text-green-400'
                  : 'border-gray-300 hover:bg-gray-50 text-gray-700 hover:text-green-600'
                  }`}
              >
                <FileText className="w-4 h-4 flex-shrink-0" />
                <div className="text-left flex-1">
                  <div className="text-sm font-medium">Supporting Documents</div>
                  <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    Receipts, invoices, attachments
                  </div>
                </div>
                <ExternalLink className="w-4 h-4 flex-shrink-0 opacity-50" />
              </button>
            </div>
          )}

          {/* ── QUICK PARENT LINK (if parent_id exists) ── */}
          {/* Follow-ups render ParentItemSection above instead — it already
              shows the parent number link plus the parent's status. */}
          {workItem.parent_id && workItem.item_type !== 'follow_up' && (
            <div>
              <h3 className={`text-xs font-semibold uppercase tracking-wider mb-1.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                Quick Navigation
              </h3>
              <button
                onClick={() => openWorkItem(workItem.parent_id!)}
                className={`w-full flex items-center gap-2 p-2.5 rounded-lg text-left transition-colors ${isDark ? 'bg-gray-900/50 hover:bg-gray-700' : 'bg-gray-100/80 hover:bg-gray-200'
                  }`}
              >
                <ChevronRight className={`w-4 h-4 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                <div className="flex-1 min-w-0">
                  <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Parent {workItem.parent_job_type || 'Item'}</span>
                  <div className={`text-sm font-medium ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                    {workItem.parent_job_number || workItem.parent_title || 'View Parent'}
                  </div>
                </div>
                <ExternalLink className="w-3.5 h-3.5 text-gray-400" />
              </button>
            </div>
          )}
        </div>
      )}

      <WorkItemTypeConfigurationModal
        isOpen={showItemTypeConfigModal}
        itemType={showItemTypeConfigModal ? workItem.item_type || null : null}
        itemSubtype={showItemTypeConfigModal ? workItem.item_subtype || null : null}
        itemTypeLabel={isVehicle ? 'Vehicles' : itemTypeLabel}
        workflowContext={workItem.module_context}
        configurationVariant={isStakeholderWorkItem ? 'stakeholder' : isVehicle ? 'vehicle' : 'default'}
        onClose={() => setShowItemTypeConfigModal(false)}
        onSaved={async () => {
          // Refresh job config to pick up updated workflow status transitions
          await refreshJobConfig();
        }}
      />
    </div>
  );
}
